/*
 * Carbisdale - EditorView
 *
 * Verantwortlich für das Rendering des Editor-Inhalts.
 * Wandelt das Datenmodell in sichtbare Screen-Zeilen um.
 */

import { Style, STYLES, EDITOR, MINIMAP, REVIEW, PARAGRAPH_SPACING, LINE_HEIGHT_Q, headingHeightQ, headingSpacing, collapseSpacing, IMPLICIT_HEAD_ID } from '../config.js'
import { buildOutlineColumns } from './OutlineTree.js'
import {
    detectParagraphType, getHangingIndent,
    sliceParsedForLine, parsedToFormatRuns, parsedCharsToString, isWordChar,
    charDisplayWidth
} from '../TextProcessor.js'
import { computeStringLayout, computeAdvance } from '../GlyphMetrics.js'
import { LinePositionMap, buildLinePositionMap, enrichFormatRuns as _enrichFormatRuns } from '../LinePositionMap.js'
import { buildMonoShiftedPositions } from '../textGeometry.js'
import { buildStyledChars, buildRichTextMapping } from '../RichTextOps.js'
import { computeReviewDiff, buildCompositeFormatRuns, sliceFormatRunsForLine, injectBlockFloor } from './reviewDiff.js'
import { pickMarkerStyle, pickSeparatorMarkerStyle, MarkerPane } from './activeFieldHighlight.js'
import { enrichRunsWithSpellWarnings } from './spellWarningEnrich.js'
import { wordRangeAt } from '../spellcheck/Tokenizer.js'
import { isSpellCheckedField as _isSpellCheckedField } from '../spellcheck/fieldRouting.js'

// Re-export fuer bestehende Imports (z.B. Tests)
export { LinePositionMap }

// Kein-Op Restore-Callback fuer _applyReviewDim (vermeidet neuen Closure
// pro Zeile wenn kein Dim aktiv).
const noopRestore = () => {}

/**
 * Eine gerenderte Zeile auf dem Screen.
 */
export class ScreenLine {
    /**
     * @param {number} paragraphId - ID des zugehörigen Paragraphs
     * @param {number} startOffset - Start-Offset im Paragraph-Text
     * @param {number} endOffset - End-Offset im Paragraph-Text
     * @param {string} field - 'text' oder 'note'
     * @param {boolean} isPadding - true für Leerzeilen (Zeilen-Sync)
     */
    constructor(paragraphId, startOffset, endOffset, field = 'text', isPadding = false) {
        this.paragraphId = paragraphId
        this.startOffset = startOffset
        this.endOffset = endOffset
        this.field = field        // 'text' oder 'note'
        this.isPadding = isPadding // Leerzeile für Zeilen-Synchronisation
        this.isFirstLine = false  // Hat > Marker
        this.isLastLine = false   // Hat < Marker
        this.isSeparator = false  // Separator-Zeile (durchgehende Linie)
        this.isBackground = false // Zeile im Background-Bereich (nach Background-Separator)
        this.separatorNumber = '' // Hierarchische Nummer (z.B. "1.2.3.")
        this.separatorTitle = ''  // Separator-Titel (text-Feld)
        this.separatorDepth = 0   // Gliederungstiefe des Separators (0-6)
        this.skippedSpaces = 0    // Übersprungene Leerzeichen am Zeilenanfang (Word-Wrap)
        this.hangingIndent = 0    // Einrückung für Continuation-Zeilen (Listen) — dual: Characters (Mono) oder Pixel (Prop.)
        this.listPrefixLen = 0    // Zeichenanzahl des Listen-Prefix (Indent + Marker + Space) — nur erste Zeile
        this.rtPrefix = ''        // RT-Modus: visueller Prefix-String (z.B. '• ', '1. '), nicht im Text enthalten
        this.pixelWidth = 0       // Tatsächliche Zeilen-Breite in Pixel (0 = Monospace)
        this.spacingBefore = 0    // Abstand VOR dieser Zeile in Viertel-Zeilenhöhen (nur Proportional)
        this.heightQ = 4          // Zeilenhöhe in Quarterlines (4 = Basis, 6 = Normal-Prop, 7-10 = Headings)

        // Cache für geparste Markdown-Zeichen (invalidiert bei Textänderung)
        this._parsedCache = null  // { text: string, paraType: string, parsed: Array }
    }
}

/**
 * EditorView-Klasse
 * Verwaltet die visuelle Darstellung des Editors.
 */
export class EditorView {
    /**
     * @param {EditorModel} model - Das Datenmodell
     * @param {number} width - Verfügbare Breite in Zeichen
     * @param {number} height - Verfügbare Höhe in Zeilen
     */
    constructor(model, width, height) {
        this.model = model

        // Dimensionen (width - SCROLLBAR_WIDTH für Scrollbar-Spalte)
        this.width = Math.max(10, width - EDITOR.SCROLLBAR_WIDTH)
        this.height = Math.max(1, height)

        // Effektive Textbreite (ohne Marker-Spalten)
        // Format: ">Text hier<" = MARKER_WIDTH für ">" am Anfang, MARKER_WIDTH für "<" am Ende
        this.markerWidth = EDITOR.MARKER_WIDTH
        this.textWidth = this.width - this.markerWidth - EDITOR.MARKER_WIDTH

        // Cache für gerenderte Zeilen
        this.lines = []

        // Parallele Note-Zeilen (synchron mit lines[])
        this.noteLines = []

        // Parallele Info-Zeilen für Context-Pane (synchron mit lines[])
        this.infoLines = []

        // Outline-Spalten (synchron mit lines[], berechnet in _rebuildLines)
        this._outlineColumns = new Map()

        // Pane-Breiten (0 = Pane nicht sichtbar)
        this.mainPaneWidth = width
        this.outlinePaneWidth = 0
        this.contextPaneWidth = 0

        // O(1)-Lookup: paragraphId → Index der ersten Zeile dieses Paragraphs
        this._paragraphLineIndex = new Map()

        // Spellcheck-Repaint-Queue (V3.1, System-Review 2026-06-10):
        // Absaetze mit frischen Worker-Warnings, die im Viewport sichtbar
        // sind. Wird vom renderLoop ueber trySpellRepaint konsumiert;
        // Full-Render (render) leert sie, weil er Warnings ohnehin live malt.
        this._spellRepaintParas = new Set()

        // Viewport
        this.viewportStart = 0

        // Pending Cursor-Screen-Position (für Restore nach erstem Resize)
        this._pendingCursorScreenY = null

        // Dirty-Flag
        this.isDirty = true

        // Hover-State für das Lock-Control auf einer Separator-Zeile im Main-Pane
        this._hoveredMainSepLock = null   // paragraphId oder null

        // Scroll-Delta für Scroll-Shift-Optimierung
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
        // Sub-Pixel-Restbetrag: shiftPaneContent rundet auf ganze Pixel,
        // wir tragen den Bruchteil in den nächsten Frame, sonst akkumuliert
        // Drift zwischen Canvas-Bild und logischer Offset-Position → Wackeln.
        this._shiftResidual = 0

        // Letzter beim Vollrender gemalter aktiver Absatz — bei Wechsel ist
        // der Active-Row-Background ungueltig und Scroll-Shift wuerde den
        // alten Streifen mitziehen (Cursor-Wandern + Scroll, z.B. Pfeiltaste
        // halten). getScrollShiftInfo blockt den Shift dann. Initial mit
        // aktueller Cursor-Position seeden, damit der erste Scroll-Shift
        // (ohne vorherigen Render) nicht durch paragraphChanged blockiert wird.
        this._lastRenderedActiveParagraphId = this.model && this.model.cursor
            ? this.model.cursor.paragraphId
            : null

        // Callback fuer Such-Highlighting (wird von main.js gesetzt)
        // Signatur: (paragraphId, offset) => null | 'match' | 'current'
        this.searchHighlightFn = null

        // Selection-Cache (wird pro Render-Zyklus einmal berechnet)
        this._selectionCache = null

        // Per-Paragraph Markdown-Parse-Caches (invalidiert bei Textänderung)
        this._textParseCache = new Map()
        this._noteParseCache = new Map()
        this._infoParseCache = new Map()

        // Version-Counter: Stimmt mit model._dirtyVersion überein wenn Lines aktuell sind
        this._linesVersion = -1

        // Flag: updateParagraph hat Zeilenanzahl geändert → Full-Render nötig
        this._hadLineDelta = false

        // Spellcheck-Suppression: Snapshot der Cursor-Position direkt nach
        // der letzten Para-Mutation. Solange der Cursor exakt hier steht,
        // wird die Welle fuer das Wort unterdrueckt — sobald der User klickt
        // oder mit Pfeiltasten woanders hin geht, springt die Welle zurueck.
        // Wird von main.js im addParagraphDirtyListener gepflegt.
        this._lastEditedCursor = null

        // Renderer-Referenz für proportionale Font-Unterstützung (wird von außen gesetzt)
        this.renderer = null

        // SpellCheckManager — wird vom Bootstrap injiziert. Wenn null, kein
        // Wavy-Underline. Wenn gesetzt, wird beim Rendern eines Paragraphen
        // im Main-Pane (field === 'text') der FormatRun mit
        // enrichRunsWithSpellWarnings() angereichert.
        this.spellCheckManager = null

        // Delta-Review Hint (vom ReviewController gesetzt). null = kein Review aktiv.
        // Shape: { dimAllExcept: Set<paragraphId>, overlays: [...] }
        // Wenn gesetzt, werden Paragraphen, die NICHT in dimAllExcept stehen,
        // mit REVIEW.DIM_ALPHA gerendert — der Rest (Review-Paragraphen) bleibt
        // voll sichtbar fuer den User zum Pruefen.
        this._reviewHint = null

        // Quarter-Line Spacing (nur Proportional-Mode)
        this._lineQuarterY = []   // Kumulative Viertel-Y-Position jeder Main-Zeile
        // Pro-Pane Y-Grids: Note/Info stapeln umgebrochene Zeilen mit EIGENER
        // Zeichenhöhe (4QL), nicht mit der Main-Höhe (6/10QL). Absätze bleiben
        // oben synchron (gemeinsamer Slot-Top); das kürzere Pane bekommt unten
        // Teil-Padding. Siehe _buildSlotQuarterY().
        this._noteLineQuarterY = []
        this._infoLineQuarterY = []
        this._slotBottomQ = []    // Unterkante (Viertel) des Slots je Zeilen-Index (Pane-übergreifend)
        // Treibendes Pane je Zeilen-Index für die Outline-Connector-Geometrie:
        // 'text' (Main sichtbar / main-getriebener Slot) oder 'note' (Main hidden
        // bzw. lange Note + kurzer Main). Bestimmt, an wessen Zeilen-Mitte der
        // └-Eckpunkt sitzt und welches Grid die Tree-Vertikale überspannt.
        this._slotConnectorField = []
        this._totalQuarters = 0   // Gesamthöhe des Dokuments in Vierteln
        this._viewportPixelOffset = 0  // Pixel-Offset in die erste sichtbare Zeile (nur Proportional)

        // Monospace-Scroll-Akkumulator: Sub-Zeilen-Pixel-Input sammeln, Zeile auslösen
        // sobald voll. Im Monospace bleibt _viewportPixelOffset bei 0 — grid-based Optik,
        // genau wie vor Lesson 52. Proportional nutzt stattdessen _viewportPixelOffset.
        this._monoScrollAccumulator = 0

        // PositionMap-Cache für proportionale Zeichen-Positionen (lineIndex → LinePositionMap)
        this._textPositionMaps = new Map()
        this._notePositionMaps = new Map()
        this._infoPositionMaps = new Map()

        // Accessor für Separator-Input-State (wird von ProseEditor gesetzt)
        this._separatorInputAccessor = null

        // Pane-Filter-Modus: null | 'outline' | 'context' | 'both'
        // Aktiv wenn Main hidden — bestimmt welche Absätze gezeigt werden
        this._paneFilterMode = null

        // Initiales Rebuild
        this._rebuildLines()
    }

    /**
     * Setzt eine ausstehende Cursor-Screen-Position für nach dem ersten Resize.
     * @param {number} screenY - Gewünschte Zeile des Cursors im Viewport
     */
    setPendingCursorScreenY(screenY) {
        this._pendingCursorScreenY = screenY
    }

    /**
     * Setzt den Accessor für den Separator-Input-State.
     * @param {Function} fn - Gibt { cursorPos, scrollOffset, selectionStart, selectionEnd, text } zurück oder null
     */
    setSeparatorInputAccessor(fn) {
        this._separatorInputAccessor = fn
    }

    /**
     * Aktualisiert die Dimensionen.
     * Erhält die relative Cursor-Position im Viewport.
     * @param {number} width
     * @param {number} height
     */
    resize(width, height, noteWidth = 0, infoWidth = 0) {
        // Pending Cursor-Position hat höchste Priorität (für Restore nach Page-Load)
        const pendingScreenY = this._pendingCursorScreenY
        this._pendingCursorScreenY = null

        // Mittlere sichtbare Zeile merken (stabil bei allen Resize-Szenarien)
        let anchorParagraphId = null
        let anchorOffset = 0
        let anchorRelativeY = 0.5  // Default: Mitte

        if (pendingScreenY === null && this.height > 0 && this.lines.length > 0) {
            const midScreenY = Math.floor(this.height / 2)
            const midLineIndex = Math.min(
                this.viewportStart + midScreenY,
                this.lines.length - 1
            )
            if (midLineIndex >= 0) {
                const midLine = this.lines[midLineIndex]
                anchorParagraphId = midLine.paragraphId
                anchorOffset = midLine.startOffset
                // Exakte relative Position der Anker-Zeile im Viewport
                anchorRelativeY = (midLineIndex - this.viewportStart) / this.height
            }
        }

        // Alten Filter-Modus merken, um Uebergaenge zu erkennen (Filter-Aktivierung
        // beim Maximize von Outline/Context, Deaktivierung beim Demaximize).
        const oldFilterMode = this._paneFilterMode

        this.mainPaneWidth = width
        this.width = Math.max(10, width - EDITOR.SCROLLBAR_WIDTH)
        this.height = Math.max(1, height)
        this.textWidth = this.width - this.markerWidth - EDITOR.MARKER_WIDTH
        this.outlinePaneWidth = noteWidth
        this.contextPaneWidth = infoWidth
        this._rebuildLines()

        // Filter-Uebergang handhaben: Wenn der Filter gerade aktiviert wurde
        // (Main hidden), den aktuellen Anker speichern — er zeigt auf die echte
        // Cursor-Position vor dem Filter. Wird der Filter spaeter deaktiviert,
        // benutzen wir diesen gespeicherten Anker statt aus den gefilterten Lines
        // neu zu berechnen (die enthalten evtl. nur gefilterte Absaetze und wuerden
        // den Viewport an den Anfang des Dokuments werfen).
        const newFilterMode = this._paneFilterMode
        if (!oldFilterMode && newFilterMode && anchorParagraphId !== null) {
            this._preFilterAnchor = { anchorParagraphId, anchorOffset, anchorRelativeY }
        } else if (oldFilterMode && !newFilterMode && this._preFilterAnchor) {
            anchorParagraphId = this._preFilterAnchor.anchorParagraphId
            anchorOffset = this._preFilterAnchor.anchorOffset
            anchorRelativeY = this._preFilterAnchor.anchorRelativeY
            this._preFilterAnchor = null
        }

        // Viewport wiederherstellen
        if (pendingScreenY !== null && this.model.cursor) {
            // Pending Position anwenden (nach Page-Load) — feld-bewusst, damit
            // ein Note-/Info-Cursor (Outline/Context) den Screen-Row korrekt trifft.
            const lineIndex = this.getCursorLineIndex(
                this.model.cursor.paragraphId,
                this.model.cursor.offset,
                this.model.cursor.field || 'text'
            )
            if (lineIndex >= 0) {
                const newViewportStart = lineIndex - pendingScreenY
                this.scrollTo(newViewportStart)
            }
        } else if (anchorParagraphId !== null) {
            // Anker-Zeile an gleicher relativer Position halten
            const lineIndex = this.getLineIndexForPosition(anchorParagraphId, anchorOffset)
            if (lineIndex >= 0) {
                const targetScreenY = Math.round(anchorRelativeY * this.height)
                const newViewportStart = lineIndex - targetScreenY
                this.scrollTo(newViewportStart)
            }
        }

        // Nach komplettem Rebuild: Scroll-Delta zurücksetzen, damit Full-Render erzwungen wird
        // (scrollTo() in der Anchor-Restoration setzt _scrollDelta, was sonst den
        // Scroll-Shift-Pfad triggern würde — aber der Canvas-Inhalt ist nach Rebuild ungültig)
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
        this._shiftResidual = 0
        this._monoScrollAccumulator = 0
    }

    /**
     * Markiert die View als geändert.
     */
    markDirty() {
        this.isDirty = true
    }

    /**
     * Setzt das Dirty-Flag zurück.
     */
    markClean() {
        this.isDirty = false
        // Scroll-Tracking zurücksetzen — sonst akkumuliert _scrollPixelDelta
        // über Frames bei Render-Pfaden in denen view.render() nicht aufgerufen wird
        // (z.B. Full-Render mit Main hidden, oder Paragraph-Partial-Pfad).
        // Ohne Reset shifted der nächste Frame doppelt → sichtbares Wackeln.
        // _shiftResidual NICHT löschen: der Sub-Pixel-Rest ist absichtlich für das
        // nächste Scroll-Frame gedacht (siehe renderPartial, Zeile 3142). Sonst verliert
        // jeder Shift-Frame bis zu ±0.5 px — der Canvas driftet am logischen Viewport
        // vorbei, sichtbar als Hüpfer bei Zeilenübergängen (z.B. Separator-Durchgang).
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
    }

    // ===== Quarter-Line Spacing =====

    /**
     * Prüft ob Quarter-Line Spacing aktiv ist (nur Proportional-Mode).
     */
    _useQuarterSpacing() {
        return this.renderer && this.renderer.isProportional()
    }

    /**
     * Bestimmt den Filter-Modus wenn Main nicht sichtbar ist.
     * @returns {string|null} 'outline' | 'context' | 'both' | null (kein Filter)
     */
    _getPaneFilterMode() {
        if (this.mainPaneWidth > 0) return null
        const outline = this.outlinePaneWidth > 0
        const context = this.contextPaneWidth > 0
        if (outline && context) return 'both'
        if (outline) return 'outline'
        if (context) return 'context'
        return null
    }

    /**
     * Prüft ob ein Absatz das Filter-Kriterium erfüllt.
     * @param {object} para - Paragraph-Objekt
     * @param {string} filterMode - 'outline' | 'context' | 'both'
     * @returns {boolean}
     */
    _paraPassesFilter(para, filterMode) {
        if (filterMode === 'outline') return para.isOutlineItem()
        if (filterMode === 'context') return para.info.length > 0
        return para.isOutlineItem() || para.info.length > 0  // 'both'
    }

    /**
     * Prueft ob lineIndex im feld-spezifischen Zeilen-Array ein gueltiger
     * Cursor-Stopp fuer Pfeil-Navigation ist.
     *
     * Regeln:
     *   1. Separator-Label-Zeile → gueltig (Cursor wechselt ins text-Feld,
     *      Separator-Input wird aktiviert)
     *   2. Content-Zeile (isPadding=false) → gueltig
     *   3. Padding-Zeile → nur wenn Nicht-Separator-Absatz, Feld leer,
     *      und erste Zeile des Absatzes. Deckt provisorisch-aufgenommene
     *      Absaetze im maximierten Context/Outline ab — der Cursor landet
     *      zwischen den >< Markern.
     *
     * Background-Separator-Padding (isPadding=true, paragraphId=BG-Separator)
     * faellt durch Regel 3 raus (para.type === 'separator').
     *
     * @param {number} lineIndex
     * @param {'text'|'note'|'info'} field
     * @returns {boolean}
     */
    _isArrowStopInField(lineIndex, field) {
        const lines = field === 'note' ? this.noteLines
            : field === 'info' ? this.infoLines : this.lines
        if (lineIndex < 0 || lineIndex >= lines.length) return false
        const line = lines[lineIndex]
        if (line.isSeparator) return true
        if (!line.isPadding) return true
        const para = this.model.getParagraph(line.paragraphId)
        if (!para || para.type === 'separator') return false
        const fieldText = field === 'note' ? (para.note || '')
            : field === 'info' ? (para.info || '') : (para.text || '')
        if (fieldText.length !== 0) return false
        return this._paragraphLineIndex.get(line.paragraphId) === lineIndex
    }

    /**
     * Findet den im aktuellen View sichtbaren Nachbar-Absatz ueber Pane-Filter
     * und Collapse hinweg.
     *
     * Ein Absatz gilt als sichtbar wenn er in _paragraphLineIndex eingetragen
     * ist (also im rebuildLines-Durchlauf aufgenommen wurde).
     *
     * @param {number} paragraphId - Aktueller Absatz (Startpunkt)
     * @param {number} direction - -1 (vorher) oder +1 (nachher)
     * @param {object} [options]
     * @param {boolean} [options.skipSeparators=false] - Separatoren ueberspringen
     * @param {boolean} [options.skipPassive=false] - Normal-Paragraphen
     *   (Nicht-Outline-Items) ueberspringen. Fuer Note-Feld-Nav: provisional
     *   aufgenommene Normal-Paras bleiben zwar sichtbar, sind aber fuer
     *   Pfeil-Nav nicht ansteuerbar — analog zur Passive-Skip-Logik in up/down.
     * @returns {object|null} Paragraph-Objekt oder null
     */
    findVisibleNeighbor(paragraphId, direction, { skipSeparators = false, skipPassive = false } = {}) {
        const idx = this.model.getParagraphIndex(paragraphId)
        if (idx < 0) return null
        for (let i = idx + direction; i >= 0 && i < this.model.paragraphs.length; i += direction) {
            const p = this.model.paragraphs[i]
            if (skipSeparators && p.type === 'separator') continue
            if (skipPassive && p.type !== 'separator' && !p.isOutlineItem()) continue
            if (this._paragraphLineIndex.has(p.id)) return p
        }
        return null
    }

    /**
     * Pixel-Y-Position einer Zeile relativ zum Viewport-Start.
     * Berücksichtigt Spacing und Pixel-Offset.
     * @param {number} lineIndex - Zeilen-Index im lines-Array
     * @returns {number} Pixel-Position (kann negativ sein bei teilweise geclippter erster Zeile)
     */
    /**
     * Liefert das Y-Grid eines Panes ('note'/'info'/'text').
     */
    _paneQuarterY(field) {
        return field === 'note' ? this._noteLineQuarterY
            : field === 'info' ? this._infoLineQuarterY
            : this._lineQuarterY
    }

    _lineScreenPixelY(lineIndex, field = 'text') {
        if (!this._useQuarterSpacing() || this._lineQuarterY.length === 0) {
            // Monospace: grid-based, kein Sub-Pixel-Offset
            return (lineIndex - this.viewportStart) * this.renderer.charHeight
        }
        const quarterH = this.renderer.charHeight / 4
        // Viewport-Referenz IMMER Main — ein gemeinsamer Scroll für alle Panes.
        const vpQ = this._lineQuarterY[this.viewportStart] || 0
        const lineQ = this._paneQuarterY(field)[lineIndex] || 0
        return (lineQ - vpQ) * quarterH - this._viewportPixelOffset
    }

    /**
     * Findet den Zeilen-Index an einer Screen-Y-Position (Pixel).
     * Für Mouse-Hit-Testing im Proportional-Mode.
     * @param {number} screenPixelY - Pixel-Position relativ zum Viewport-Top
     * @returns {number} Zeilen-Index
     */
    _lineAtScreenPixelY(screenPixelY, field = 'text') {
        if (!this._useQuarterSpacing() || this._lineQuarterY.length === 0) {
            // Monospace: grid-based, kein Sub-Pixel-Offset
            return this.viewportStart + Math.floor(screenPixelY / this.renderer.charHeight)
        }
        const quarterH = this.renderer.charHeight / 4
        // Viewport-Referenz IMMER Main (gemeinsamer Scroll), Suche im Pane-Grid.
        const vpQ = this._lineQuarterY[this.viewportStart] || 0
        const arr = this._paneQuarterY(field)
        const targetQ = vpQ + (screenPixelY + this._viewportPixelOffset) / quarterH

        // Binärsuche: finde Zeile deren Viertel-Position <= targetQ
        // (Pane-Grids sind non-decreasing: Content steigt, Padding springt auf Slot-Unterkante)
        let lo = 0, hi = arr.length - 1
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            if ((arr[mid] || 0) <= targetQ) lo = mid
            else hi = mid - 1
        }
        return lo
    }

    /**
     * Erste sichtbare Zeile eines Panes (Index), an der dessen Render-Loop starten
     * muss. Da Note/Info ihr eigenes Y-Grid haben, kann die oben am Viewport-Rand
     * sichtbare Note/Info-Zeile (oder deren Connector-Zelle) einen NIEDRIGEREN Index
     * als der Main-`viewportStart` haben — etwa eine kurze Note in einem main-hohen
     * Slot, deren Gap-Absorb-Zelle bis in den Viewport reicht. Loop ab `viewportStart`
     * würde sie überspringen → Zeile/Connector verschwindet an der oberen Kante.
     * `min` stellt sicher, dass wir nie SPÄTER als bisher starten (Lines oberhalb
     * werden ohnehin per `pxY + cellH <= 0` übersprungen).
     * @param {'note'|'info'|'text'} field
     * @returns {number}
     */
    _firstVisibleLine(field) {
        if (!this._useQuarterSpacing() || !this.renderer) return this.viewportStart
        return Math.max(0, Math.min(this.viewportStart, this._lineAtScreenPixelY(0, field)))
    }

    /**
     * Berechnet spacingBefore für alle Zeilen und baut _lineQuarterY auf.
     * Wird am Ende von _rebuildLines() und nach updateParagraph() aufgerufen.
     */
    _computeSpacing() {
        const n = this.lines.length
        this._lineQuarterY = new Array(n)
        this._noteLineQuarterY = new Array(n)
        this._infoLineQuarterY = new Array(n)
        this._slotBottomQ = new Array(n)
        this._slotConnectorField = new Array(n)
        if (n === 0) {
            this._totalQuarters = 0
            return
        }

        if (!this._useQuarterSpacing()) {
            // Monospace: kein Spacing, alle drei Grids identisch (i * 4)
            for (let i = 0; i < n; i++) {
                this.lines[i].spacingBefore = 0
                this._lineQuarterY[i] = i * 4
                this._noteLineQuarterY[i] = i * 4
                this._infoLineQuarterY[i] = i * 4
                this._slotBottomQ[i] = (i + 1) * 4
                this._slotConnectorField[i] = 'text'
            }
            this._totalQuarters = n * 4
            return
        }

        // Pane-Filter aktiv (Main hidden, Outline/Context maximiert):
        // Slot-Höhe folgt dem Sekundärtext (SECONDARY 4QL) — KEIN künstlicher Rest
        // aus einer größeren Main-Höhe, der bei einzeiligen Notes wie ein Abstand
        // wirkte, bei umgebrochenen aber wegfiel. Der Abstand kommt jetzt explizit
        // aus spacingBefore und sitzt auf der ersten Zeile jedes Slots (wrap-
        // unabhängig): 3QL vor einem Separator, 2QL vor einer Note
        // (Separator→Note, Note→Note). Erste Zeile im Dokument ohne Leading-Spacing.
        if (this._paneFilterMode) {
            let prevMainPid = null
            let prevNotePid = null
            for (let i = 0; i < this.lines.length; i++) {
                const line = this.lines[i]
                const noteLine = i < this.noteLines.length ? this.noteLines[i] : null
                const infoLine = i < this.infoLines.length ? this.infoLines[i] : null
                const notePid = noteLine ? noteLine.paragraphId : line.paragraphId
                // Slot-Wechsel = neuer Absatz/Knoten (Paar (mainPid, notePid) ändert sich)
                const isNewSlot = line.paragraphId !== prevMainPid || notePid !== prevNotePid
                // Separator über den Para-Typ erkennen (deckt auch die Leading-Padding-
                // Zeilen des Background-Separators ab, die selbst keine isSeparator sind).
                const para = this.model.getParagraph(line.paragraphId)
                const isSep = !!(para && para.type === 'separator')
                const sb = (isNewSlot && i > 0) ? (isSep ? 3 : 2) : 0
                line.spacingBefore = sb
                line.heightQ = isSep ? LINE_HEIGHT_Q.SEPARATOR : LINE_HEIGHT_Q.SECONDARY
                // Note/Info-Zeilen synchron (Inhalt kleiner: SECONDARY/4QL + System-Font)
                if (noteLine) {
                    noteLine.heightQ = LINE_HEIGHT_Q.SECONDARY
                    noteLine.spacingBefore = sb
                }
                if (infoLine) {
                    infoLine.heightQ = LINE_HEIGHT_Q.SECONDARY
                    infoLine.spacingBefore = sb
                }
                prevMainPid = line.paragraphId
                prevNotePid = notePid
            }
            this._buildSlotQuarterY()
            return
        }

        // Proportional: Margin-Collapsing — max(prev.after, curr.before)
        let prevParagraphId = null
        let prevSpacing = null  // Spacing-Definition des vorherigen Absatzes

        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i]

            if (i === 0) {
                // Erste Zeile im Dokument
                line.spacingBefore = 0
            } else if (line.paragraphId === prevParagraphId) {
                // Continuation-Zeile (Word-Wrap)
                line.spacingBefore = 0
            } else {
                // Neuer Absatz: Margin-Collapsing berechnen
                const currSpacing = this._getParaSpacing(line)
                line.spacingBefore = collapseSpacing(prevSpacing, currSpacing)
            }

            // Absatzwechsel: Spacing für nächsten Vergleich merken
            if (line.paragraphId !== prevParagraphId) {
                prevSpacing = this._getParaSpacing(line)
                prevParagraphId = line.paragraphId
            }
        }

        // heightQ pro Zeile bestimmen (nur Proportional)
        // Auch noteLines/infoLines spiegeln (zeilen-synchron, gleicher Index)
        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (line.isSeparator) {
                line.heightQ = LINE_HEIGHT_Q.SEPARATOR
            } else {
                let para = this.model.getParagraph(line.paragraphId)
                // Virtuelle Insert-Lines stehen nicht im Modell (Fake-IDs) —
                // ohne Pseudo-Para faellt heightQ auf NORMAL und Heading-
                // Inserts werden in der Vorschau nicht groesser dargestellt.
                if (!para && line._reviewInsertOverlay) {
                    para = this._pseudoParaFromInsertOverlay(line._reviewInsertOverlay)
                }
                // _getParaHeightQ wendet bereits _effectiveParaForRender an,
                // sodass Composite-Lines bei Heading-Wechsel im Review die
                // richtige Hoehe je activeView bekommen.
                line.heightQ = para ? this._getParaHeightQ(para) : LINE_HEIGHT_Q.NORMAL
            }
            // Note/Info-Zeilen: SECONDARY (4QL + System-Font, wie Separator-Labels).
            // Sekundärtext ist kleiner als der Haupttext — die vertikale Ausdehnung
            // der Zeile kommt aus _lineQuarterY (an die Main-Zeile gekoppelt), nicht
            // aus heightQ der Note/Info; heightQ steuert nur Fontgröße + Font-Wahl.
            if (i < this.noteLines.length) this.noteLines[i].heightQ = LINE_HEIGHT_Q.SECONDARY
            if (i < this.infoLines.length) this.infoLines[i].heightQ = LINE_HEIGHT_Q.SECONDARY
        }

        this._buildSlotQuarterY()
    }

    /**
     * Baut die drei Pane-Y-Grids (_lineQuarterY / _noteLineQuarterY /
     * _infoLineQuarterY) und _slotBottomQ slot-weise auf.
     *
     * Ein "Slot" ist ein zusammenhängender Zeilenblock, der zu demselben Absatz
     * (bzw. co-located Outline-Item + Folge-Para) gehört — erkennbar daran, dass
     * sich das Paar (lines[i].paragraphId, noteLines[i].paragraphId) ändert.
     *
     * Innerhalb eines Slots stapelt jedes Pane SEINE Content-Zeilen top-aligned
     * ab dem Slot-Top mit der EIGENEN heightQ (Note/Info 4QL, Main 6/10QL). Die
     * Slot-Höhe = max der drei Pane-Content-Höhen; das kürzere Pane lässt unten
     * Platz (Teil-Padding) und re-synchronisiert so den nächsten Slot-Top.
     * Padding-Zeilen sitzen auf der Slot-Unterkante (Höhe 0), damit der Outline-
     * Baum-Connector über die letzte Content-Zeile bis zur Slot-Unterkante reicht.
     */
    _buildSlotQuarterY() {
        const n = this.lines.length
        let q = 0
        let i = 0
        while (i < n) {
            const mainPid = this.lines[i].paragraphId
            const notePid = this.noteLines[i] ? this.noteLines[i].paragraphId : mainPid
            // Slot-Ende: erstes Paar (mainPid, notePid), das abweicht
            let e = i + 1
            while (e < n) {
                const m = this.lines[e].paragraphId
                const nt = this.noteLines[e] ? this.noteLines[e].paragraphId : m
                if (m !== mainPid || nt !== notePid) break
                e++
            }

            const slotTop = q + (this.lines[i].spacingBefore || 0)
            const mainH = this._paneContentHeightQ(this.lines, i, e)
            const noteH = this._paneContentHeightQ(this.noteLines, i, e)
            const infoH = this._paneContentHeightQ(this.infoLines, i, e)
            // Baseline-Excess (Viertel): bei einem Heading sitzt die erste Note/Info-
            // Zeile auf der Heading-Baseline. Der Teil, der nicht in die 4QL-Zelle
            // passt (Render-Cap), wird hier ins Grid gebacken → die Note/Info-Zeilen
            // starten um excessQ tiefer, der Block bleibt intern 4QL-gestapelt.
            const excessQ = this._paneBaselineExcessQ(this.lines[i].heightQ || 4)
            const noteExtent = noteH > 0 ? noteH + excessQ : 0
            const infoExtent = infoH > 0 ? infoH + excessQ : 0
            // Mindesthöhe: erste Main-Zeile (deckt leere/all-Padding-Panes ab).
            // Aufrunden, damit der nächste Slot-Top (und damit das Main-Grid) ganzzahlig
            // bleibt — Scroll/Minimap/Scrollbar rechnen mit ganzzahligen Vierteln.
            const slotH = Math.ceil(Math.max(mainH, noteExtent, infoExtent, this.lines[i].heightQ || 4))
            const slotBottom = slotTop + slotH

            this._layoutPaneSlot(this._lineQuarterY, this.lines, i, e, slotTop, slotBottom, 0)
            this._layoutPaneSlot(this._noteLineQuarterY, this.noteLines, i, e, slotTop, slotBottom, excessQ)
            this._layoutPaneSlot(this._infoLineQuarterY, this.infoLines, i, e, slotTop, slotBottom, excessQ)
            for (let k = i; k < e; k++) this._slotBottomQ[k] = slotBottom

            // Treibendes Pane für die Connector-Geometrie = das Pane mit der
            // größten Content-Höhe im Slot (Main/Note/Info). Boris' Vorgabe: die
            // Outline-Linie umspannt den GANZEN Slot, der └-Eckpunkt sitzt auf der
            // Mitte der letzten echten Zeile dieses Pane. So degeneriert die
            // Vertikale nie am kollabierten Padding-Grid eines kürzeren Pane.
            // Bei Main-hidden (Outline maximiert) ist nur die Note sichtbar → note.
            let connField = 'text'
            let connBest = mainH
            if (noteExtent > connBest) { connField = 'note'; connBest = noteExtent }
            if (infoExtent > connBest) { connField = 'info'; connBest = infoExtent }
            if (this._paneFilterMode) connField = 'note'
            for (let k = i; k < e; k++) this._slotConnectorField[k] = connField

            q = slotBottom
            i = e
        }
        this._totalQuarters = q
    }

    /**
     * Summe der heightQ aller Content-Zeilen (ohne Padding) eines Pane-Slots.
     */
    _paneContentHeightQ(lines, start, end) {
        let h = 0
        for (let k = start; k < end; k++) {
            const ln = lines[k]
            if (ln && !ln.isPadding) h += (ln.heightQ || 4)
        }
        return h
    }

    /**
     * Stapelt die Content-Zeilen eines Pane-Slots top-aligned ab slotTop;
     * Padding-Zeilen landen auf slotBottom (Höhe 0).
     */
    _layoutPaneSlot(arr, lines, start, end, slotTop, slotBottom, contentOffsetQ = 0) {
        let y = slotTop + contentOffsetQ
        let seenContent = false
        for (let k = start; k < end; k++) {
            const ln = lines[k]
            if (ln && !ln.isPadding) {
                arr[k] = y
                y += (ln.heightQ || 4)
                seenContent = true
            } else {
                // Trailing-Padding (nach Content) sitzt auf der Slot-Unterkante,
                // damit der Outline-Connector bis dort reicht. Leading-Padding (vor
                // dem ersten Content, z.B. vor dem Background-Separator) sitzt auf
                // slotTop — sonst entstünde ein nicht-monotones Quarter-Grid und der
                // Hit-Test (Binärsuche) träfe daneben.
                arr[k] = seenContent ? slotBottom : y
            }
        }
    }

    /**
     * Baseline-Excess in Vierteln für die Note/Info-Panes eines Slots: der Teil des
     * idealen Sekundärtext-Baseline-Shifts, der nicht in die 4QL-Zelle passt und
     * deshalb ins Grid gebacken wird (statt beim Rendern angewendet zu werden, wo er
     * Render und Grid entkoppeln und das Scroll-„Hereinspringen" auslösen würde).
     * Nur bei Headings > 0. Siehe `renderer.secondaryBaselineExcessPx`.
     * @param {number} mainHeightQ - heightQ der Haupt-(ersten) Zeile des Slots
     * @returns {number} Excess in Vierteln (>= 0)
     */
    _paneBaselineExcessQ(mainHeightQ) {
        if (!this._useQuarterSpacing() || !this.renderer || !this.renderer.secondaryBaselineExcessPx) return 0
        return this.renderer.secondaryBaselineExcessPx(mainHeightQ) / (this.renderer.charHeight / 4)
    }

    /**
     * Inkrementelles Spacing-Update fuer den Tipp-Pfad (V3.2, System-Review
     * 2026-06-10): updateParagraph mit lineDelta 0 ersetzt die Zeilen eines
     * Absatzes 1:1 — dann muss nur SEIN Slot neu gelayoutet werden, plus ein
     * Suffix-Shift der Y-Grids, falls sich die Slot-Hoehe oder das Spacing
     * des Folge-Slots geaendert hat. Der volle _computeSpacing-Lauf
     * alloziert dagegen 5 Arrays ueber ALLE Zeilen (gemessen: ~3,6 ms pro
     * Tastendruck bei 34k Zeilen).
     *
     * Voraussetzungen (sonst false → Aufrufer macht den vollen Lauf):
     * lineDelta 0, kein Review-Overlay, kein Pane-Filter-Modus, Grids
     * synchron zur Zeilenanzahl, Slot-Grenzen sauber (Co-Location hat
     * updateParagraph schon vorher per Full-Rebuild ausgeschlossen).
     *
     * @param {string} paragraphId
     * @param {number} startLineIndex - erste Zeile des Absatzes
     * @param {number} newLineCount - Zeilenanzahl nach dem Splice
     * @param {number} lineDelta - Zeilen-Differenz alt→neu
     * @returns {boolean} true wenn inkrementell aktualisiert wurde
     */
    _updateSpacingIncremental(paragraphId, startLineIndex, newLineCount, lineDelta) {
        if (lineDelta !== 0) return false
        if (this._reviewHint) return false
        if (this._paneFilterMode) return false
        const n = this.lines.length
        if (!Array.isArray(this._slotBottomQ) || this._slotBottomQ.length !== n) return false
        if (!Array.isArray(this._lineQuarterY) || this._lineQuarterY.length !== n) return false

        const end = startLineIndex + newLineCount
        if (end > n) return false

        if (!this._useQuarterSpacing()) {
            // Monospace: Grids sind i*4 — ein 1:1-Zeilentausch aendert daran
            // nichts. Nur spacingBefore der frisch gesplicten Zeilen nullen
            // (das erledigt sonst der volle Lauf).
            for (let i = startLineIndex; i < end; i++) {
                this.lines[i].spacingBefore = 0
            }
            return true
        }

        // Slot-Grenzen pruefen: der Bereich muss ein eigener, vollstaendiger
        // Slot sein (Nachbarzeilen gehoeren anderen Absaetzen).
        if (!this.lines[startLineIndex] || this.lines[startLineIndex].paragraphId !== paragraphId) return false
        if (startLineIndex > 0 && this.lines[startLineIndex - 1].paragraphId === paragraphId) return false
        if (end < n && this.lines[end].paragraphId === paragraphId) return false

        // Alte Werte sichern, BEVOR der Slot neu geschrieben wird
        const oldSlotBottom = this._slotBottomQ[startLineIndex]
        const oldNextSb = end < n ? (this.lines[end].spacingBefore || 0) : 0

        // spacingBefore + heightQ fuer die Slot-Zeilen — Spiegel der
        // beiden Schleifen im vollen _computeSpacing-Lauf.
        const prevSpacing = startLineIndex > 0
            ? this._getParaSpacing(this.lines[startLineIndex - 1])
            : null
        const currSpacing = this._getParaSpacing(this.lines[startLineIndex])
        for (let i = startLineIndex; i < end; i++) {
            const line = this.lines[i]
            line.spacingBefore = (i === startLineIndex && startLineIndex > 0)
                ? collapseSpacing(prevSpacing, currSpacing)
                : 0
            if (line.isSeparator) {
                line.heightQ = LINE_HEIGHT_Q.SEPARATOR
            } else {
                const para = this.model.getParagraph(line.paragraphId)
                line.heightQ = para ? this._getParaHeightQ(para) : LINE_HEIGHT_Q.NORMAL
            }
            if (i < this.noteLines.length) this.noteLines[i].heightQ = LINE_HEIGHT_Q.SECONDARY
            if (i < this.infoLines.length) this.infoLines[i].heightQ = LINE_HEIGHT_Q.SECONDARY
        }

        // Slot-Layout — Spiegel von _buildSlotQuarterY fuer genau diesen Slot
        const prevBottom = startLineIndex > 0 ? this._slotBottomQ[startLineIndex - 1] : 0
        const slotTop = prevBottom + (this.lines[startLineIndex].spacingBefore || 0)
        const mainH = this._paneContentHeightQ(this.lines, startLineIndex, end)
        const noteH = this._paneContentHeightQ(this.noteLines, startLineIndex, end)
        const infoH = this._paneContentHeightQ(this.infoLines, startLineIndex, end)
        const excessQ = this._paneBaselineExcessQ(this.lines[startLineIndex].heightQ || 4)
        const noteExtent = noteH > 0 ? noteH + excessQ : 0
        const infoExtent = infoH > 0 ? infoH + excessQ : 0
        const slotH = Math.ceil(Math.max(mainH, noteExtent, infoExtent, this.lines[startLineIndex].heightQ || 4))
        const slotBottom = slotTop + slotH

        this._layoutPaneSlot(this._lineQuarterY, this.lines, startLineIndex, end, slotTop, slotBottom, 0)
        this._layoutPaneSlot(this._noteLineQuarterY, this.noteLines, startLineIndex, end, slotTop, slotBottom, excessQ)
        this._layoutPaneSlot(this._infoLineQuarterY, this.infoLines, startLineIndex, end, slotTop, slotBottom, excessQ)
        for (let k = startLineIndex; k < end; k++) this._slotBottomQ[k] = slotBottom

        let connField = 'text'
        let connBest = mainH
        if (noteExtent > connBest) { connField = 'note'; connBest = noteExtent }
        if (infoExtent > connBest) { connField = 'info'; connBest = infoExtent }
        for (let k = startLineIndex; k < end; k++) this._slotConnectorField[k] = connField

        // Folge-Slot: dessen spacingBefore haengt von UNSERER Spacing-
        // Definition ab (Margin-Collapsing).
        let newNextSb = oldNextSb
        if (end < n) {
            newNextSb = collapseSpacing(currSpacing, this._getParaSpacing(this.lines[end]))
            this.lines[end].spacingBefore = newNextSb
        }

        // Suffix-Shift: alles nach dem Slot verschiebt sich um die Differenz
        // aus Slot-Hoehen- und Folge-Spacing-Aenderung (meist 0 beim Tippen).
        const deltaQ = (slotBottom - oldSlotBottom) + (newNextSb - oldNextSb)
        if (deltaQ !== 0) {
            for (let i = end; i < n; i++) {
                this._lineQuarterY[i] += deltaQ
                this._noteLineQuarterY[i] += deltaQ
                this._infoLineQuarterY[i] += deltaQ
                this._slotBottomQ[i] += deltaQ
            }
            this._totalQuarters += deltaQ
        }
        return true
    }

    /**
     * Zell-Höhe (Viertel) einer Note/Info-Zeile für Connector-/Clear-/Selektions-
     * Geometrie: bis zur nächsten Zeile im selben Slot, gedeckelt auf die Slot-
     * Unterkante. So füllt die letzte Content-Zeile den Teil-Padding-Gap (der
     * Outline-Connector reicht bis zur Slot-Unterkante), Padding-Zeilen sind 0.
     */
    _paneCellHeightQ(field, lineIndex) {
        const arr = this._paneQuarterY(field)
        const top = arr[lineIndex] || 0
        const slotBottom = this._slotBottomQ[lineIndex] || top
        const next = (lineIndex + 1 < arr.length) ? (arr[lineIndex + 1] || 0) : slotBottom
        return Math.max(0, Math.min(next, slotBottom) - top)
    }

    /**
     * Treibendes Pane ('text'|'note') für die Outline-Connector-Geometrie an einer
     * Zeile. Siehe `_slotConnectorField` (in `_buildSlotQuarterY` slot-weise gesetzt).
     * Default 'text' für Robustheit (Monospace, nicht initialisierte Indizes).
     * @param {number} lineIndex
     * @returns {'text'|'note'}
     */
    _outlineConnectorFieldAt(lineIndex) {
        return this._slotConnectorField[lineIndex] || 'text'
    }

    /**
     * Baut Zeilen neu auf falls Model dirty ist.
     * Rebuild-Entscheidung basiert NUR auf model._dirtyLevel, nicht auf view.isDirty.
     * Aktualisiert auch den Selection-Cache, damit Outline-Notes und Context-Info
     * im Fullscreen-Modus (ohne Main-Render) trotzdem die aktuelle Selection sehen —
     * sonst friert der Selection-Highlight im jeweils maximierten seitlichen Pane ein.
     */
    rebuildIfDirty() {
        const modelDirty = this.model._dirtyLevel
        if (modelDirty === 'STRUCTURAL' && this._linesVersion !== this.model._dirtyVersion) {
            this._rebuildLines()
        } else if (modelDirty === 'PARAGRAPH' && this._linesVersion !== this.model._dirtyVersion) {
            for (const paraId of this.model._dirtyParagraphIds) {
                this.updateParagraph(paraId)
            }
            this._linesVersion = this.model._dirtyVersion
        }
        // Selection-Cache hier zentral aktualisieren — wird sonst nur von
        // EditorView.render() / renderPartial / tryRenderDirtyOnly gemacht,
        // also nur wenn der Main-Pane gerendert wird.
        this._selectionCache = this._buildSelectionCache()
    }

    // ===== Line Breaking =====

    /**
     * Baut den Zeilen-Cache komplett neu auf.
     * Erzeugt auch noteLines[] parallel mit Zeilen-Synchronisation.
     */
    _rebuildLines() {
        this.lines = []
        this.noteLines = []
        this.infoLines = []
        this._paragraphLineIndex.clear()
        this._textPositionMaps.clear()
        this._notePositionMaps.clear()
        this._infoPositionMaps.clear()

        // Scroll-State zurücksetzen — nach vollem Rebuild ist alter Delta bedeutungslos
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
        this._shiftResidual = 0
        this._monoScrollAccumulator = 0
        this._hadLineDelta = false

        // Parse-Caches prunen: nur Einträge für gelöschte Paragraphs entfernen
        // (Text-Vergleich in _getParsedRichText stellt Cache-Korrektheit sicher)
        this._pruneParseCache(this._textParseCache)
        this._pruneParseCache(this._noteParseCache)
        this._pruneParseCache(this._infoParseCache)

        // Separator-Map für Link-Auflösung (Label + isBroken)
        this._separatorMap = this.model.buildSeparatorMap()
        this._separatorMapVersion = (this._separatorMapVersion || 0) + 1

        // Abschnitts-Einrückung und Background-Start VOR dem Zeilen-Umbruch berechnen,
        // da _breakNote() → getNoteTextWidth() → getNoteStartCol() die _sectionIndents braucht.
        // Separator-Nummern kommen aus _separatorMap (buildSeparatorMap, oben).
        // Owner-Depth (v4): für Normal-Paras die Tiefe des letzten Outline-Items
        // davor in derselben Section — wird in getNoteStartCol() für die korrekte
        // Note-Spalten-Einrückung gebraucht.
        this._sectionIndents = new Map()  // paragraphId → Einrückung (2 * Separator-Tiefe)
        this._ownerDepths = new Map()     // paragraphId → Owner-Outline-Tiefe (-1 = kein Owner)
        this._backgroundStartIndex = -1
        let currentSectionIndent = 0
        let currentOwnerDepth = -1
        for (let pi = 0; pi < this.model.paragraphs.length; pi++) {
            const para = this.model.paragraphs[pi]
            if (para.type === 'separator') {
                currentOwnerDepth = -1  // Section-Wechsel: Owner-Stack zurücksetzen
                const d = para.outlineDepth
                if (d === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                    this._backgroundStartIndex = pi
                    currentSectionIndent = 0
                } else {
                    currentSectionIndent = d * 2
                }
            } else {
                this._sectionIndents.set(para.id, currentSectionIndent)
                if (para.isOutlineItem()) {
                    currentOwnerDepth = para.outlineDepth
                }
                this._ownerDepths.set(para.id, currentOwnerDepth)
            }
        }

        // Pane-Filter: Absätze überspringen wenn Main nicht sichtbar
        const filterMode = this._getPaneFilterMode()
        this._paneFilterMode = filterMode
        // Tracking pro Abschnitt: ersten nicht-qualifizierten vorläufig aufnehmen,
        // bei Erscheinen eines qualifizierten wieder entfernen
        let provisionalStart = -1   // Index in lines[] wo der vorläufige beginnt
        let provisionalCount = 0    // Anzahl Zeilen des vorläufigen
        let provisionalId = null    // paragraphId des vorläufigen
        let sectionHasQualified = false

        // Collapse-Tracking (siehe docs/pane-formatting-specification.md §5.4).
        // sectionCollapseDepth: Tiefe des aktiven section-collapse-Hosts; null = inaktiv.
        //   Endet wenn ein Separator mit outlineDepth <= Tiefe auftaucht.
        // activeCollapseDepth: Tiefe des aktiven active-collapse-Hosts; null = inaktiv.
        //   Endet per OutlineTree-Subtree-Regel (Separator / depth-Vergleich).
        let sectionCollapseDepth = null
        let activeCollapseDepth = null

        // v4 Co-Location (Spec §3.2): Ein Outline-Item OHNE eigene Main-Zeile
        // teilt sich die Y-Zeile mit dem direkt folgenden Normal-Para. Sein
        // Note-Inhalt landet im noteLines-Array unter der paragraphId des
        // Outline-Items, sein Text-Slot bleibt vom Normal-Para belegt.
        // Wenn kein Folge-Normal-Para existiert (letzter Para, oder darauf
        // folgt Outline-Item/Separator), wird das Outline-Item Solo gerendert
        // mit einer Phantom-Main-Zeile (`>` rechts; Spec §3.4).
        let pendingOutlineBuddy = null  // Outline-Item-Para, das auf den nächsten Normal-Para wartet

        for (const para of this.model.paragraphs) {
            // ── Collapse-Zone-Ende prüfen (vor Sichtbarkeitsentscheidung) ──

            // Active-Collapse: endet per v4-Subtree-Regel (Spec §2.3).
            // Normal-Paras beenden den Range NIE — sie haben keine eigene
            // Tiefe, sondern erben sie vom letzten Outline-Item.
            if (activeCollapseDepth !== null) {
                const N = activeCollapseDepth
                const endsActive =
                    para.type === 'separator' ||
                    (para.isOutlineItem() && para.outlineDepth <= N)
                if (endsActive) activeCollapseDepth = null
            }

            // Section-Collapse: endet an Separator mit gleicher oder geringerer Tiefe.
            if (sectionCollapseDepth !== null && para.type === 'separator' &&
                para.outlineDepth <= sectionCollapseDepth) {
                sectionCollapseDepth = null
            }

            // ── Sichtbarkeit: wenn eine Zone noch aktiv ist, ist para versteckt. ──
            // Hosts selbst sind sichtbar, weil ihre Zone erst NACH diesem Check
            // unten eröffnet wird.
            const hiddenByCollapse =
                sectionCollapseDepth !== null || activeCollapseDepth !== null
            if (hiddenByCollapse) {
                continue
            }

            // Padding-Zeilen vor Background-Separator einfuegen
            if (para.type === 'separator' && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                // Pending-Buddy abschließen: Separator beendet Co-Location-Chance.
                if (pendingOutlineBuddy) {
                    this._pushSoloOutlineLines(pendingOutlineBuddy)
                    if (pendingOutlineBuddy.activeCollapsed) activeCollapseDepth = pendingOutlineBuddy.outlineDepth
                    pendingOutlineBuddy = null
                }
                for (let pad = 0; pad < EDITOR.BACKGROUND_PADDING_LINES; pad++) {
                    this.lines.push(this._makePaddingLine(para.id, 'text'))
                    this.noteLines.push(this._makePaddingLine(para.id, 'note'))
                    this.infoLines.push(this._makePaddingLine(para.id, 'info'))
                }
            }

            if (para.type === 'separator') {
                // Pending-Buddy noch offen? → Solo rauspushen (Separator beendet Co-Location).
                if (pendingOutlineBuddy) {
                    this._pushSoloOutlineLines(pendingOutlineBuddy)
                    if (pendingOutlineBuddy.activeCollapsed) activeCollapseDepth = pendingOutlineBuddy.outlineDepth
                    pendingOutlineBuddy = null
                }
                // Neuer Abschnitt: Reset
                provisionalStart = -1
                provisionalCount = 0
                provisionalId = null
                sectionHasQualified = false
            } else if (filterMode) {
                if (this._paraPassesFilter(para, filterMode)) {
                    // Qualifizierter Absatz: vorläufigen rauswerfen (falls vorhanden)
                    if (provisionalStart >= 0) {
                        this.lines.splice(provisionalStart, provisionalCount)
                        this.noteLines.splice(provisionalStart, provisionalCount)
                        this.infoLines.splice(provisionalStart, provisionalCount)
                        this._paragraphLineIndex.delete(provisionalId)
                        provisionalStart = -1
                        provisionalId = null
                    }
                    sectionHasQualified = true
                } else {
                    // Nicht qualifiziert
                    if (!sectionHasQualified && provisionalStart < 0) {
                        // Erster im Abschnitt ohne Qualifizierte → vorläufig aufnehmen
                        provisionalStart = this.lines.length
                        provisionalId = para.id
                        // Zeilen werden unten normal aufgenommen, Count danach setzen
                    } else {
                        // Überspringen
                        continue
                    }
                }
            }

            // ── Co-Location-Branch: Outline-Item puffert auf nächsten Normal-Para ──
            // Auch ein collapsed Outline-Item co-located, WENN direkt ein Normal-Para
            // folgt: sein Range wird zu einer Zeile zusammengeklappt, und diese Zeile
            // zeigt den folgenden Text-Para gekürzt (Spec §3.2 + §4.8) — NICHT den
            // leeren Text des Outline-Items. Der collapseHost-Pfad unten kürzt die
            // Buddy-Text-Zeile, das Öffnen der Collapse-Zone übernimmt der Push.
            if (para.type !== 'separator' && para.isOutlineItem()) {
                // Falls schon ein Buddy hängt (zwei Outline-Items hintereinander):
                // ersten als Solo rausschicken, jetzigen puffern. WICHTIG: Buddy
                // direkt zurücksetzen — sonst bleibt er gesetzt, wenn dieses Item
                // collapsed ist (unten `continue` vor der Neuzuweisung) und würde
                // beim nächsten Para ein ZWEITES Mal gepusht (Doppel-Display).
                if (pendingOutlineBuddy) {
                    this._pushSoloOutlineLines(pendingOutlineBuddy)
                    if (pendingOutlineBuddy.activeCollapsed) activeCollapseDepth = pendingOutlineBuddy.outlineDepth
                    pendingOutlineBuddy = null
                }
                // Ein collapsed Item darf NUR puffern, wenn direkt ein Normal-Para
                // folgt (der die gekürzte Host-Zeile stellt). Folgt ein Item/Separator/
                // Doc-Ende, MUSS es sofort solo gerendert werden und seine Collapse-Zone
                // sofort öffnen — sonst läuft die Zone-Ende-/Sichtbarkeits-Prüfung des
                // nächsten Paragraphen zu früh (Zone noch geschlossen → Range-Mitglieder
                // würden fälschlich sichtbar).
                if (para.activeCollapsed && !this._nextParagraphIsNormal(para)) {
                    this._pushSoloOutlineLines(para)
                    activeCollapseDepth = para.outlineDepth
                    continue
                }
                pendingOutlineBuddy = para
                continue
            }

            // ── Normaler Push (Normal-Para, Separator, oder collapsed Outline-Item) ──
            const outlineForNote = (
                para.type !== 'separator' &&
                !para.isOutlineItem() &&
                pendingOutlineBuddy
            ) ? pendingOutlineBuddy : null
            if (outlineForNote) pendingOutlineBuddy = null

            const startLineIdx = this.lines.length
            this._paragraphLineIndex.set(para.id, startLineIdx)
            if (outlineForNote) {
                this._paragraphLineIndex.set(outlineForNote.id, startLineIdx)
            }

            const textLines = this._breakParagraph(para)
            const noteTextLines = outlineForNote
                ? this._breakNote(outlineForNote)
                : this._breakNote(para)
            const infoTextLines = this._breakInfo(para)
            const notePaddingId = outlineForNote ? outlineForNote.id : para.id

            // Active-Collapse: text auf erste Zeile reduzieren, Ellipsis-Flag setzen
            // (siehe pane-formatting-specification.md §5.2). note/info bleiben
            // unangetastet — mehrzeilige Notes/Infos treiben maxCount weiterhin hoch
            // und werden in Main gepadded. Host ist immer der co-located Buddy:
            // ein collapsed Outline-Item OHNE Folge-Para läuft über
            // _pushSoloOutlineLines (Phantom-Zeile), nicht hier.
            const collapseHost = (outlineForNote && outlineForNote.activeCollapsed) ? outlineForNote : null
            if (collapseHost && textLines.length > 0) {
                // Ellipsis zeigt an, dass der Collapse mehr versteckt als die eine
                // sichtbare Zeile: entweder war der Buddy-Text mehrzeilig, oder der
                // Range reicht über den Buddy hinaus (weitere Paras werden gleich
                // per activeCollapseDepth versteckt).
                const wasMultiLine = textLines.length > 1
                const buddyIdx = this.model.getParagraphIndex(para.id)
                const next = buddyIdx >= 0 ? this.model.paragraphs[buddyIdx + 1] : null
                const rangeExtendsBeyondBuddy = !!next &&
                    next.type !== 'separator' &&
                    !(next.isOutlineItem() && next.outlineDepth <= collapseHost.outlineDepth)
                const first = textLines[0]
                first.isCollapsedActiveHost = true
                first.collapseHasEllipsis = wasMultiLine || rangeExtendsBeyondBuddy
                // Bei Umbruch (mehrzeilig) ist die erste Zeile abgeschnitten → die
                // Ellipsis ersetzt deren letzte 3 Zeichen. Ohne Umbruch zeigt die
                // erste Zeile den vollständigen Text → die Ellipsis wird dahinter
                // angehängt ("Kapitel 1…" statt "Kapite…").
                first.collapseEllipsisAppend = !wasMultiLine
                textLines.length = 1
            }

            // Zeilen-Synchronisation: Padding-Zeilen auffüllen
            const maxCount = Math.max(textLines.length, noteTextLines.length, infoTextLines.length)

            while (textLines.length < maxCount) {
                textLines.push(this._makePaddingLine(para.id, 'text'))
            }
            while (noteTextLines.length < maxCount) {
                noteTextLines.push(this._makePaddingLine(notePaddingId, 'note'))
            }
            while (infoTextLines.length < maxCount) {
                infoTextLines.push(this._makePaddingLine(para.id, 'info'))
            }

            this.lines.push(...textLines)
            this.noteLines.push(...noteTextLines)
            this.infoLines.push(...infoTextLines)

            // Count des vorläufig aufgenommenen passiven Absatzes setzen
            if (provisionalId === para.id) {
                provisionalCount = this.lines.length - provisionalStart
            }

            // ── Neue Collapse-Zonen öffnen (nach Aufnahme des Hosts) ──
            if (para.type === 'separator' && para.sectionCollapsed) {
                sectionCollapseDepth = para.outlineDepth
            }
            if (collapseHost && collapseHost.activeCollapsed) {
                activeCollapseDepth = collapseHost.outlineDepth
            }
        }

        // Document-Ende: noch hängender Outline-Item-Buddy → Solo rauspushen
        if (pendingOutlineBuddy) {
            this._pushSoloOutlineLines(pendingOutlineBuddy)
            pendingOutlineBuddy = null
        }

        // Delta-Review: Insert-Overlays als virtuelle Zeilen einsetzen.
        // Muss VOR dem Separator-/Background-Pass laufen, damit isBackground
        // konsistent durchpropagiert wird.
        this._applyReviewInserts()

        // Separator-Nummern und Background-Flag in alle ScreenLine-Arrays eintragen
        for (const lineArray of [this.lines, this.noteLines, this.infoLines]) {
            let bg = false
            for (const line of lineArray) {
                if (line.isSeparator) {
                    const sepEntry = this._separatorMap.get(line.paragraphId)
                    line.separatorNumber = sepEntry ? sepEntry.number : ''
                    const para = this.model.getParagraph(line.paragraphId)
                    line.separatorDepth = para ? para.outlineDepth : 0
                    line.separatorTitle = para ? para.text : ''
                    // Background-Separator erkennen
                    if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                        bg = true
                    }
                }
                line.isBackground = bg
            }
        }

        // Outline-Spalten synchron mit den Zeilen berechnen (ohne Cursor-Highlighting)
        // v4 Co-Location: noteLines[i].paragraphId zeigt bei Co-Located-Outline-
        // Items auf das Outline-Item (während lines[i] zum Normal-Para gehört).
        // buildOutlineColumns muss das Outline-Item finden, damit (-) gezeichnet wird.
        this._outlineColumns = buildOutlineColumns(this.model.paragraphs, this.noteLines)

        // Quarter-Line Spacing berechnen
        this._computeSpacing()

        // Viewport validieren (kann nach Resize ungültig sein)
        this._clampViewport()

        this.isDirty = true
        this._linesVersion = this.model._dirtyVersion
    }

    /**
     * Bricht einen Text in Screen-Zeilen um (generische Methode).
     * @param {number} paragraphId - ID des zugehörigen Paragraphs
     * @param {string} text - Der umzubrechende Text
     * @param {number} textWidth - Verfügbare Breite
     * @param {string} field - 'text' oder 'note'
     * @returns {ScreenLine[]}
     */
    _breakText(paragraphId, text, textWidth, field = 'text', mapping = null, heightQ = 4, overrideFormatRuns = null) {
        // Proportional: Pixel-basierter Word-Wrap
        if (this.renderer && this.renderer.isProportional()) {
            const availablePixelWidth = textWidth * this.renderer.charWidth
            return this._breakTextProportional(paragraphId, text, availablePixelWidth, field, mapping, heightQ, overrideFormatRuns)
        }

        // Monospace: bestehende Logik
        if (mapping && mapping.displayLength !== text.length) {
            return this._breakTextWithMapping(paragraphId, text, textWidth, field, mapping)
        }

        return this._breakTextRaw(paragraphId, text, textWidth, field)
    }

    /**
     * Berechnet den Hanging Indent für Listen-Text.
     * Gibt die Display-Breite des Listen-Prefix zurück (Spaces + Marker + Space).
     */
    _getHangingIndent(text) {
        return getHangingIndent(text)
    }

    /**
     * Liefert eine "Effective"-Sicht eines Paragraphs fuer den Render-Pfad
     * waehrend eines Delta-Reviews. Wenn der Para einen aktiven Rewrite-
     * Vorschlag hat und der Toggle auf 'proposed' steht, werden die Block-
     * Felder (headingLevel, listType, listLevel, blockquote) mit den Werten
     * aus dem Vorschlag ueberschrieben — der Composite-Absatz wird dann live
     * in Vorschlags-Block-Format gerendert (Heading-Groesse, Bullet-Marker,
     * Blockquote-Bar). Toggle T schaltet zurueck auf den alten Stand.
     *
     * Block-WYSIWYG: Boris-Entscheidung 2026-05-02 — der User soll den
     * Block-Wechsel direkt sehen, statt einer Hint-Zeile mit Beschreibung.
     *
     * Gibt einen flachen Klon zurueck, mutiert nichts.
     *
     * @param {object|null} para
     * @returns {object|null}
     */
    _effectiveParaForRender(para) {
        if (!para || !this._reviewComposites) return para
        const composite = this._reviewComposites.get(para.id)
        if (!composite || composite.kind !== 'rewrite') return para
        const overlay = composite.overlay
        const view = overlay?.activeView || 'proposed'
        if (view === 'original') return para
        // Prototype-erhaltender Override, damit Methoden wie `isOutlineItem()`
        // auch auf dem Composite-Wrapper aufrufbar bleiben.
        const proto = Object.getPrototypeOf(para)
        const override = Object.create(proto)
        Object.assign(override, para)
        override.headingLevel = overlay.proposedHeadingLevel || 0
        override.listType = overlay.proposedListType || null
        override.listLevel = overlay.proposedListLevel || 0
        override.blockquote = !!overlay.proposedBlockquote
        return override
    }

    /**
     * Liefert einen Pseudo-Para fuer eine virtuelle Insert-Vorschau-Zeile.
     * Wird von `_getParaSpacing` und `_rebuildLines` benutzt, wenn
     * `model.getParagraph(line.paragraphId)` null liefert (Insert-fakeIds
     * stehen nicht im Modell). Ohne diesen Helper fielen Heading-Inserts auf
     * NORMAL-Schriftgroesse und NORMAL-Margin zurueck.
     *
     * Bei activeView='original' liefert der Pseudo-Para Plain-Defaults — der
     * Insert ist semantisch "nicht drin", die virtuelle Zeile rendert dann
     * Strikethrough-Plaintext mit NORMAL-Hoehe.
     *
     * @param {object} overlay - line._reviewInsertOverlay
     * @returns {object} Pseudo-Paragraph mit type/headingLevel/listType/...
     */
    _pseudoParaFromInsertOverlay(overlay) {
        const view = overlay?.activeView || 'proposed'
        if (view === 'original') {
            return { id: '__insert_pseudo__', type: 'paragraph' }
        }
        return {
            id: '__insert_pseudo__',
            type: 'paragraph',
            headingLevel: overlay.proposedHeadingLevel || 0,
            listType: overlay.proposedListType || null,
            listLevel: overlay.proposedListLevel || 0,
            blockquote: !!overlay.proposedBlockquote
        }
    }

    /**
     * Berechnet den visuellen Prefix und Hanging Indent für RT-Block-Formate.
     * Im RT-Modus steht kein Prefix im Text — der View generiert ihn.
     * @param {object} para - Paragraph-Objekt
     * @returns {{ prefix: string, hangingIndent: number }}
     */
    _getRichTextPrefix(para) {
        if (!para) return { prefix: '', hangingIndent: 0 }
        para = this._effectiveParaForRender(para)

        if (para.listType === 'bullet') {
            const indent = '  '.repeat(para.listLevel || 0)
            const prefix = indent + '• '
            return { prefix, hangingIndent: prefix.length }
        }

        if (para.listType === 'numbered') {
            const indent = '  '.repeat(para.listLevel || 0)
            // Nummer: Position im Eltern-Block zählen
            const number = this._getListNumber(para)
            const prefix = indent + number + '. '
            return { prefix, hangingIndent: prefix.length }
        }

        if (para.headingLevel > 0) {
            // Proportional: Heading durch größere Schrift erkennbar, kein #-Prefix nötig
            if (this.renderer && this.renderer.isProportional()) {
                return { prefix: '', hangingIndent: 0 }
            }
            const prefix = '#'.repeat(para.headingLevel) + ' '
            return { prefix, hangingIndent: prefix.length }
        }

        if (para.blockquote) {
            return { prefix: '> ', hangingIndent: 2 }
        }

        return { prefix: '', hangingIndent: 0 }
    }

    /**
     * Ermittelt die hierarchische Nummer eines nummerierten Listen-Eintrags.
     * Sub-Nummerierung je nach Einrückungstiefe: "1.", "1.1.", "1.2.3." etc.
     * Pro Ebene: rückwärts zählen bis ein Eintrag auf niedrigerer Ebene den Scope begrenzt.
     * @param {object} para - Paragraph mit listType 'numbered'
     * @returns {string} Nummer als String (z.B. "1", "1.2", "1.2.3")
     */
    _getListNumber(para) {
        const targetLevel = para.listLevel || 0
        const idx = this.model.getParagraphIndex(para.id)
        const numbers = []

        for (let level = 0; level <= targetLevel; level++) {
            let count = 0
            let startOffset = 0  // Startnummer des ersten Items in der Kette
            for (let i = idx; i >= 0; i--) {
                const p = this.model.paragraphs[i]
                if (p.type === 'separator') break
                if (!p.listType) break
                if (p.listType === 'numbered' && p.listLevel === level) {
                    count++
                    // listStart des ersten gefundenen Items (= letztes in der Rückwärts-Suche)
                    if (p.listStart > 1) startOffset = p.listStart - 1
                    else startOffset = 0
                } else if (p.listLevel < level) {
                    break
                }
            }
            numbers.push(Math.max(1, count + startOffset))
        }

        return numbers.map(String).join('.')
    }

    /**
     * Bricht Text um ohne Mapping (Originalverhalten).
     */
    _breakTextRaw(paragraphId, text, textWidth, field = 'text', hangingIndent = -1) {
        const lines = []

        // Auto-berechne Hanging Indent wenn nicht angegeben
        let rtPrefixText = ''  // RT-Modus: visueller Prefix (nicht im Text)
        if (hangingIndent < 0) {
            if (field === 'text') {
                const para = this.model.getParagraph(paragraphId)
                const rt = this._getRichTextPrefix(para)
                hangingIndent = rt.hangingIndent
                rtPrefixText = rt.prefix
            } else {
                hangingIndent = 0
            }
        }

        // Leerer Text = eine leere Zeile
        if (text.length === 0) {
            const line = new ScreenLine(paragraphId, 0, 0, field)
            line.isFirstLine = true
            line.isLastLine = true
            // RT-Prefix auch bei leerem Text setzen (z.B. leerer Bullet-Punkt)
            if (rtPrefixText) {
                line.rtPrefix = rtPrefixText
                line.hangingIndent = hangingIndent
            }
            lines.push(line)
            return lines
        }

        // Text in Zeilen aufteilen (Word-Wrap mit \n als erzwungener Umbruch)
        let offset = 0
        let isFirst = true
        while (offset < text.length) {
            // RT: Prefix ist NICHT im Text → erste Zeile auch reduzieren
            // MD: Prefix IST im Text → erste Zeile bekommt volle Breite
            const lineWidth = (isFirst && !rtPrefixText) ? textWidth : Math.max(1, textWidth - hangingIndent)

            // Erzwungener Umbruch bei \n (vor dem normalen Word-Wrap)
            const newlinePos = text.indexOf('\n', offset)
            let endOffset
            // Display-Spalten zählen (Emoji = 2 Spalten)
            let cols = 0
            let scanPos = offset
            while (scanPos < text.length && cols < lineWidth) {
                if (text[scanPos] === '\n') break
                const cp = text.codePointAt(scanPos)
                const utf16Len = cp > 0xFFFF ? 2 : 1
                const ch = text.substr(scanPos, utf16Len)
                const w = charDisplayWidth(ch)
                // Prüfe ob Zeichen noch reinpasst
                if (cols + w > lineWidth) break
                cols += w
                scanPos += utf16Len
            }
            if (newlinePos >= 0 && newlinePos >= offset && newlinePos < scanPos) {
                // \n innerhalb der Zeilenbreite: Zeile endet vor dem \n
                endOffset = newlinePos
            } else {
                endOffset = Math.min(scanPos, text.length)

                // Wortumbruch: Am letzten Leerzeichen umbrechen wenn möglich
                if (endOffset < text.length && text[endOffset] !== ' ' && text[endOffset] !== '\n') {
                    const lastSpace = text.lastIndexOf(' ', endOffset)
                    if (lastSpace > offset) {
                        endOffset = lastSpace + 1  // Nach dem Leerzeichen umbrechen
                    }
                }
            }

            // Leerzeichen am Anfang der nächsten Zeile zählen
            let skippedChars = 0
            if (endOffset < text.length) {
                if (text[endOffset] === '\n') {
                    // \n überspringen (wird nicht angezeigt)
                    skippedChars = 1
                } else {
                    let skip = endOffset
                    while (skip < text.length && text[skip] === ' ') {
                        skip++
                    }
                    if (skip < text.length) {
                        // Spaces zwischen zwei Zeilen: überspringen
                        skippedChars = skip - endOffset
                    } else {
                        // Trailing Spaces am Textende: zur aktuellen Zeile zählen
                        endOffset = text.length
                    }
                }
            }

            const line = new ScreenLine(paragraphId, offset, endOffset, field)
            line.skippedSpaces = skippedChars
            // RT: hangingIndent auf ALLEN Zeilen (Prefix nicht im Text, aber visuell vorhanden)
            // MD: hangingIndent nur auf Continuation-Zeilen (Prefix IST im Text der ersten Zeile)
            line.hangingIndent = (isFirst && !rtPrefixText) ? 0 : hangingIndent
            lines.push(line)

            // Nächste Zeile beginnt nach den übersprungenen Zeichen
            offset = endOffset + skippedChars
            isFirst = false
        }

        // Trailing \n: leere Zeile nach dem Soft Linebreak erzeugen
        // (Cursor muss dort positionierbar sein)
        if (text.length > 0 && text[text.length - 1] === '\n') {
            const line = new ScreenLine(paragraphId, text.length, text.length, field)
            line.hangingIndent = hangingIndent
            lines.push(line)
        }

        // Erste und letzte Zeile markieren
        if (lines.length > 0) {
            lines[0].isFirstLine = true
            lines[lines.length - 1].isLastLine = true
            if (rtPrefixText) {
                // RT-Modus: Prefix ist NICHT im Text → als rtPrefix-Property speichern
                lines[0].rtPrefix = rtPrefixText
            } else if (hangingIndent > 0) {
                // MD-Modus: Prefix IST im Text → listPrefixLen sagt dem Renderer
                lines[0].listPrefixLen = hangingIndent
            }
        }

        return lines
    }

    /**
     * Bricht Text um MIT Display-Mapping.
     * Word-Wrap wird auf dem Display-Text (ohne Marker) ausgeführt,
     * dann werden die Offsets zurück auf Data-Offsets konvertiert.
     */
    _breakTextWithMapping(paragraphId, text, textWidth, field, mapping) {
        const { displayToData, displayLength } = mapping

        // Display-Text: bei Links aus Mapping, sonst aus Data-Text rekonstruieren
        let displayText
        if (mapping.displayText) {
            displayText = mapping.displayText
        } else {
            const displayChars = []
            for (let i = 0; i < displayLength; i++) {
                displayChars.push(text[displayToData[i]])
            }
            displayText = displayChars.join('')
        }

        // hangingIndent = -1: _breakTextRaw ermittelt RT-Prefix selbst aus Paragraph-Daten
        const displayLines = this._breakTextRaw(paragraphId, displayText, textWidth, field, -1)

        // Display-Offsets zurück zu Data-Offsets konvertieren
        for (let idx = 0; idx < displayLines.length; idx++) {
            const line = displayLines[idx]

            if (idx === 0) {
                // Erste Zeile: beginnt immer bei Data-Offset 0
                // (auch wenn führende Marker ausgeblendet sind)
                line.startOffset = 0
            } else if (line.startOffset < displayLength) {
                line.startOffset = displayToData[line.startOffset]
            } else {
                line.startOffset = text.length
            }

            if (line.endOffset < displayLength) {
                line.endOffset = displayToData[line.endOffset]
            } else {
                line.endOffset = text.length
            }
        }

        return displayLines
    }

    /**
     * Liefert MetricsCache + finalen scaleFactor für eine heightQ. Bei ≤4QL
     * (Note/Info) gibt der Renderer den System-Font-Cache zurück (z.B. Noto
     * Sans, wie Separator-Labels), sonst den Body-Cache — Wrap, PositionMap und
     * Render müssen dieselben Metriken sehen, sonst driften Zeilenumbrüche und
     * Cursor-Positionen.
     * Fallback (Test-Mocks ohne System-Font / _activeMetrics): Body-Cache.
     * @param {number} heightQ
     * @returns {{cache: object, scaleFactor: number}}
     */
    _metricsForHeightQ(heightQ) {
        const r = this.renderer
        if (typeof r._activeMetrics === 'function') {
            // _activeMetrics liefert den Basis-scaleFactor (4QL) → auf heightQ skalieren
            const m = r._activeMetrics(heightQ)
            return { cache: m.cache, scaleFactor: m.scaleFactor * heightQ / 4 }
        }
        return { cache: r.getGlyphMetricsCache(), scaleFactor: r.scaleFactorForHeightQ(heightQ) }
    }

    /**
     * Bricht Text im Proportional-Modus um.
     * Akkumuliert Pixel-Breiten statt Zeichen zu zählen.
     * Nutzt computeStringLayout() für Kerning-basierte Positionierung.
     */
    _breakTextProportional(paragraphId, text, availablePixelWidth, field, mapping, heightQ = 4, overrideFormatRuns = null) {
        // Metriken passend zur heightQ wählen: bei ≤4QL (Note/Info) der System-
        // Font-Cache (z.B. Noto Sans), sonst der Body-Cache. Sonst rechnet der
        // Wrap mit anderen Glyphen als der Render → Umbrüche driften.
        const { cache: metricsCache, scaleFactor } = this._metricsForHeightQ(heightQ)
        if (!metricsCache) {
            // Fallback auf Monospace wenn kein Cache verfügbar
            const textWidth = Math.round(availablePixelWidth / this.renderer.charWidth)
            return this._breakTextRaw(paragraphId, text, textWidth, field)
        }

        // Display-Text erzeugen (ohne Marker bei WYSIWYG, bei Links mit Label-Text)
        let displayText, displayToData
        if (mapping && mapping.displayLength !== text.length) {
            displayToData = mapping.displayToData
            if (mapping.displayText) {
                displayText = mapping.displayText
            } else {
                const chars = []
                for (let i = 0; i < mapping.displayLength; i++) {
                    chars.push(text[displayToData[i]])
                }
                displayText = chars.join('')
            }
        } else {
            displayText = text
            displayToData = null
        }

        const lines = []

        // RT-Prefix ermitteln (vor dem leeren-Text-Check)
        let rtPrefixText = ''
        if (field === 'text') {
            const rtPara = this.model.getParagraph(paragraphId)
            if (rtPara) rtPrefixText = this._getRichTextPrefix(rtPara).prefix
        }

        if (displayText.length === 0) {
            const line = new ScreenLine(paragraphId, 0, 0, field)
            line.isFirstLine = true
            line.isLastLine = true
            line.pixelWidth = 0
            // RT-Prefix auch bei leerem Text
            if (rtPrefixText) {
                line.rtPrefix = rtPrefixText
                if (metricsCache) {
                    // Leerzeichen-Indent in Grid-Breite, Marker proportional
                    const indentLevel = rtPrefixText.length - rtPrefixText.trimStart().length
                    const markerPart = rtPrefixText.trimStart().replace(/[0-9]/g, '0')
                    const markerLayout = computeStringLayout(markerPart, metricsCache, metricsCache.baseStep, null)
                    line.hangingIndent = indentLevel * this.renderer.charWidth + markerLayout.totalWidth * scaleFactor + this.renderer.charWidth * 0.8
                } else {
                    line.hangingIndent = rtPrefixText.length * this.renderer.charWidth
                }
            }
            lines.push(line)
            return lines
        }

        // Format-Runs für den kompletten Display-Text
        // fullParsed hat dieselben Display-Zeichen wie displayText (Marker übersprungen),
        // Bold/Italic-Erkennung ist unabhängig vom Base-Style
        // Override: bei Composite-Wrap (Delta-Review) reicht der Caller die fertigen
        // Composite-Format-Runs durch — sonst wuerde der Wrap auf den Para-Spans
        // arbeiten, die nicht zur Composite-Text-Position passen.
        let formatRuns
        if (overrideFormatRuns) {
            formatRuns = overrideFormatRuns
        } else {
            const paraForType = this.model.getParagraph(paragraphId)
            const paraType = paraForType ? this._getParaType(paraForType) : this._detectParagraphType(text)
            const fullParsed = this._getParsedRichText(
                field === 'text' ? this._textParseCache : this._noteParseCache,
                paragraphId, text, paraType, field
            )
            formatRuns = _enrichFormatRuns(parsedToFormatRuns(fullParsed))
            // Hinweis: Spellcheck-Anreicherung passiert NICHT hier (Wrap-
            // Pfad), sondern erst im _renderLineProportional, wo die
            // formatRuns pro Render-Frame frisch gebaut werden. Der Wrap
            // braucht das spellWarning-Flag nicht — es ist eine reine
            // Dekoration ohne Layout-Auswirkung.
        }

        // Layout bei Referenzgröße berechnen, dann mit scaleFactor skalieren
        const layout = computeStringLayout(displayText, metricsCache, metricsCache.baseStep, formatRuns)

        // Mono-aware Positionen: bei Inline-Code-Spans (Style.EDITOR_CODE oder
        // formatRun.mono) wird der Glyph-Loop mono gerendert (charWidth*heightQ/4
        // statt proportional). Der Word-Wrap muss das spiegeln, sonst stimmt
        // die Zeilenbreite nicht und Zeichen rutschen in Scrollbar/Border.
        const monoCharWidth = this.renderer.charWidth * heightQ / 4
        const cpLen = layout.positions.length
        const effLeft = buildMonoShiftedPositions(layout, formatRuns, STYLES, scaleFactor, monoCharWidth, cpLen, 0)

        // Hanging Indent: Monospace-basierte Breite (konsistent breit, unabhängig von Proportional-Metrik)
        let indent
        if (field === 'text') {
            const rtPara = this.model.getParagraph(paragraphId)
            indent = rtPara ? this._getRichTextPrefix(rtPara).hangingIndent : 0
        } else {
            indent = 0
        }
        const charWidth = this.renderer.charWidth
        // Einrückungsbreite: Proportional die Pixel-Breite eines normalisierten Prefix,
        // Mono die Grid-Breite. Ziffern werden durch '0' ersetzt, damit die Einrückung
        // unabhängig von der konkreten Nummer konsistent bleibt (1. vs 8. gleich breit).
        let hangingIndentPx
        if (indent > 0 && rtPrefixText && metricsCache) {
            // Einrückung: Leerzeichen-Indent in Grid-Breite (fest, wie Monospace),
            // Bullet/Nummer-Teil proportional gemessen
            const indentLevel = rtPrefixText.length - rtPrefixText.trimStart().length
            const markerPart = rtPrefixText.trimStart().replace(/[0-9]/g, '0')
            const markerLayout = computeStringLayout(markerPart, metricsCache, metricsCache.baseStep, null)
            // Proportional: etwas mehr Luft zwischen Marker und Text (halbe Grid-Breite)
            hangingIndentPx = indentLevel * charWidth + markerLayout.totalWidth * scaleFactor + charWidth * 0.8
        } else {
            hangingIndentPx = indent > 0 ? indent * charWidth : 0
        }
        // Proportionale Breite des Prefix (für Kompensation in erster Zeile)
        // RT: Prefix nicht im displayText → keine Kompensation (propPrefixPx = 0)
        const propPrefixPx = (!rtPrefixText && indent > 0 && indent < displayText.length)
            ? layout.positions[indent] * scaleFactor
            : 0

        // End-Marker-Reserve auf der letzten Zeile: Der '<'-Marker (markerWidthPx)
        // wird beim Rendern rechts vom Text platziert, mit einem baseStep-Abstand
        // (siehe renderer.renderProportionalLine, markerPx-Berechnung). Wenn die
        // Zeile die letzte Zeile eines Absatzes wird, muss diese Reserve im
        // Wordwrap-Budget eingeplant werden, damit Marker und letztes Zeichen
        // (z.B. ein breites Emoji) nicht in Scrollbar/Border überlaufen.
        // Marker-Höhe: Note/Info 4QL, Normal/Heading 6QL (Heading-Marker ist auf
        // NORMAL gedeckelt, siehe renderProportionalLine) → min(heightQ, NORMAL).
        const markerHQ = Math.min(heightQ, LINE_HEIGHT_Q.NORMAL)
        const markerReservePx = this.renderer.markerWidthPx(markerHQ) + metricsCache.baseStep * scaleFactor
        // Trailing \n erzeugt eine zusätzliche leere Zeile, diese trägt den
        // End-Marker. Die Zeile vor dem \n ist dann NICHT die letzte Zeile.
        const hasTrailingNewline = displayText.length > 0 && displayText[displayText.length - 1] === '\n'
        const lastContentIndex = hasTrailingNewline ? displayText.length - 2 : displayText.length - 1

        let lineStart = 0
        let isFirst = true

        while (lineStart < displayText.length) {
            // RT: Prefix ist NICHT im Text → erste Zeile auch reduzieren
            // MD: Prefix IST im Text → erste Zeile bekommt Kompensation (propPrefixPx)
            const lineWidthPx = (isFirst && !rtPrefixText)
                ? availablePixelWidth - hangingIndentPx + propPrefixPx
                : availablePixelWidth - hangingIndentPx

            // Erzwungener Umbruch bei \n
            const newlinePos = displayText.indexOf('\n', lineStart)

            // Skalierte Positionen aus effLeft (mono-aware): linke Pixel-Kante
            // pro Codepoint, effLeft[cpLen] = rechtes Zeilenende. Bei Inline-Code
            // sind die Positionen mono-shifted, damit der Wrap die echten
            // Glyph-Endpositionen sieht (ohne Scrollbar/Border-Kollision).
            const lineStartPos = lineStart > 0 ? effLeft[lineStart] : 0
            let endOffset = lineStart
            let lastSpaceOffset = -1
            let hitNewline = false

            while (endOffset < displayText.length) {
                if (endOffset === newlinePos) {
                    hitNewline = true
                    break
                }
                // Rechte Kante des Zeichens (statt linker Kante des nächsten):
                // sonst ragen breite Glyphen (Emojis, Ascender) über das Budget hinaus
                const rightEdge = (endOffset + 1 < displayText.length
                    ? effLeft[endOffset + 1]
                    : effLeft[cpLen]) - lineStartPos
                // Wenn dies das letzte Textzeichen ist (keine weitere Zeile folgt),
                // muss der '<'-Marker zusätzlich ins Budget passen
                const isTailChar = endOffset === lastContentIndex
                const budget = isTailChar ? lineWidthPx - markerReservePx : lineWidthPx
                if (rightEdge > budget && endOffset > lineStart) break

                if (displayText[endOffset] === ' ') {
                    lastSpaceOffset = endOffset + 1
                }
                endOffset++
            }

            // Wortumbruch am letzten Leerzeichen (nicht bei \n-Umbruch)
            if (!hitNewline && endOffset < displayText.length && lastSpaceOffset > lineStart) {
                endOffset = lastSpaceOffset
            }

            // Zeichen nach Zeilenende überspringen
            let skippedChars = 0
            if (endOffset < displayText.length) {
                if (displayText[endOffset] === '\n') {
                    skippedChars = 1
                } else {
                    let skip = endOffset
                    while (skip < displayText.length && displayText[skip] === ' ') {
                        skip++
                    }
                    if (skip < displayText.length) {
                        skippedChars = skip - endOffset
                    } else {
                        endOffset = displayText.length
                    }
                }
            }

            // Pixel-Breite dieser Zeile berechnen (mono-aware via effLeft)
            const lineEndPos = endOffset > 0 && endOffset <= displayText.length
                ? (endOffset < displayText.length
                    ? effLeft[endOffset] - lineStartPos
                    : effLeft[cpLen] - lineStartPos)
                : 0

            // Display-Offsets → Data-Offsets konvertieren
            const dataStart = displayToData
                ? (lineStart < displayToData.length ? displayToData[lineStart] : text.length)
                : lineStart
            const dataEnd = displayToData
                ? (endOffset < displayToData.length ? displayToData[endOffset] : text.length)
                : endOffset

            // Erste Zeile beginnt immer bei Data-Offset 0
            const line = new ScreenLine(
                paragraphId,
                isFirst ? 0 : dataStart,
                dataEnd,
                field
            )
            line.skippedSpaces = skippedChars
            line.hangingIndent = hangingIndentPx > 0 ? hangingIndentPx : 0
            line.pixelWidth = lineEndPos
            lines.push(line)

            lineStart = endOffset + skippedChars
            isFirst = false
        }

        // Trailing \n: leere Zeile nach dem Soft Linebreak erzeugen
        if (text.length > 0 && text[text.length - 1] === '\n') {
            const line = new ScreenLine(paragraphId, text.length, text.length, field)
            line.hangingIndent = hangingIndentPx > 0 ? hangingIndentPx : 0
            line.pixelWidth = 0
            lines.push(line)
        }

        if (lines.length > 0) {
            lines[0].isFirstLine = true
            lines[lines.length - 1].isLastLine = true
            if (rtPrefixText) {
                // RT-Modus: Prefix als rtPrefix speichern (nicht im Text)
                lines[0].rtPrefix = rtPrefixText
            } else if (indent > 0) {
                // MD-Modus: Prefix IST im Text → listPrefixLen
                lines[0].listPrefixLen = indent
            }
        }

        return lines
    }

    /**
     * Prüft, ob der unmittelbar folgende Paragraph ein Normal-Para ist — der
     * einzige Co-Location-Partner für ein Outline-Item (Spec §3.2). Wird genutzt,
     * um zu entscheiden, ob ein collapsed Outline-Item puffern darf (Buddy folgt)
     * oder sofort solo gerendert werden muss (Item/Separator/Doc-Ende folgt).
     * @param {Paragraph} para - Outline-Item
     * @returns {boolean}
     */
    _nextParagraphIsNormal(para) {
        const idx = this.model.getParagraphIndex(para.id)
        if (idx < 0) return false
        const next = this.model.paragraphs[idx + 1]
        return !!next && next.type !== 'separator' && !next.isOutlineItem()
    }

    /**
     * Pusht die ScreenLines für ein Solo-Outline-Item (Spec §3.4):
     *  - Eine Phantom-Main-Zeile mit Marker `>` rechts (vom Renderer gezeichnet).
     *  - Note-Zeilen wie heute.
     *  - Info-Padding (Outline-Items haben kein info).
     * Wird benutzt, wenn ein Outline-Item keinen Folge-Normal-Para zum
     * Co-Locaten hat oder selbst activeCollapsed ist (Branch oben).
     *
     * @param {Paragraph} para - Outline-Item
     */
    _pushSoloOutlineLines(para) {
        const startLineIdx = this.lines.length
        this._paragraphLineIndex.set(para.id, startLineIdx)

        const { textLines, noteTextLines, infoTextLines } = this._makeSoloOutlineLines(para)

        this.lines.push(...textLines)
        this.noteLines.push(...noteTextLines)
        this.infoLines.push(...infoTextLines)
    }

    /**
     * Baut die synchronisierten ScreenLine-Tripel für ein Solo-Outline-Item:
     * Phantom-Main-Zeile (`>` rechts, vom Renderer gezeichnet) + Note-Zeilen +
     * Info-Padding. Gemeinsame Basis für _pushSoloOutlineLines (Full-Rebuild)
     * und updateParagraph (inkrementell) — beide Pfade MÜSSEN dieselbe
     * Zeilen-Shape liefern. Sonst rendert der inkrementelle Pfad den
     * (kanonisch leeren) text-Slot des Items als normale Text-Zeile ohne
     * Phantom-Marker und macht illegal befüllten OI-Text sichtbar (Lesson 103).
     *
     * @param {Paragraph} para - Outline-Item
     * @returns {{textLines: ScreenLine[], noteTextLines: ScreenLine[], infoTextLines: ScreenLine[]}}
     */
    _makeSoloOutlineLines(para) {
        // Phantom-Text-Zeile: ScreenLine ohne Text-Inhalt, markiert für Renderer.
        const phantomLine = new ScreenLine(para.id, 0, 0, 'text')
        phantomLine.isFirstLine = true
        phantomLine.isLastLine = true
        phantomLine.isPhantomOutline = true  // Renderer zeichnet darauf `>` rechts
        const textLines = [phantomLine]

        const noteTextLines = this._breakNote(para)
        const infoTextLines = [this._makePaddingLine(para.id, 'info')]

        if (para.activeCollapsed && textLines.length > 0) {
            // Im Solo-Fall existiert sowieso nur 1 Phantom-Zeile, aber wir
            // markieren sie konsistent als collapsed-Host (Ellipsis erscheint,
            // wenn der zugehörige Range mehrzeiligen Text enthält — was hier
            // unsichtbar ist, weil collapse ihn versteckt).
            textLines[0].isCollapsedActiveHost = true
            textLines[0].collapseHasEllipsis = true
            // Phantom-Zeile trägt keinen eigenen Main-Text (nur den `>`-Marker) →
            // eine etwaige Ellipsis würde angehängt, nicht ersetzt.
            textLines[0].collapseEllipsisAppend = true
        }

        const maxCount = Math.max(textLines.length, noteTextLines.length, infoTextLines.length)
        while (textLines.length < maxCount) {
            const padLine = this._makePaddingLine(para.id, 'text')
            padLine.isPhantomOutline = true
            textLines.push(padLine)
        }
        while (noteTextLines.length < maxCount) {
            noteTextLines.push(this._makePaddingLine(para.id, 'note'))
        }
        while (infoTextLines.length < maxCount) {
            infoTextLines.push(this._makePaddingLine(para.id, 'info'))
        }

        return { textLines, noteTextLines, infoTextLines }
    }

    /**
     * Bricht einen Paragraph-Text in Screen-Zeilen um.
     * @param {Paragraph} para
     * @returns {ScreenLine[]}
     */
    _breakParagraph(para) {
        // Separator: eine einzelne Zeile
        if (para.type === 'separator') {
            const line = new ScreenLine(para.id, 0, para.text.length, 'text')
            line.isFirstLine = true
            line.isLastLine = true
            line.isSeparator = true
            return [line]
        }

        // Delta-Review aktiv und Paragraph hat einen Rewrite-Vorschlag?
        // Dann den Composite (Original + Vorschlag inline-gemerged) umbrechen
        // statt para.text. Die ScreenLine bekommt ein _reviewComposite-Flag,
        // damit _renderLine den Custom-Renderpfad waehlt.
        const composite = this._getReviewComposite(para.id)
        if (composite) {
            // Main nicht sichtbar: minimale Zeile
            if (this.mainPaneWidth <= 0) {
                const line = new ScreenLine(para.id, 0, composite.composite.length, 'text')
                line.isFirstLine = true
                line.isLastLine = true
                line._reviewComposite = composite
                return [line]
            }
            // Plain-Text-Wrap: kein Markdown-Mapping, weil der Composite
            // beide Versionen mischt. heightQ = NORMAL. Format-Runs vom
            // proposed-View durchreichen, damit der Wrap die Mono-Geometrie
            // sieht (Inline-Code: monoCharWidth statt proportional).
            const heightQ = this._getParaHeightQ(para)
            const wrapRuns = composite.formatRunsProposed || composite.formatRunsOriginal || null
            const lines = this._breakText(para.id, composite.composite, this.textWidth, 'text', null, heightQ, wrapRuns)
            for (const ln of lines) ln._reviewComposite = composite
            return lines
        }

        // Main nicht sichtbar: eine minimale Zeile (kein Umbruch bei width=0)
        if (this.mainPaneWidth <= 0) {
            const line = new ScreenLine(para.id, 0, para.text.length, 'text')
            line.isFirstLine = true
            line.isLastLine = true
            return [line]
        }
        const mapping = this.getDisplayMapping(para.id, 'text')
        // heightQ für Word-Wrap: bestimmt den scaleFactor für Breitenberechnung
        const heightQ = this._getParaHeightQ(para)
        return this._breakText(para.id, para.text, this.textWidth, 'text', mapping, heightQ)
    }

    /**
     * Bestimmt die Zeilenhöhe in Quarterlines für einen Absatz.
     * @param {object} para - Paragraph-Objekt
     * @returns {number} heightQ (4 = Basis/Mono, 6+ = Proportional)
     */
    _getParaHeightQ(para) {
        if (!this.renderer || !this.renderer.isProportional()) return 4
        // Review-WYSIWYG: bei aktivem Rewrite-Vorschlag mit Block-Aenderung
        // bestimmt der activeView, welcher Heading-Level die Zeilenhoehe
        // diktiert (Toggle wechselt zwischen alter und neuer Schriftgroesse).
        para = this._effectiveParaForRender(para)
        if (para.type === 'separator') return LINE_HEIGHT_Q.SEPARATOR
        if (para.codeBlock) return LINE_HEIGHT_Q.CODE_PARA
        if (para.headingLevel > 0) return headingHeightQ(para.headingLevel)
        return LINE_HEIGHT_Q.NORMAL
    }

    /**
     * Gibt den Abschnitts-Indent eines Absatzes zurück (2 * Separator-Tiefe).
     * @param {number} paragraphId
     * @returns {number}
     */
    getSectionIndent(paragraphId) {
        if (!this._sectionIndents) return 0
        return this._sectionIndents.get(paragraphId) || 0
    }

    /**
     * Gibt die hierarchische Nummer eines Separators zurück.
     * @param {number} paragraphId
     * @returns {string} z.B. "1.2.3." oder ''
     */
    getSeparatorNumber(paragraphId) {
        if (!this._separatorMap) return ''
        const entry = this._separatorMap.get(paragraphId)
        return entry ? entry.number : ''
    }

    /**
     * Findet den Separator oberhalb des aktuellen Viewports.
     * @returns {{ number: string, paragraphId: number, firstContentLineIndex: number }|null}
     *   null wenn kein Separator oberhalb (= Dokument-Anfang sichtbar)
     */
    getSectionAboveViewport() {
        if (this.lines.length === 0) return null

        // Im Proportional-Mode mit Sub-Pixel-Scroll kann viewportStart noch auf
        // einer Zeile stehen, die visuell schon vollständig oberhalb des Viewports
        // ist — die Forward-Normalization im scrollByPixels-Pfad triggert erst bei
        // _viewportPixelOffset >= lineH + nextSpacingBefore, sodass im Spacing-
        // Bereich (Sep visuell raus, aber spacingBefore der naechsten Zeile noch
        // nicht ueberschritten) viewportStart auf der Sep-Zeile haengen bleibt.
        // Wir behandeln viewportStart selbst als "above", sobald _viewportPixelOffset
        // >= heightQ dieser Zeile ist (Zeile sichtbar nicht mehr).
        let startIdx = this.viewportStart - 1
        if (this._useQuarterSpacing() && this.renderer && this.lines[this.viewportStart]) {
            const quarterH = this.renderer.charHeight / 4
            // Echte Grid-Distanz zur nächsten Zeile (Per-Pane-Slot-Modell): die
            // heightQ-basierte Rekonstruktion stimmt seit den Slot-Grids nicht mehr.
            const distToNext = ((this._lineQuarterY[this.viewportStart + 1] || this._lineQuarterY[this.viewportStart] || 0)
                - (this._lineQuarterY[this.viewportStart] || 0)) * quarterH
            if (distToNext > 0 && this._viewportPixelOffset >= distToNext) {
                startIdx = this.viewportStart
            }
        }
        if (startIdx < 0) return null

        // Von der ersten sichtbaren Zeile rückwärts zum nächsten Separator suchen
        startIdx = Math.min(startIdx, this.lines.length - 1)
        for (let i = startIdx; i >= 0; i--) {
            const line = this.lines[i]
            if (line.isSeparator) {
                const sepEntry = this._separatorMap.get(line.paragraphId)
                const number = sepEntry ? sepEntry.number : ''
                const para = this.model.getParagraph(line.paragraphId)
                const title = para ? para.text : ''
                // Erste Content-Zeile nach dem Separator
                const firstContent = Math.min(i + 1, this.lines.length - 1)
                return { number, title, paragraphId: line.paragraphId, firstContentLineIndex: firstContent }
            }
        }
        return null
    }

    /**
     * Berechnet die Note-Start-Spalte für einen Paragraph.
     * Jeder Paragraph bekommt seine Note so weit links wie möglich,
     * abhängig von seiner eigenen Gliederungstiefe.
     * @param {number} paragraphId
     * @returns {number} Spalte wo der > Marker beginnt
     */
    getNoteStartCol(paragraphId) {
        const para = this.model.getParagraph(paragraphId)
        if (!para) return 1
        const sectionIndent = (this._sectionIndents && this._sectionIndents.get(paragraphId)) || 0
        // v4: Outline-Items nutzen die eigene Tiefe; Normal-Paras erben die
        // Tiefe vom Owner-Outline-Item (sonst überlappt der Note-Marker mit
        // dessen │-Linie). Kein Owner ⇒ 0.
        let effectiveDepth
        if (para.isOutlineItem()) {
            effectiveDepth = para.outlineDepth
        } else {
            const owner = this._ownerDepths ? this._ownerDepths.get(paragraphId) : -1
            effectiveDepth = (owner === undefined || owner < 0) ? 0 : owner
        }
        return sectionIndent + (effectiveDepth + 1) * 2 + 1
    }

    /**
     * Berechnet die Note-Textbreite für einen Paragraph.
     * @param {number} paragraphId
     * @returns {number} Verfügbare Breite für Note-Text (0 = Outline nicht sichtbar)
     */
    getNoteTextWidth(paragraphId) {
        if (this.outlinePaneWidth <= 0) return 0
        const noteStartCol = this.getNoteStartCol(paragraphId)
        // -1 für > Marker, -1 für rechten Rand
        return Math.max(0, this.outlinePaneWidth - noteStartCol - 2)
    }

    /**
     * Bricht einen Paragraph-Note in Screen-Zeilen um.
     * Nutzt per-Paragraph noteTextWidth basierend auf Gliederungstiefe.
     * @param {Paragraph} para
     * @returns {ScreenLine[]}
     */
    _breakNote(para) {
        // Separator: eine einzelne Zeile
        if (para.type === 'separator') {
            const line = new ScreenLine(para.id, 0, 0, 'note')
            line.isFirstLine = true
            line.isLastLine = true
            line.isSeparator = true
            return [line]
        }
        const noteTextWidth = this.getNoteTextWidth(para.id)
        if (noteTextWidth <= 0) {
            // Kein Outline sichtbar oder zu wenig Platz: eine minimale Zeile
            const line = new ScreenLine(para.id, 0, 0, 'note')
            line.isFirstLine = true
            line.isLastLine = true
            return [line]
        }
        const mapping = this.getDisplayMapping(para.id, 'note')
        // Notes in SECONDARY-Größe umbrechen (4QL + System-Font). Wrap muss
        // dieselben Metriken nutzen wie der Render, sonst driften Zeilenumbrüche.
        const heightQ = this._useQuarterSpacing() ? LINE_HEIGHT_Q.SECONDARY : 4
        return this._breakText(para.id, para.note, noteTextWidth, 'note', mapping, heightQ)
    }

    /**
     * Bricht Info-Text in Zeilen um (für Context-Pane).
     * @param {Paragraph} para
     * @returns {ScreenLine[]}
     */
    _breakInfo(para) {
        // Separator: eine einzelne Zeile
        if (para.type === 'separator') {
            const line = new ScreenLine(para.id, 0, 0, 'info')
            line.isFirstLine = true
            line.isLastLine = true
            line.isSeparator = true
            return [line]
        }
        const infoTextWidth = this.getInfoTextWidth()
        if (infoTextWidth <= 0) {
            const line = new ScreenLine(para.id, 0, 0, 'info')
            line.isFirstLine = true
            line.isLastLine = true
            return [line]
        }
        const mapping = this.getDisplayMapping(para.id, 'info')
        // Info in SECONDARY-Größe umbrechen (4QL + System-Font). Wrap muss
        // dieselben Metriken nutzen wie der Render, sonst driften Zeilenumbrüche.
        const heightQ = this._useQuarterSpacing() ? LINE_HEIGHT_Q.SECONDARY : 4
        return this._breakText(para.id, para.info, infoTextWidth, 'info', mapping, heightQ)
    }

    /**
     * Gibt die Textbreite für Info im Context-Pane zurück.
     * @returns {number}
     */
    getInfoTextWidth() {
        if (this.contextPaneWidth <= 0) return 0
        return Math.max(0, this.contextPaneWidth - 3)
    }

    /**
     * Erzeugt eine Padding-Zeile (Leerzeile für Zeilen-Synchronisation).
     * @param {number} paragraphId
     * @param {string} field - 'text', 'note' oder 'info'
     * @returns {ScreenLine}
     */
    _makePaddingLine(paragraphId, field) {
        return new ScreenLine(paragraphId, 0, 0, field, true)
    }

    /**
     * Aktualisiert die Zeilen für einen einzelnen Paragraph (inkrementell).
     * @param {number} paragraphId
     * @returns {{startLineIndex: number, newLineCount: number, lineDelta: number}|null}
     *          null wenn Fallback auf _rebuildLines() nötig war
     */
    updateParagraph(paragraphId) {
        const para = this.model.getParagraph(paragraphId)
        if (!para) {
            this._rebuildLines()
            return null
        }

        const startLineIndex = this._paragraphLineIndex.get(paragraphId)
        if (startLineIndex === undefined) {
            this._rebuildLines()
            return null
        }

        // Co-Location-Detection (v4 Spec §3.2): Ein Outline-Item kann sich
        // eine Y-Zeile mit dem direkt folgenden Normal-Para teilen — dann ist
        // `lines[i].paragraphId` der Normal-Para, `noteLines[i].paragraphId`
        // das Outline-Item. Inkrementelles Update kann hier nicht zuverlässig
        // arbeiten (Splice mit oldLineCount=0 würde Phantom-Zeilen einfügen,
        // statt zu ersetzen). Sicherer Fallback: Full-Rebuild.
        if (para.isOutlineItem()) {
            // Solo-Outline-Item hat lines[startLineIndex].paragraphId === paragraphId
            // (isPhantomOutline=true). Co-Located dagegen NICHT.
            const ownsTextLine = this.lines[startLineIndex] &&
                this.lines[startLineIndex].paragraphId === paragraphId
            if (!ownsTextLine) {
                this._rebuildLines()
                return null
            }
        } else {
            // Normal-Para mit Co-Location-Buddy: noteLines[start].paragraphId
            // gehört dem Outline-Item, nicht dem Normal-Para. Inkrementelles
            // Update würde die Buddy-Note überschreiben.
            const noteLine = this.noteLines[startLineIndex]
            if (noteLine && noteLine.paragraphId !== paragraphId) {
                this._rebuildLines()
                return null
            }
        }

        // Alte Zeilen dieses Paragraphs finden
        let endLineIndex = startLineIndex
        while (endLineIndex < this.lines.length &&
               this.lines[endLineIndex].paragraphId === paragraphId) {
            endLineIndex++
        }
        const oldLineCount = endLineIndex - startLineIndex

        // Neue Zeilen berechnen (text + note + info mit Sync)
        let textLines, noteTextLines, infoTextLines
        if (para.isOutlineItem()) {
            // Solo-Outline-Item (ownsTextLine, oben geprüft): dieselbe
            // Phantom-Zeilen-Shape wie im Full-Rebuild. _breakParagraph
            // würde den (kanonisch leeren) text-Slot als normale Zeile
            // ohne `>`-Marker rendern (Lesson 103).
            ({ textLines, noteTextLines, infoTextLines } = this._makeSoloOutlineLines(para))
        } else {
            textLines = this._breakParagraph(para)
            noteTextLines = this._breakNote(para)
            infoTextLines = this._breakInfo(para)

            // Zeilen-Synchronisation: Padding auffüllen
            const maxCount = Math.max(textLines.length, noteTextLines.length, infoTextLines.length)
            while (textLines.length < maxCount) {
                textLines.push(this._makePaddingLine(para.id, 'text'))
            }
            while (noteTextLines.length < maxCount) {
                noteTextLines.push(this._makePaddingLine(para.id, 'note'))
            }
            while (infoTextLines.length < maxCount) {
                infoTextLines.push(this._makePaddingLine(para.id, 'info'))
            }
        }

        const newLineCount = textLines.length
        const lineDelta = newLineCount - oldLineCount

        // PositionMap-Cache für geänderte Zeilen IMMER invalidieren
        // (Text/Formate können sich ändern ohne dass Zeilen-Offsets sich ändern,
        // z.B. bei Format-Makros wo insertText + Marker-Entfernung gleiche Länge ergeben)
        for (let i = startLineIndex; i < startLineIndex + oldLineCount; i++) {
            this._textPositionMaps.delete(i)
            this._notePositionMaps.delete(i)
            this._infoPositionMaps.delete(i)
        }
        // Bei Zeilenverschiebung: kompletten Cache leeren (Indizes ändern sich)
        if (oldLineCount !== textLines.length) {
            this._textPositionMaps.clear()
            this._notePositionMaps.clear()
            this._infoPositionMaps.clear()
        }

        // Zeilen ersetzen (text + note + info)
        this.lines.splice(startLineIndex, oldLineCount, ...textLines)
        this.noteLines.splice(startLineIndex, oldLineCount, ...noteTextLines)
        this.infoLines.splice(startLineIndex, oldLineCount, ...infoTextLines)

        // isBackground-Flag setzen (wird sonst nur in _rebuildLines gesetzt)
        const bg = (startLineIndex > 0 && this.lines[startLineIndex - 1].isBackground) ||
            (para.type === 'separator' && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH)
        if (bg) {
            for (let i = 0; i < newLineCount; i++) {
                this.lines[startLineIndex + i].isBackground = true
                this.noteLines[startLineIndex + i].isBackground = true
                this.infoLines[startLineIndex + i].isBackground = true
            }
        }

        // Paragraph-Line-Index-Map aktualisieren wenn sich Zeilenanzahl geändert hat
        if (lineDelta !== 0) {
            // Alle nachfolgenden Paragraphs verschieben
            for (const [id, idx] of this._paragraphLineIndex) {
                if (idx > startLineIndex) {
                    this._paragraphLineIndex.set(id, idx + lineDelta)
                }
            }
            // Outline-Spalten neu berechnen (Zeilen-Indizes haben sich verschoben)
            // v4 Co-Location: noteLines[i].paragraphId zeigt bei Co-Located-Outline-
// Items auf das Outline-Item (während lines[i] zum Normal-Para gehört).
// buildOutlineColumns muss das Outline-Item finden, damit (-) gezeichnet wird.
this._outlineColumns = buildOutlineColumns(this.model.paragraphs, this.noteLines)
            // Partial-Render kann Zeilenverschiebungen nicht darstellen → Full-Render erzwingen
            this._hadLineDelta = true
        }

        // Quarter-Line Spacing aktualisieren — im Tipp-Pfad (lineDelta 0)
        // inkrementell: nur dieser Slot + ggf. Suffix-Shift (V3.2, System-
        // Review 2026-06-10). Der volle Lauf alloziert 5 Arrays ueber ALLE
        // Zeilen — gemessen ~3,6 ms pro Tastendruck bei 34k Zeilen.
        if (!this._updateSpacingIncremental(paragraphId, startLineIndex, newLineCount, lineDelta)) {
            this._computeSpacing()
        }

        // Viewport validieren
        this._clampViewport()

        this.isDirty = true
        return { startLineIndex, newLineCount, lineDelta }
    }

    // ===== Position Mapping =====

    /**
     * Konvertiert Screen-Koordinaten zu Daten-Position.
     * @param {number} screenX - Spalte auf dem Screen
     * @param {number} screenY - Zeile auf dem Screen
     * @returns {{paragraphId: number, offset: number}|null}
     */
    screenToData(screenX, screenY, pixelX = null) {
        // Proportional mit Quarter-Spacing: pixelbasierte Y-Zuordnung
        let lineIndex
        if (this._useQuarterSpacing() && this.renderer) {
            const screenPixelY = screenY * this.renderer.charHeight
            lineIndex = this._lineAtScreenPixelY(screenPixelY)
        } else {
            lineIndex = this.viewportStart + Math.floor(screenY)
        }
        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            return null
        }

        const line = this.lines[lineIndex]
        if (line.isPadding) {
            return { paragraphId: line.paragraphId, offset: 0 }
        }

        // Proportional: Pixel-basierte Positionierung
        const posMap = this._getPositionMap(lineIndex)
        if (posMap && pixelX !== null) {
            return this._screenToDataProportional(line, posMap, pixelX)
        }

        // Monospace: Zeichenbasierte Positionierung
        const prefixLen = line.listPrefixLen || 0
        const hangingOffset = prefixLen > 0 ? prefixLen : (line.hangingIndent || 0)
        let textX = screenX - this.markerWidth - hangingOffset

        const para = this.model.getParagraph(line.paragraphId)
        if (!para) return { paragraphId: line.paragraphId, offset: line.startOffset }

        const paraType = this._getParaType(para)
        const fullParsed = this._getParsedRichText(this._textParseCache, para.id, para.text, paraType)
        let lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Listen-Prefix: Prefix-Zeichen aus Slice entfernen, Klick im Indent → Content-Start
        if (prefixLen > 0) {
            lineSlice = lineSlice.slice(prefixLen)
        }

        // Display-Breite kumulativ berechnen (Emoji = 2 Spalten)
        let totalCols = 0
        for (let k = 0; k < lineSlice.length; k++) {
            totalCols += charDisplayWidth(lineSlice[k].char)
        }
        let maxDisplayX = totalCols
        const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
        if (wrapBoundary && lineSlice.length > 0) {
            maxDisplayX -= charDisplayWidth(lineSlice[lineSlice.length - 1].char)
        }
        textX = Math.max(0, Math.min(textX, maxDisplayX))

        // Kumulativ zählen um geklickte Spalte → Daten-Offset zu finden
        let col = 0
        for (let k = 0; k < lineSlice.length; k++) {
            const sc = lineSlice[k]
            const w = charDisplayWidth(sc.char)
            if (col + w > textX) {
                const result = { paragraphId: line.paragraphId, offset: sc.dataOffset }
                if (sc.isLink) { result.isLink = true; result.linkTargetId = sc.linkTargetId }
                if (sc.isUrl) { result.isUrl = true; result.url = sc.url }
                return result
            }
            col += w
        }

        return { paragraphId: line.paragraphId, offset: line.endOffset }
    }

    /**
     * Proportionaler screenToData: Pixel-basierte Position zu Data-Offset.
     */
    /**
     * Konvertiert UTF-16-Index (aus LinePositionMap) zu Data-Offset (im Paragraph-Text).
     * Zentrale Methode für Cursor-Klick UND Selection-Berechnung.
     * @param {Array} lineSlice - Parsed StyledChars der Zeile (ohne Prefix)
     * @param {number} utf16Index - UTF-16-Index im lineText
     * @param {number} fallbackOffset - Fallback-Offset (z.B. line.endOffset)
     * @returns {number} Data-Offset im Paragraph-Text
     */
    _utf16ToDataOffset(lineSlice, utf16Index, fallbackOffset) {
        let utf16Pos = 0
        for (let k = 0; k < lineSlice.length; k++) {
            if (utf16Pos >= utf16Index) {
                return lineSlice[k].dataOffset
            }
            utf16Pos += lineSlice[k].char.length
        }
        return fallbackOffset
    }

    /**
     * Setzt Link-Info (isLink/linkTargetId bzw. isUrl/url) auf result, falls das
     * Zeichen DIREKT UNTER der Pixel-Position ein Link ist. Nutzt getCharIndexUnderX statt der
     * cursor-gerundeten getCharIndexAtX-Position — sonst läge die Trefferzone
     * eine halbe Zeichenbreite links neben dem Label (bei schmalen Labels nach
     * breitem Vorzeichen fast vollständig daneben). Der Cursor-Offset selbst
     * bleibt unberührt (der nutzt weiterhin die gerundete Position).
     * @param {object} result - Positions-Objekt, wird bei Treffer mutiert
     * @param {Array} lineSlice - geparste StyledChars der Zeile
     * @param {LinePositionMap} posMap
     * @param {number} pixelX - Pixel-Position relativ zum Zeilen-Textstart
     */
    _applyLinkUnderPixelX(result, lineSlice, posMap, pixelX) {
        const hitU16 = posMap.getCharIndexUnderX(Math.max(0, pixelX))
        if (hitU16 < 0) return
        let utf16Pos = 0
        for (let k = 0; k < lineSlice.length; k++) {
            if (utf16Pos >= hitU16) {
                if (lineSlice[k].isLink) {
                    result.isLink = true
                    result.linkTargetId = lineSlice[k].linkTargetId
                }
                if (lineSlice[k].isUrl) {
                    result.isUrl = true
                    result.url = lineSlice[k].url
                }
                return
            }
            utf16Pos += lineSlice[k].char.length
        }
    }

    _screenToDataProportional(line, posMap, pixelX) {
        const r = this.renderer
        const markerPx = r.isProportional() ? r.markerWidthPx() : this.markerWidth * r.charWidth
        const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0

        // pixelX ist relativ zum Pane-Start (lokale Koordinate * charWidth)
        const textPixelX = pixelX - markerPx - hangingPx
        const displayIndex = posMap.getCharIndexAtX(Math.max(0, textPixelX))

        // Display-Index → Data-Offset via parsed Markdown
        const para = this.model.getParagraph(line.paragraphId)
        if (!para) return { paragraphId: line.paragraphId, offset: line.startOffset }

        const paraType = this._getParaType(para)
        const fullParsed = this._getParsedRichText(this._textParseCache, para.id, para.text, paraType)
        let lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Listen-Prefix: Prefix-Zeichen aus Slice entfernen
        const prefixLen = line.listPrefixLen || 0
        if (prefixLen > 0) {
            lineSlice = lineSlice.slice(prefixLen)
        }

        // Wrap-Boundary: maximaler UTF-16-Index
        const lineText = parsedCharsToString(lineSlice)
        let maxUtf16 = lineText.length
        const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
        if (wrapBoundary && lineSlice.length > 0) {
            // Letztes Zeichen abziehen (gehört zur nächsten Zeile)
            maxUtf16 -= lineSlice[lineSlice.length - 1].char.length
        }

        const clampedIndex = Math.min(displayIndex, maxUtf16)
        const offset = this._utf16ToDataOffset(lineSlice, clampedIndex, line.endOffset)
        const result = { paragraphId: line.paragraphId, offset }

        // Link-Info: Zeichen UNTER der Maus (nicht die gerundete Cursor-Position)
        this._applyLinkUnderPixelX(result, lineSlice, posMap, textPixelX)

        return result
    }

    /**
     * Konvertiert lineIndex zu fraktionaler Screen-Y-Position.
     * Im Proportional-Mode mit Quarter-Spacing: berücksichtigt Spacing und Pixel-Offset.
     * @param {number} lineIndex
     * @returns {number} fraktionale Y-Position
     */
    _lineToScreenY(lineIndex, field = 'text') {
        if (this._useQuarterSpacing() && this.renderer) {
            return this._lineScreenPixelY(lineIndex, field) / this.renderer.charHeight
        }
        return lineIndex - this.viewportStart
    }

    /**
     * Konvertiert eine Screen-Y-Koordinate (Zeichenraster) in einen Zeilen-Index.
     * Berücksichtigt Quarter-Spacing im Proportional-Mode.
     * @param {number} screenY - Y-Position im Zeichenraster (relativ zum Pane)
     * @returns {number} Zeilen-Index
     */
    screenYToLineIndex(screenY, field = 'text') {
        if (this._useQuarterSpacing() && this.renderer) {
            return this._lineAtScreenPixelY(screenY * this.renderer.charHeight, field)
        }
        return this.viewportStart + Math.floor(screenY)
    }

    /**
     * Setzt den Hover-State für das Lock-Control auf einer Separator-Zeile im
     * Main-Pane.
     * @param {string|null} paragraphId
     * @returns {boolean} true, wenn sich der Hover-State geändert hat
     */
    setHoveredMainSepLock(paragraphId) {
        if (this._hoveredMainSepLock === paragraphId) return false
        this._hoveredMainSepLock = paragraphId
        this.isDirty = true
        return true
    }

    /**
     * Prüft, ob localX/localY (Main-Pane-lokal) auf das Lock-Control eines
     * Separators fällt. Spiegelt den Klick-Hit-Test in EditorInput.handleMouseDown.
     * @returns {string|null} paragraphId oder null
     */
    getMainSeparatorLockAtPosition(localX, localY, pixelX) {
        if (this.width < 8) return null
        const controlX = this.width - 4
        if (controlX <= 0 || localX < controlX || localX > controlX + 2) return null
        const pos = this.screenToData(localX, localY, pixelX)
        if (!pos || !this.model.isSeparator(pos.paragraphId)) return null
        const para = this.model.getParagraph(pos.paragraphId)
        // Background-Separator hat kein Lock-Control
        if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return null
        return pos.paragraphId
    }

    dataToScreen(paragraphId, offset) {
        // O(1)-Sprung zur ersten Zeile dieses Paragraphs
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return null

        const para = this.model.getParagraph(paragraphId)
        if (!para) return null

        // Separator-Spezialbehandlung: Cursor-Position aus SingleLineInput-State
        if (para.type === 'separator') {
            const line = this.lines[startIdx]
            if (!line) return null
            const screenY = this._lineToScreenY(startIdx)
            if (screenY === null) return null
            const sepState = this._separatorInputAccessor ? this._separatorInputAccessor() : null
            if (sepState) {
                // Label sitzt direkt an Spalte labelCol=2 (zwischen den Markern
                // > <, ohne Spacer). Bei leerem Label faellt der Cursor auf die
                // <-Marke an Spalte labelCol — analog zum Render-Layout.
                const labelCol = 2
                const displayCursorPos = sepState.cursorPos - sepState.scrollOffset
                const cursorX = labelCol + displayCursorPos
                const result = { x: cursorX, y: screenY, heightQ: line.heightQ || 4 }

                // Proportional: pixelX berechnen für I-Beam-Cursor
                if (this.renderer && this.renderer.isProportional()) {
                    const charWidth = this.renderer.charWidth
                    const titleStartPx = labelCol * charWidth
                    const displayTitle = para.text.slice(sepState.scrollOffset)
                    if (displayTitle.length > 0 && displayCursorPos > 0) {
                        const formatRuns = [{ start: 0, end: displayTitle.length, bold: true, italic: false }]
                        const { cache: activeCache, scaleFactor: activeSF } = this.renderer._activeMetrics()
                        const layout = computeStringLayout(
                            displayTitle, activeCache,
                            activeCache.baseStep, formatRuns
                        )
                        // layout.positions deckt alle gueltigen Cursor-Indices ab;
                        // bei Out-of-Bounds Fallback auf totalWidth.
                        const posInTitle = displayCursorPos < layout.positions.length
                            ? layout.positions[displayCursorPos] * activeSF
                            : layout.totalWidth * activeSF
                        result.pixelX = titleStartPx + posInTitle
                    } else {
                        result.pixelX = titleStartPx
                    }
                }

                return result
            }
            // Fallback: Cursor am Zeilenanfang
            return { x: 0, y: screenY, heightQ: line.heightQ || 4 }
        }

        const paraType = this._getParaType(para)
        const fullParsed = this._getParsedRichText(this._textParseCache, para.id, para.text, paraType)

        for (let i = startIdx; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (line.paragraphId !== paragraphId) break

            const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
            const endMatch = wrapBoundary ? offset < line.endOffset : offset <= line.endOffset

            if (offset >= line.startOffset && endMatch) {
                let lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

                // Listen-Prefix: Prefix-Zeichen aus Slice entfernen
                const prefixLen = line.listPrefixLen || 0
                if (prefixLen > 0) {
                    // Offset im Prefix-Bereich → an Content-Start mappen
                    if (offset < prefixLen + line.startOffset) {
                        const posMap = this._getPositionMap(i)
                        let hangingOffset
                        if (posMap && this.renderer) {
                            const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                            hangingOffset = Math.round(hangingPx / this.renderer.charWidth)
                        } else {
                            hangingOffset = prefixLen
                        }
                        const result = {
                            x: this.markerWidth + hangingOffset,
                            y: this._lineToScreenY(i),
                            heightQ: line.heightQ || 4
                        }
                        if (posMap) {
                            const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                            const r = this.renderer
                            const markerPx = r.isProportional() ? r.markerWidthPx() : this.markerWidth * r.charWidth
                            result.pixelX = markerPx + hangingPx
                        }
                        return result
                    }
                    lineSlice = lineSlice.slice(prefixLen)
                }

                // Data-Offset → Display-Spalte (kumulative Breite) UND UTF-16-Index
                let displayX = 0   // kumulative Display-Spalten (für Monospace x-Position)
                let displayU16 = 0 // UTF-16-Index (für posMap)
                let found = false
                {
                    let u16 = 0
                    let cols = 0
                    for (let j = 0; j < lineSlice.length; j++) {
                        if (lineSlice[j].dataOffset >= offset) {
                            displayX = cols
                            displayU16 = u16
                            found = true
                            break
                        }
                        cols += charDisplayWidth(lineSlice[j].char)
                        u16 += lineSlice[j].char.length
                    }
                    if (!found) {
                        displayX = cols
                        displayU16 = u16
                    }
                }

                // Proportional: hangingIndent ist in Pixel → für x als Zeichen-Approximation umrechnen
                const posMap = this._getPositionMap(i)
                let hangingOffset
                if (posMap && this.renderer) {
                    const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                    hangingOffset = Math.round(hangingPx / this.renderer.charWidth)
                } else {
                    hangingOffset = prefixLen > 0 ? prefixLen : (line.hangingIndent || 0)
                }
                const result = {
                    x: this.markerWidth + hangingOffset + displayX,
                    y: this._lineToScreenY(i),
                    heightQ: line.heightQ || 4
                }

                // Proportional: Pixel-Position über posMap (UTF-16-Index)
                if (posMap) {
                    const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                    const r = this.renderer
                    const markerPx = r.isProportional() ? r.markerWidthPx() : this.markerWidth * r.charWidth
                    result.pixelX = markerPx + hangingPx +
                        (displayU16 < posMap.xPositions.length
                            ? posMap.xPositions[displayU16]
                            : posMap.totalWidth)
                } else if (this.renderer && this.renderer.isProportional()) {
                    // Leerer Text: Cursor nach dem > Marker, mit proportionaler Marker-Breite
                    const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                    result.pixelX = this.renderer.markerWidthPx() + hangingPx
                }

                return result
            }

            if (line.skippedSpaces > 0 &&
                offset >= line.endOffset &&
                offset < line.endOffset + line.skippedSpaces) {
                let lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)
                const prefixLen = line.listPrefixLen || 0
                if (prefixLen > 0) lineSlice = lineSlice.slice(prefixLen)

                // Proportional: hangingIndent ist in Pixel → für x als Zeichen-Approximation umrechnen
                const posMap = this._getPositionMap(i)
                let hangingOffset
                if (posMap && this.renderer) {
                    const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                    hangingOffset = Math.round(hangingPx / this.renderer.charWidth)
                } else {
                    hangingOffset = prefixLen > 0 ? prefixLen : (line.hangingIndent || 0)
                }
                // Kumulative Display-Spalten für Monospace
                let endCols = 0
                for (let j = 0; j < lineSlice.length; j++) {
                    endCols += charDisplayWidth(lineSlice[j].char)
                }
                const result = {
                    x: this.markerWidth + hangingOffset + endCols,
                    y: this._lineToScreenY(i),
                    heightQ: line.heightQ || 4
                }

                if (posMap) {
                    const hangingPx = (line.pixelWidth > 0 || line.rtPrefix) ? (line.hangingIndent || 0) : 0
                    const r = this.renderer
                    const markerPx = r.isProportional() ? r.markerWidthPx() : this.markerWidth * r.charWidth
                    result.pixelX = markerPx + hangingPx + posMap.totalWidth
                }

                return result
            }
        }

        return null
    }

    /**
     * Findet den Zeilen-Index für eine Daten-Position.
     * @param {number} paragraphId
     * @param {number} offset
     * @returns {number} -1 wenn nicht gefunden
     */
    getLineIndexForPosition(paragraphId, offset) {
        // O(1)-Sprung zur ersten Zeile dieses Paragraphs
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return -1

        for (let i = startIdx; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (line.paragraphId !== paragraphId) break

            // Gleiche Boundary-Regel wie in dataToScreen
            const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
            const endMatch = wrapBoundary ? offset < line.endOffset : offset <= line.endOffset

            if (offset >= line.startOffset && endMatch) {
                return i
            }

            // Übersprungene Spaces gehören zu dieser Zeile
            if (line.skippedSpaces > 0 &&
                offset >= line.endOffset &&
                offset < line.endOffset + line.skippedSpaces) {
                return i
            }
        }
        return -1
    }

    // ===== Note/Info Position Mapping (gemeinsame Hilfsmethoden) =====

    /**
     * Liefert das Lines-Array, den Parse-Cache und den Feldtext für 'note' oder 'info'.
     * @param {'note'|'info'} field
     * @param {Object} para - Paragraph-Objekt
     * @returns {{ lines: Array, cache: Map, text: string }}
     */
    _fieldMeta(field, para) {
        if (field === 'note') {
            return { lines: this.noteLines, cache: this._noteParseCache, text: para.note }
        }
        return { lines: this.infoLines, cache: this._infoParseCache, text: para.info }
    }

    /**
     * Baseline-Shift in Pixeln für Note/Info-Inhalt (Cursor/Klick-Position), wenn
     * die Hauptzeile größer ist. Delegiert an `renderer.secondaryBaselineShiftPx`
     * (Single-Source mit dem Render-Pfad in OutlineView/ContextView) — der dort
     * gekappte Shift muss hier identisch sein, sonst driften Cursor/Klick vom
     * gerenderten Text weg.
     * @param {number} mainHeightQ - heightQ der zugehörigen Hauptzeile
     * @returns {number} Pixel-Shift (0 wenn Hauptzeile ≤ SECONDARY)
     */
    _fieldBaselineShiftPx(mainHeightQ) {
        if (!this.renderer || !this.renderer.isProportional()) return 0
        return this.renderer.secondaryBaselineShiftPx(mainHeightQ)
    }

    /**
     * Gemeinsame Implementierung für noteDataToScreen / infoDataToScreen.
     * Konvertiert eine Daten-Position im Feld-Text in Screen-Koordinaten.
     * @param {'note'|'info'} field
     * @param {number} paragraphId
     * @param {number} offset - Offset im Feld-Text
     * @returns {{x: number, y: number, pixelX?: number}|null}
     */
    _fieldDataToScreen(field, paragraphId, offset) {
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return null

        const para = this.model.getParagraph(paragraphId)
        if (!para) return null

        const { lines, cache, text } = this._fieldMeta(field, para)
        const fullParsed = this._getParsedRichText(cache, para.id, text, 'normal', field)

        // Leeres Feld: Cursor an offset=0 mapped auf die erste (Padding-)Line,
        // damit er zwischen den >< Markern sichtbar ist (sonst unsichtbar auf
        // note-/info-losen Absaetzen).
        const fieldEmpty = (text || '').length === 0
        if (fieldEmpty && offset === 0 && startIdx < lines.length
                && lines[startIdx].paragraphId === paragraphId
                && lines[startIdx].isPadding) {
            const line = lines[startIdx]
            const fieldHeightQ = line.heightQ || 4
            const mainHeightQ = (startIdx < this.lines.length ? this.lines[startIdx].heightQ : fieldHeightQ) || fieldHeightQ
            const result = {
                x: 0,
                y: this._lineToScreenY(startIdx, field),
                heightQ: fieldHeightQ,
                pixelYOffset: this._fieldBaselineShiftPx(mainHeightQ)
            }
            if (this.renderer && this.renderer.isProportional()) {
                result.pixelX = 0
            }
            return result
        }

        for (let i = startIdx; i < lines.length; i++) {
            const line = lines[i]
            if (line.paragraphId !== paragraphId) break
            if (line.isPadding) continue

            const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
            const endMatch = wrapBoundary ? offset < line.endOffset : offset <= line.endOffset

            if (offset >= line.startOffset && endMatch) {
                const lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)
                let displayX = 0   // kumulative Display-Spalten (Monospace)
                let displayU16 = 0 // UTF-16-Index (posMap)
                let found = false
                {
                    let u16 = 0
                    let cols = 0
                    for (let j = 0; j < lineSlice.length; j++) {
                        if (lineSlice[j].dataOffset >= offset) {
                            displayX = cols
                            displayU16 = u16
                            found = true
                            break
                        }
                        cols += charDisplayWidth(lineSlice[j].char)
                        u16 += lineSlice[j].char.length
                    }
                    if (!found) {
                        displayX = cols
                        displayU16 = u16
                    }
                }
                // Note/Info: heightQ=SECONDARY (Inhalt ist immer 4QL), aber wenn die
                // Hauptzeile größer ist, wird der Text per Baseline-Shift nach
                // unten verschoben — Cursor/Selection müssen denselben Shift bekommen.
                const fieldHeightQ = line.heightQ || 4
                const mainHeightQ = (i < this.lines.length ? this.lines[i].heightQ : fieldHeightQ) || fieldHeightQ
                const result = {
                    x: displayX,
                    y: this._lineToScreenY(i, field),
                    heightQ: fieldHeightQ,
                    pixelYOffset: this._fieldBaselineShiftPx(mainHeightQ)
                }

                // Proportional: Pixel-Position über posMap (UTF-16-Index)
                const posMap = this._getPositionMap(i, field)
                if (posMap) {
                    result.pixelX = displayU16 < posMap.xPositions.length
                        ? posMap.xPositions[displayU16]
                        : posMap.totalWidth
                } else if (this.renderer && this.renderer.isProportional()) {
                    // Leerer Text: pixelX=0 (Textbereich-Start, Marker wird außerhalb addiert)
                    result.pixelX = 0
                }

                return result
            }

            if (line.skippedSpaces > 0 &&
                offset >= line.endOffset &&
                offset < line.endOffset + line.skippedSpaces) {
                const lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)
                // Kumulative Display-Spalten für Monospace
                let endCols = 0
                for (let j = 0; j < lineSlice.length; j++) {
                    endCols += charDisplayWidth(lineSlice[j].char)
                }
                const fieldHeightQ2 = line.heightQ || 4
                const mainHeightQ2 = (i < this.lines.length ? this.lines[i].heightQ : fieldHeightQ2) || fieldHeightQ2
                const result = {
                    x: endCols,
                    y: this._lineToScreenY(i, field),
                    heightQ: fieldHeightQ2,
                    pixelYOffset: this._fieldBaselineShiftPx(mainHeightQ2)
                }

                const posMap = this._getPositionMap(i, field)
                if (posMap) {
                    result.pixelX = posMap.totalWidth
                }

                return result
            }
        }
        return null
    }

    /**
     * Gemeinsame Implementierung für noteScreenToData / infoScreenToData.
     * Konvertiert Screen-Koordinaten in eine Daten-Position im Feld-Text.
     * @param {'note'|'info'} field
     * @param {number} fieldX - Spalte innerhalb des Feld-Textbereichs
     * @param {number} screenY - Zeile auf dem Screen
     * @param {number|null} pixelX - Pixel-Position (proportional)
     * @returns {{paragraphId: number, offset: number}|null}
     */
    _fieldScreenToData(field, fieldX, screenY, pixelX = null) {
        let lineIndex
        if (this._useQuarterSpacing() && this.renderer) {
            lineIndex = this._lineAtScreenPixelY(screenY * this.renderer.charHeight, field)
        } else {
            lineIndex = this.viewportStart + Math.floor(screenY)
        }

        // lines-Array über _fieldMeta ermitteln — para wird erst bei Bedarf geladen
        const fieldLines = field === 'note' ? this.noteLines : this.infoLines
        if (lineIndex < 0 || lineIndex >= fieldLines.length) return null

        const line = fieldLines[lineIndex]
        if (line.isPadding) {
            return { paragraphId: line.paragraphId, offset: 0 }
        }

        // Proportional: Pixel-basiert → UTF-16-Index aus posMap → dataOffset via _utf16ToDataOffset
        const posMap = this._getPositionMap(lineIndex, field)
        if (posMap && pixelX !== null) {
            const displayIndex = posMap.getCharIndexAtX(Math.max(0, pixelX))
            const para = this.model.getParagraph(line.paragraphId)
            if (!para) return { paragraphId: line.paragraphId, offset: line.startOffset }
            const { cache, text } = this._fieldMeta(field, para)
            const fullParsed = this._getParsedRichText(cache, para.id, text, 'normal', field)
            const lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)
            const lineText = parsedCharsToString(lineSlice)
            let maxUtf16 = lineText.length
            const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
            if (wrapBoundary && lineSlice.length > 0) {
                maxUtf16 -= lineSlice[lineSlice.length - 1].char.length
            }
            const clampedIndex = Math.min(displayIndex, maxUtf16)
            const offset = this._utf16ToDataOffset(lineSlice, clampedIndex, line.endOffset)
            const result = { paragraphId: line.paragraphId, offset }

            // Link-Info: Zeichen UNTER der Maus (nicht die gerundete Cursor-Position)
            this._applyLinkUnderPixelX(result, lineSlice, posMap, pixelX)

            return result
        }

        // Monospace: Zeichenbasiert (Emoji = 2 Spalten)
        const para = this.model.getParagraph(line.paragraphId)
        if (!para) return { paragraphId: line.paragraphId, offset: line.startOffset }

        const { cache, text } = this._fieldMeta(field, para)
        const fullParsed = this._getParsedRichText(cache, para.id, text, 'normal', field)
        const lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Display-Breite kumulativ berechnen
        let totalCols = 0
        for (let k = 0; k < lineSlice.length; k++) {
            totalCols += charDisplayWidth(lineSlice[k].char)
        }
        let maxDisplayX = totalCols
        const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
        if (wrapBoundary && lineSlice.length > 0) {
            maxDisplayX -= charDisplayWidth(lineSlice[lineSlice.length - 1].char)
        }
        const textX = Math.max(0, Math.min(fieldX, maxDisplayX))

        // Kumulativ zählen um geklickte Spalte → Daten-Offset zu finden
        let col = 0
        for (let k = 0; k < lineSlice.length; k++) {
            const sc = lineSlice[k]
            const w = charDisplayWidth(sc.char)
            if (col + w > textX) {
                const result = { paragraphId: line.paragraphId, offset: sc.dataOffset }
                if (sc.isLink) { result.isLink = true; result.linkTargetId = sc.linkTargetId }
                if (sc.isUrl) { result.isUrl = true; result.url = sc.url }
                return result
            }
            col += w
        }
        return { paragraphId: line.paragraphId, offset: line.endOffset }
    }

    /**
     * Gemeinsame Implementierung für getNoteLineIndexForPosition / getInfoLineIndexForPosition.
     * Findet den Zeilen-Index für eine Position im Feld-Text.
     * @param {'note'|'info'} field
     * @param {number} paragraphId
     * @param {number} offset - Offset im Feld-Text
     * @returns {number} -1 wenn nicht gefunden
     */
    _getFieldLineIndexForPosition(field, paragraphId, offset) {
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return -1

        const lines = field === 'note' ? this.noteLines : this.infoLines

        // Leeres Feld erkennen — dann mapped offset=0 auf die erste (Padding-)
        // Line des Paragraphs. Sonst waere der Cursor auf info-/note-losen
        // Absaetzen nicht renderbar (z.B. Context maximieren, provisional
        // aufgenommener Absatz ohne info — Cursor sitzt zwischen den >< Markern).
        const para = this.model.getParagraph(paragraphId)
        const fieldText = para ? (field === 'note' ? (para.note || '') : (para.info || '')) : ''
        const fieldEmpty = fieldText.length === 0

        for (let i = startIdx; i < lines.length; i++) {
            const line = lines[i]
            if (line.paragraphId !== paragraphId) break
            if (line.isPadding) {
                if (fieldEmpty && offset === 0 && i === startIdx) return i
                continue
            }

            const wrapBoundary = !line.isLastLine && line.skippedSpaces === 0
            const endMatch = wrapBoundary ? offset < line.endOffset : offset <= line.endOffset

            if (offset >= line.startOffset && endMatch) {
                return i
            }

            if (line.skippedSpaces > 0 &&
                offset >= line.endOffset &&
                offset < line.endOffset + line.skippedSpaces) {
                return i
            }
        }
        return -1
    }

    // ===== Note Position Mapping =====

    /**
     * Konvertiert Note-Daten-Position zu Screen-Koordinaten (Zeile im Viewport).
     * @param {number} paragraphId
     * @param {number} offset - Offset im Note-Text
     * @returns {{x: number, y: number}|null} x ist relativ zum Note-Bereich
     */
    noteDataToScreen(paragraphId, offset) {
        return this._fieldDataToScreen('note', paragraphId, offset)
    }

    /**
     * Konvertiert Note-Screen-Koordinaten zu Daten-Position.
     * @param {number} noteX - Spalte innerhalb des Note-Textbereichs
     * @param {number} screenY - Zeile auf dem Screen
     * @param {number|null} pixelX - Pixel-Position (proportional)
     * @returns {{paragraphId: number, offset: number}|null}
     */
    noteScreenToData(noteX, screenY, pixelX = null) {
        return this._fieldScreenToData('note', noteX, screenY, pixelX)
    }

    /**
     * Findet den Zeilen-Index für eine Note-Position.
     * @param {number} paragraphId
     * @param {number} offset - Offset im Note-Text
     * @returns {number} -1 wenn nicht gefunden
     */
    getNoteLineIndexForPosition(paragraphId, offset) {
        return this._getFieldLineIndexForPosition('note', paragraphId, offset)
    }

    // ===== Info-Position-Mapping (Context-Pane) =====

    /**
     * Konvertiert eine Info-Datenposition in Screen-Koordinaten.
     * @param {number} paragraphId
     * @param {number} offset - Offset im Info-Text
     * @returns {{x: number, y: number, pixelX?: number}|null}
     */
    infoDataToScreen(paragraphId, offset) {
        return this._fieldDataToScreen('info', paragraphId, offset)
    }

    /**
     * Konvertiert Screen-Koordinaten in eine Info-Datenposition.
     * @param {number} infoX - X in Textbreite
     * @param {number} screenY - Y relativ zum Viewport
     * @param {number|null} pixelX - Pixel-Position (proportional)
     * @returns {{paragraphId: number, offset: number}|null}
     */
    infoScreenToData(infoX, screenY, pixelX = null) {
        return this._fieldScreenToData('info', infoX, screenY, pixelX)
    }

    /**
     * Findet den Zeilen-Index für eine Info-Position.
     * @param {number} paragraphId
     * @param {number} offset - Offset im Info-Text
     * @returns {number} -1 wenn nicht gefunden
     */
    getInfoLineIndexForPosition(paragraphId, offset) {
        return this._getFieldLineIndexForPosition('info', paragraphId, offset)
    }

    /**
     * Feld-bewusster Screen-Zeilen-Index für eine Cursor-Position:
     * text → lines, note → noteLines, info → infoLines. Nötig, weil
     * Co-Located Outline-Items nicht in lines[] stehen — getLineIndexForPosition
     * allein fände einen Note-Cursor auf einem Outline-Item nicht (Viewport-
     * Restore nach Reload schlug deshalb im Outline-Pane fehl).
     * @param {number} paragraphId
     * @param {number} offset
     * @param {'text'|'note'|'info'} field
     * @returns {number} -1 wenn nicht gefunden
     */
    getCursorLineIndex(paragraphId, offset, field = 'text') {
        if (field === 'note') return this.getNoteLineIndexForPosition(paragraphId, offset)
        if (field === 'info') return this.getInfoLineIndexForPosition(paragraphId, offset)
        return this.getLineIndexForPosition(paragraphId, offset)
    }

    // ===== Viewport Management =====

    /**
     * Begrenzt viewportStart und _viewportPixelOffset auf gültige Bereiche.
     */
    _clampViewport() {
        // Grund-Sicherung: viewportStart muss in [0, lines.length) liegen.
        // Beim Pane-Filter-Wechsel (z.B. Main hidden → Outline FULLSCREEN) schrumpft
        // lines[] stark, waehrend viewportStart seinen alten Wert behaelt. Ohne diesen
        // Guard liest der Quarter-Branch unten `_lineQuarterY[OOB] || 0` und denkt, der
        // Viewport stehe ganz oben — die maxTopQ-Pruefung schlaegt nie an, viewportStart
        // bleibt jenseits des Endes, und das Pane wirkt vollstaendig leer.
        if (this.viewportStart >= this.lines.length) {
            this.viewportStart = Math.max(0, this.lines.length - 1)
            this._viewportPixelOffset = 0
        }

        // Oberer Rand
        if (this.viewportStart <= 0 && this._viewportPixelOffset < 0) {
            this.viewportStart = 0
            this._viewportPixelOffset = 0
        }

        // Unterer Rand: quarter-basiert mit Padding
        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            const viewportQ = this.height * 4
            const effectiveTotalQ = this._totalQuarters + PARAGRAPH_SPACING.BOTTOM_PADDING
            if (effectiveTotalQ <= viewportQ) {
                this.viewportStart = 0
                this._viewportPixelOffset = 0
            } else {
                const quarterH = this.renderer.charHeight / 4
                const vpQ = this._lineQuarterY[this.viewportStart] || 0
                const currentTopQ = vpQ + this._viewportPixelOffset / quarterH
                const maxTopQ = effectiveTotalQ - viewportQ
                if (currentTopQ > maxTopQ) {
                    // Binärsuche: Zeile finden deren quarterY <= maxTopQ
                    let lo = 0, hi = this._lineQuarterY.length - 1
                    while (lo < hi) {
                        const mid = (lo + hi + 1) >> 1
                        if (this._lineQuarterY[mid] <= maxTopQ) lo = mid
                        else hi = mid - 1
                    }
                    this.viewportStart = lo
                    this._viewportPixelOffset = (this._lineQuarterY[lo] < maxTopQ)
                        ? (maxTopQ - this._lineQuarterY[lo]) * quarterH
                        : 0
                }
            }
        } else {
            // Monospace: grid-based, nur viewportStart clampen (kein Sub-Pixel-Offset)
            const maxViewportStart = Math.max(0, this.lines.length - this.height)
            if (this.viewportStart > maxViewportStart) {
                this.viewportStart = maxViewportStart
            }
            this._viewportPixelOffset = 0
        }
    }

    /**
     * Scrollt zu einer bestimmten Zeile.
     * @param {number} lineIndex
     */
    scrollTo(lineIndex) {
        lineIndex = Math.max(0, lineIndex)
        // Oberes Maximum: darf nicht zu weit nach unten scrollen
        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            // Quarter-basiert: _clampViewport korrigiert nach dem Setzen
        } else {
            lineIndex = Math.min(lineIndex, Math.max(0, this.lines.length - this.height))
        }
        const oldStart = this.viewportStart
        const oldOffset = this._viewportPixelOffset
        if (this.viewportStart !== lineIndex || this._viewportPixelOffset !== 0) {
            // Pixel-Delta für Scroll-Shift VOR dem Schreiben berechnen
            let pixelDelta = 0
            if (this.renderer) {
                const charHeight = this.renderer.charHeight
                if (this._useQuarterSpacing() && this._lineQuarterY.length > 0) {
                    const quarterH = charHeight / 4
                    const oldPx = (this._lineQuarterY[oldStart] || 0) * quarterH + oldOffset
                    const newPx = (this._lineQuarterY[lineIndex] || 0) * quarterH
                    pixelDelta = newPx - oldPx
                } else {
                    pixelDelta = (lineIndex - oldStart) * charHeight - oldOffset
                }
            }

            this.viewportStart = lineIndex
            this._viewportPixelOffset = 0
            this.isDirty = true
            this._scrollDelta = (this._scrollDelta || 0) + (lineIndex - oldStart)
            this._scrollPixelDelta = (this._scrollPixelDelta || 0) + pixelDelta
            // Quarter-Spacing: _clampViewport stellt sicher, dass wir nicht ueber das Ende scrollen
            if (this._useQuarterSpacing()) {
                this._clampViewport()
            }
        }
    }

    /**
     * Scrollt um eine Anzahl Zeilen.
     * @param {number} delta - Positive Werte = nach unten
     */
    scrollBy(delta) {
        this.scrollTo(this.viewportStart + delta)
    }

    /**
     * Scrollt um Viertel-Zeilenhöhen (Legacy-Wrapper).
     * Leitet an scrollByPixels weiter — dort läuft die eigentliche Scroll-Mechanik.
     * @param {number} deltaQ - Anzahl Viertel-Zeilen (positiv = nach unten)
     */
    scrollByQ(deltaQ) {
        if (!this.renderer) return
        this.scrollByPixels(deltaQ * this.renderer.charHeight / 4)
    }

    /**
     * Scrollt pixelgenau — funktioniert in Proportional- UND Monospace-Mode.
     * Kein Rasten auf Quarters oder Zeilen; _viewportPixelOffset nimmt den Bruchteil auf.
     * Das verhindert das Hüpfen bei kleinen Trackpad-/Touch-Bewegungen auf Retina-Displays.
     * @param {number} deltaPx - Pixel-Betrag (positiv = nach unten)
     */
    scrollByPixels(deltaPx) {
        if (!this.renderer) return
        const charHeight = this.renderer.charHeight
        if (charHeight <= 0) return

        const useQuarter = this._useQuarterSpacing() && this._lineQuarterY.length > 0
        const quarterH = charHeight / 4

        if (!useQuarter) {
            // Monospace: grid-based, zeilenweise scrollen (wie vor Lesson 52).
            // Sub-Zeilen-Pixel werden akkumuliert bis eine ganze Zeile voll ist.
            // _viewportPixelOffset bleibt 0 — kein Sub-Pixel-Rendering im Monospace.
            this._monoScrollAccumulator += deltaPx
            const lineDelta = Math.trunc(this._monoScrollAccumulator / charHeight)
            if (lineDelta !== 0) {
                this._monoScrollAccumulator -= lineDelta * charHeight
                this.scrollTo(this.viewportStart + lineDelta)
            }
            return
        }

        // Proportional: Pixel-genaues Scrolling mit Sub-Pixel-Offset.
        // Viewport-Position VOR dem Scroll merken (für Pixel-Delta-Tracking)
        const beforePx = ((this._lineQuarterY[this.viewportStart] || 0) * quarterH) + this._viewportPixelOffset

        this._viewportPixelOffset += deltaPx

        // Vorwärts-/Rückwärts-Normalisierung anhand der ECHTEN Grid-Distanz aus
        // _lineQuarterY (Quelle der Wahrheit). Früher wurde die Distanz aus
        // `lineH + nextSpacingBefore` rekonstruiert — das stimmte exakt mit dem
        // alten Cumulative-Grid überein. Seit dem Per-Pane-Slot-Modell weicht die
        // tatsächliche Main-Grid-Distanz davon ab (Slot-Höhe = max der Panes,
        // kollabierte Padding-Zeilen mit Distanz 0). Die heightQ-basierte Schwelle
        // desynchronisierte dann Offset und gerenderte Position → Scroll-Hüpfer.
        // Distanz-0-Zeilen (kollabierte Padding) werden hier sauber übersprungen.
        while (this.viewportStart < this.lines.length - 1) {
            const distToNextTop = ((this._lineQuarterY[this.viewportStart + 1] || 0)
                - (this._lineQuarterY[this.viewportStart] || 0)) * quarterH
            if (this._viewportPixelOffset < distToNextTop) break
            this.viewportStart++
            this._viewportPixelOffset -= distToNextTop
        }
        while (this._viewportPixelOffset < 0 && this.viewportStart > 0) {
            const prevDist = ((this._lineQuarterY[this.viewportStart] || 0)
                - (this._lineQuarterY[this.viewportStart - 1] || 0)) * quarterH
            this._viewportPixelOffset += prevDist
            this.viewportStart--
        }

        this._clampViewport()

        // Tatsächlichen Pixel-Delta nach Clamping tracken (für Scroll-Shift-Optimierung)
        const afterPx = ((this._lineQuarterY[this.viewportStart] || 0) * quarterH) + this._viewportPixelOffset
        const actualPixelDelta = afterPx - beforePx

        this.isDirty = true
        // _scrollDelta in Quartern halten (Kompatibilität mit bestehenden Lesern)
        this._scrollDelta = (this._scrollDelta || 0) + (actualPixelDelta / quarterH)
        this._scrollPixelDelta = (this._scrollPixelDelta || 0) + actualPixelDelta
    }

    /**
     * Stellt sicher, dass der Cursor sichtbar ist.
     * @param {number} margin - Mindestabstand zum Viewport-Rand in Zeilen (0 = knapp sichtbar)
     */
    ensureCursorVisible(margin = 0) {
        if (!this.model.cursor) return

        // Zeilen aktualisieren falls nötig (sonst stimmt die Position nicht)
        // Rebuild basiert NUR auf model._dirtyLevel, nicht auf view.isDirty
        const modelDirty = this.model._dirtyLevel
        if (modelDirty === 'STRUCTURAL' && this._linesVersion !== this.model._dirtyVersion) {
            this._rebuildLines()
        } else if (modelDirty === 'PARAGRAPH' && this._linesVersion !== this.model._dirtyVersion) {
            for (const paraId of this.model._dirtyParagraphIds) {
                this.updateParagraph(paraId)
            }
            this._linesVersion = this.model._dirtyVersion
        }

        // Feld-bewusst: Info/Note/Text-Cursor nutzen jeweilige Lines-Arrays
        const cursorField = this.model.cursor.field || 'text'
        const isInfo = cursorField === 'info'
        const isNote = cursorField === 'note'
        const lineIndex = isInfo
            ? this.getInfoLineIndexForPosition(
                this.model.cursor.paragraphId,
                this.model.cursor.offset
            )
            : isNote
            ? this.getNoteLineIndexForPosition(
                this.model.cursor.paragraphId,
                this.model.cursor.offset
            )
            : this.getLineIndexForPosition(
                this.model.cursor.paragraphId,
                this.model.cursor.offset
            )

        if (lineIndex < 0) return

        // Margin auf sinnvollen Bereich begrenzen
        const effectiveMargin = Math.min(margin, Math.floor(this.height / 2))

        if (this._useQuarterSpacing() && this.renderer) {
            // Proportional: pixelbasierte Prüfung und Korrektur — feld-bewusst,
            // da Note/Info ihr eigenes Y-Grid (4QL) haben.
            const fieldLines = isInfo ? this.infoLines : isNote ? this.noteLines : this.lines
            const linePxY = this._lineScreenPixelY(lineIndex, cursorField)
            const marginPx = effectiveMargin * this.renderer.charHeight
            const viewportPx = this.height * this.renderer.charHeight
            const quarterH = this.renderer.charHeight / 4
            const lineH = (fieldLines[lineIndex]?.heightQ || 4) * quarterH
            if (linePxY < marginPx) {
                // Cursor über dem Viewport — exakt um den Fehlbetrag nach oben scrollen
                const overPx = marginPx - linePxY
                this.scrollByQ(-Math.ceil(overPx / quarterH))
            } else if (linePxY + lineH > viewportPx - marginPx) {
                // Cursor unter dem Viewport — exakt um den Fehlbetrag nach unten scrollen
                const overPx = (linePxY + lineH) - (viewportPx - marginPx)
                this.scrollByQ(Math.ceil(overPx / quarterH))
            }
        } else {
            // Monospace: zeilenbasierte Prüfung (wie bisher)
            if (lineIndex < this.viewportStart + effectiveMargin) {
                this.scrollTo(lineIndex - effectiveMargin)
            } else if (lineIndex >= this.viewportStart + this.height - effectiveMargin) {
                this.scrollTo(lineIndex - this.height + 1 + effectiveMargin)
            }
        }
    }

    /**
     * Gibt die Anzahl sichtbarer Zeilen zurück.
     * @returns {number}
     */
    getVisibleLineCount() {
        return Math.min(this.height, this.lines.length - this.viewportStart)
    }

    /**
     * Gibt die Gesamtzahl der Zeilen zurück.
     * @returns {number}
     */
    getTotalLineCount() {
        return this.lines.length
    }

    // ===== Scrollbar =====

    /**
     * Berechnet den Scrollbar-Status.
     * @param {number} [viewHeight] - Optionale Höhe (für externes Rendering in anderem Pane)
     * @returns {{visible: boolean, thumbTop: number, thumbHeight: number}}
     */
    getScrollbarState(viewHeight) {
        const h = viewHeight !== undefined ? viewHeight : this.height

        // Im Quarter-Spacing-Mode (Worker/Artist/etc.) ist eine Doc-Line in der
        // Praxis hoeher als eine Char-Cell — typische Worker-Zeile sind 6 Quarters
        // (= 1.5 Char-Cells) plus Paragraph-Spacing. Die Sichtbarkeits-
        // Entscheidung muss daher VOR dem klassischen `lines.length <= height`-
        // Vergleich passieren; sonst erscheint die Scrollbar erst, wenn das
        // Dokument ca. 1.5 Screenlaengen lang ist (Hauptbug 2026-05-17).
        if (this._useQuarterSpacing() && this._totalQuarters > 0) {
            // Proportional: Quarter-basierte Scrollbar (mit Bottom-Padding)
            const visibleQ = this.height * 4
            const effectiveTotalQ = this._totalQuarters + PARAGRAPH_SPACING.BOTTOM_PADDING
            if (effectiveTotalQ <= visibleQ) {
                return { visible: false, thumbTop: 0, thumbHeight: 0 }
            }
            // Mindest-Thumb-Höhe: der Minimap-Viewport ist zugleich Drag-Handle
            // und braucht eine greifbare Größe — MIN_VIEWPORT_HEIGHT ist in Pixeln,
            // hier auf Chars umgerechnet, um mit dem Char-basierten Click/Drag konsistent zu bleiben.
            const minThumbChars = this.renderer
                ? Math.ceil(MINIMAP.MIN_VIEWPORT_HEIGHT / this.renderer.charHeight) : 1
            const naturalThumbHeight = Math.max(1, Math.floor(visibleQ / effectiveTotalQ * h))
            const thumbHeight = Math.min(h, Math.max(naturalThumbHeight, minThumbChars))
            const scrollRangeQ = effectiveTotalQ - visibleQ
            const thumbRange = h - thumbHeight
            const quarterH = this.renderer ? this.renderer.charHeight / 4 : 1
            const currentQ = (this._lineQuarterY[this.viewportStart] || 0) + this._viewportPixelOffset / quarterH
            const thumbTop = thumbRange > 0
                ? Math.floor(Math.min(currentQ / scrollRangeQ, 1) * thumbRange) : 0
            return { visible: true, thumbTop, thumbHeight }
        }

        // Monospace: zeilenbasierte Scrollbar — eine Line = eine Char-Zelle.
        const totalLines = this.lines.length
        const visibleLines = this.height
        if (totalLines <= visibleLines) {
            return { visible: false, thumbTop: 0, thumbHeight: 0 }
        }
        const thumbHeight = Math.max(1, Math.floor(visibleLines / totalLines * h))
        const scrollRange = totalLines - visibleLines
        const thumbRange = h - thumbHeight
        const thumbTop = Math.floor(this.viewportStart / scrollRange * thumbRange)

        return {
            visible: true,
            thumbTop,
            thumbHeight
        }
    }

    /**
     * Prüft ob eine X-Position auf der Scrollbar liegt.
     * @param {number} x - X-Position relativ zum Editor
     * @returns {boolean}
     */
    isOnScrollbar(x) {
        return x === this.width
    }

    /**
     * Scrollt basierend auf einer Y-Position in der Scrollbar.
     * Wird für Klick auf Track verwendet.
     * @param {number} y - Y-Position relativ zur Scrollbar (0 bis viewHeight-1)
     * @param {number} [viewHeight] - Optionale Höhe (wenn Scrollbar in anderem Pane)
     */
    scrollToScrollbarY(y, viewHeight) {
        const h = viewHeight !== undefined ? viewHeight : this.height
        const totalLines = this.lines.length
        const visibleLines = this.height

        if (totalLines <= visibleLines) return

        // Proportional: pixel-/quarter-genaues Mapping auf die Minimap-Position
        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            const quarterH = this.renderer.charHeight / 4
            const visibleQ = this.height * 4
            const totalQ = this._totalQuarters + PARAGRAPH_SPACING.BOTTOM_PADDING
            const scrollRangeQ = Math.max(0, totalQ - visibleQ)

            // Pixel-Position VOR dem Viewport-Sprung merken — wird unten für
            // _scrollPixelDelta/_scrollDelta gebraucht, damit der Scroll-Shift-Pfad
            // Main/Context korrekt aktualisiert (sonst bleiben sie eingefroren,
            // wenn `_shiftResidual` von einem vorherigen Scroll noch != 0 ist).
            const oldPx = (this._lineQuarterY[this.viewportStart] || 0) * quarterH + (this._viewportPixelOffset || 0)

            // Klick-Position (in Chars, fractional möglich) → Quarter-Position
            const ratio = Math.max(0, Math.min(1, y / h))
            let targetQ = ratio * totalQ

            // Snap auf Separator-Linien: ±2 Pixel auf der Minimap ergibt einen
            // Treffer und zentriert den Separator exakt im Viewport.
            const minimapPxH = h * this.renderer.charHeight
            const clickPxY = y * this.renderer.charHeight
            for (let i = 0; i < this.lines.length; i++) {
                const ln = this.lines[i]
                if (!ln.isSeparator) continue
                const sepQ = (this._lineQuarterY[i] || 0) + (ln.heightQ || 0) / 2
                const sepPxY = (sepQ / totalQ) * minimapPxH
                if (Math.abs(clickPxY - sepPxY) <= 2) {
                    targetQ = sepQ
                    break
                }
            }

            // Zentrieren: Ziel-Quarter in die Mitte des Viewports bringen
            const newStartQ = Math.max(0, Math.min(scrollRangeQ, targetQ - visibleQ / 2))

            // Viewport-Start: Zeile finden mit _lineQuarterY[line] <= newStartQ
            let lo = 0, hi = this._lineQuarterY.length - 1
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1
                if (this._lineQuarterY[mid] <= newStartQ) lo = mid
                else hi = mid - 1
            }
            this.viewportStart = lo
            this._viewportPixelOffset = (this._lineQuarterY[lo] < newStartQ)
                ? (newStartQ - this._lineQuarterY[lo]) * quarterH
                : 0
            this._clampViewport()
            this.isDirty = true

            // Tatsächlichen Pixel-Delta nach Clamping tracken — analog scrollByPixels.
            const newPx = (this._lineQuarterY[this.viewportStart] || 0) * quarterH + (this._viewportPixelOffset || 0)
            const pixelDelta = newPx - oldPx
            this._scrollDelta = (this._scrollDelta || 0) + pixelDelta / quarterH
            this._scrollPixelDelta = (this._scrollPixelDelta || 0) + pixelDelta
            return
        }

        // Monospace: zeilenbasiert, Ziel-Zeile zentrieren
        const ratio = y / h
        const targetLine = Math.floor(ratio * totalLines)
        const newViewportStart = Math.max(0, targetLine - Math.floor(visibleLines / 2))
        this.scrollTo(newViewportStart)
    }

    /**
     * Scrollt basierend auf Thumb-Drag-Delta.
     * @param {number} deltaY - Änderung der Y-Position seit Drag-Start
     * @param {number} startViewport - Viewport-Position bei Drag-Start
     * @param {number} [viewHeight] - Optionale Höhe (wenn Scrollbar in anderem Pane)
     */
    scrollByThumbDrag(deltaY, startViewport, viewHeight) {
        const totalLines = this.lines.length
        const visibleLines = this.height

        if (totalLines <= visibleLines) return

        const state = this.getScrollbarState(viewHeight)
        const h = viewHeight !== undefined ? viewHeight : this.height
        const thumbRange = h - state.thumbHeight

        if (thumbRange <= 0) return

        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            // Quarter-basiert: Thumb-Position → Quarter-Position umrechnen
            const visibleQ = this.height * 4
            const effectiveTotalQ = this._totalQuarters + PARAGRAPH_SPACING.BOTTOM_PADDING
            const scrollRangeQ = effectiveTotalQ - visibleQ
            const qPerPixel = scrollRangeQ / thumbRange
            const targetQ = (this._lineQuarterY[startViewport] || 0) + deltaY * qPerPixel

            const quarterH = this.renderer.charHeight / 4
            // Pixel-Position VOR dem Viewport-Sprung merken (s.u.)
            const oldPx = (this._lineQuarterY[this.viewportStart] || 0) * quarterH + (this._viewportPixelOffset || 0)

            // Binärsuche: Zeile finden deren quarterY <= targetQ
            const clampedQ = Math.max(0, Math.min(targetQ, scrollRangeQ))
            let lo = 0, hi = this._lineQuarterY.length - 1
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1
                if (this._lineQuarterY[mid] <= clampedQ) lo = mid
                else hi = mid - 1
            }
            this.viewportStart = lo
            this._viewportPixelOffset = (this._lineQuarterY[lo] < clampedQ)
                ? (clampedQ - this._lineQuarterY[lo]) * quarterH
                : 0
            this._clampViewport()
            this.isDirty = true

            // Pixel-Delta tracken — ohne das bleibt der Scroll-Shift-Pfad im
            // Sub-Pixel-Residual-Limbo hängen (sp=0, renderPartial no-op) und
            // Main/Context rendern nicht neu, obwohl der Viewport sich bewegt hat.
            const newPx = (this._lineQuarterY[this.viewportStart] || 0) * quarterH + (this._viewportPixelOffset || 0)
            const pixelDelta = newPx - oldPx
            this._scrollDelta = (this._scrollDelta || 0) + pixelDelta / quarterH
            this._scrollPixelDelta = (this._scrollPixelDelta || 0) + pixelDelta
        } else {
            // Monospace: zeilenbasiert
            const scrollRange = totalLines - visibleLines
            const linesPerPixel = scrollRange / thumbRange
            const deltaLines = Math.round(deltaY * linesPerPixel)
            this.scrollTo(startViewport + deltaLines)
        }
    }

    /**
     * Prüft ob eine Y-Position auf dem Thumb liegt.
     * @param {number} y - Y-Position relativ zur Scrollbar
     * @param {number} [viewHeight] - Optionale Höhe (wenn Scrollbar in anderem Pane)
     * @returns {boolean}
     */
    isOnScrollbarThumb(y, viewHeight) {
        const state = this.getScrollbarState(viewHeight)
        if (!state.visible) return false

        return y >= state.thumbTop && y < state.thumbTop + state.thumbHeight
    }

    // ===== Markdown Parsing =====

    /**
     * Parsed Markdown-Formatierung und gibt Zeichen mit Styles zurück.
     *
     * Markdown-Regeln (streng):
     * - Marker stehen direkt am formatierten Text (kein Leerzeichen dazwischen)
     * - Vor öffnendem Marker: Leerzeichen, Zeilenanfang oder öffnende Klammer
     * - Nach schließendem Marker: Leerzeichen, Zeilenende oder Satzzeichen
     * - Nur VOLLSTÄNDIGE gültige Marker-Paare werden formatiert
     *
     * @param {string} text
     * @param {string} paragraphType - Typ des Paragraphs
     * @returns {Array<{char: string, style: string}>}
     */
    /**
     * Erkennt den Paragraph-Typ anhand des Texts.
     * @param {string} text
     * @returns {string} 'normal', 'heading', 'list', 'blockquote', 'hr'
     */
    _detectParagraphType(text) {
        return detectParagraphType(text)
    }

    /**
     * Dual-Path-Wrapper: ermittelt Paragraph-Typ.
     * Im RT-Modus aus Block-Format-Feldern, sonst aus Markdown-Text.
     * @param {object} para - Paragraph-Objekt
     * @returns {string} 'normal', 'heading', 'list', 'blockquote', 'hr'
     */
    _getParaType(para) {
        para = this._effectiveParaForRender(para)
        if (para.type === 'hr') return 'hr'
        if (para.headingLevel > 0) return 'heading'
        if (para.listType) return 'list'
        if (para.blockquote) return 'blockquote'
        return 'normal'
    }

    /**
     * Gibt die Spacing-Definition (before/after) für eine ScreenLine zurück.
     * @param {object} line - ScreenLine
     * @returns {{ before: number, after: number }}
     */
    _getParaSpacing(line) {
        if (line.isSeparator) return PARAGRAPH_SPACING.SEPARATOR
        let para = this.model.getParagraph(line.paragraphId)
        // Virtuelle Insert-Vorschau-Zeilen stehen nicht im Modell — ohne
        // Pseudo-Para fiele das Margin-Collapsing fuer Heading-/List-/
        // Blockquote-Inserts auf NORMAL zurueck.
        if (!para && line._reviewInsertOverlay) {
            para = this._pseudoParaFromInsertOverlay(line._reviewInsertOverlay)
        }
        if (!para) return PARAGRAPH_SPACING.NORMAL
        // Leading-Padding-Zeilen vor dem Background-Separator (gleiche paragraphId
        // wie der Separator): großer Abstand davor. Im Proportional kollabieren die
        // Padding-Zeilen (Höhe 0), daher wird das 5-Zeilen-Padding hier als
        // spacingBefore nachgebildet — im Monospace tragen die Zeilen selbst.
        if (para.type === 'separator' && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            return PARAGRAPH_SPACING.BACKGROUND_SEPARATOR
        }
        para = this._effectiveParaForRender(para)
        if (para.type === 'hr') return PARAGRAPH_SPACING.HR
        if (para.codeBlock) return PARAGRAPH_SPACING.CODE_PARA
        if (para.headingLevel > 0) return headingSpacing(para.headingLevel)
        if (para.listType) return PARAGRAPH_SPACING.LIST
        if (para.blockquote) return PARAGRAPH_SPACING.BLOCKQUOTE
        return PARAGRAPH_SPACING.NORMAL
    }

    /**
     * Gibt die LinePositionMap für eine proportionale Zeile zurück (lazy, gecacht).
     * @param {number} lineIndex - Globaler Zeilenindex in this.lines
     * @param {string} field - 'text', 'note' oder 'info'
     * @returns {LinePositionMap|null} null wenn nicht proportional
     */
    _getPositionMap(lineIndex, field = 'text') {
        if (!this.renderer || !this.renderer.isProportional()) return null
        const metricsCache = this.renderer.getGlyphMetricsCache()
        if (!metricsCache) return null

        const mapCache = field === 'info' ? this._infoPositionMaps
            : field === 'note' ? this._notePositionMaps : this._textPositionMaps
        let map = mapCache.get(lineIndex)
        if (map) return map

        const lines = field === 'info' ? this.infoLines
            : field === 'note' ? this.noteLines : this.lines
        const line = lines[lineIndex]
        if (!line || line.isPadding) return null

        const para = this.model.getParagraph(line.paragraphId)
        if (!para) return null

        const text = field === 'info' ? (para.info || '')
            : field === 'note' ? (para.note || '') : para.text
        const paraType = this._getParaType(para)
        const parseCache = field === 'info' ? this._infoParseCache
            : field === 'note' ? this._noteParseCache : this._textParseCache
        const fullParsed = this._getParsedRichText(parseCache, para.id, text, paraType, field)
        let lineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Listen-Prefix: posMap nur für Content (ohne Prefix-Zeichen)
        const prefixLen = line.listPrefixLen || 0
        if (prefixLen > 0) {
            lineSlice = lineSlice.slice(prefixLen)
        }

        // Display-Text und Format-Runs für diese Zeile
        const lineText = parsedCharsToString(lineSlice)
        const lineFormatRuns = parsedToFormatRuns(lineSlice)

        // Style-Definitionen in bold/italic-Flags einbeziehen (z.B. Headings sind bold per Style)
        const enrichedRuns = _enrichFormatRuns(lineFormatRuns)

        // Metriken passend zur heightQ der Zeile: bei ≤4QL (Note/Info) der
        // System-Font-Cache, sonst Body-Cache. Muss zum Render-Pfad passen,
        // damit Cursor/Selection/Hit-Test pixelgenau sitzen.
        const lineHeightQ = line.heightQ
        const { cache: lineCache, scaleFactor: lineScaleFactor } = this._metricsForHeightQ(lineHeightQ)
        const rawLayout = computeStringLayout(lineText, lineCache, lineCache.baseStep, enrichedRuns)
        // Mono-Spacing fuer Inline-Code-Spans (Style.EDITOR_CODE / formatRun.mono)
        const monoCharWidth = this.renderer.charWidth * (lineHeightQ || 4) / 4
        map = buildLinePositionMap(lineText, enrichedRuns, lineCache, lineScaleFactor, rawLayout, monoCharWidth)
        map._rawLayout = rawLayout  // Layout für Renderer wiederverwenden
        mapCache.set(lineIndex, map)
        return map
    }

    /**
     * Gibt das per-Paragraph gecachte StyledChars-Array zurueck.
     * Baut StyledChars direkt aus FormatSpan[]/LinkEntry[] (Rich-Text).
     * @param {Map} cacheMap - Cache-Map (_textParseCache oder _noteParseCache)
     * @param {number} paraId - Paragraph-ID
     * @param {string} text - Vollstaendiger Paragraph-Text
     * @param {string} paraType - Paragraph-Typ
     * @param {string} field - 'text', 'note' oder 'info'
     * @returns {Array<{char: string, style: string}>}
     */
    _getParsedRichText(cacheMap, paraId, text, paraType, field = 'text') {
        const para = this.model.getParagraph(paraId)
        if (!para) return []

        const hasLinks = para.links[field] && para.links[field].length > 0
        const linkVersion = hasLinks ? (this._separatorMapVersion || 0) : 0

        // Cache-Identitaet auch ueber formats/links-Array-Referenz pruefen.
        // Grund: setParagraphsRT (Remote-Sync) baut alle Paragraphen frisch via
        // new Paragraph(...) — _gen=0 per Konstruktor. Wenn nur formats/links
        // sich aendern (Text identisch), waeren text/paraType/_gen/linkVersion
        // identisch zum Cache-Eintrag des vorherigen Loads → Cache-HIT mit
        // veralteten StyledChars. Format-Mutationen weisen `formats[field]`
        // immer als neues Array zu (extendOrCreateSpan/removeFormatFromRange/
        // deserializeParagraphRT), Reference-Equality reicht.
        const cached = cacheMap.get(paraId)
        if (cached && cached.text === text && cached.paraType === paraType
            && cached._gen === para._gen && cached.linkVersion === linkVersion
            && cached.formatsRef === para.formats[field]
            && cached.linksRef === para.links[field]) {
            return cached.parsed
        }

        // SeparatorMap für Link-Label-Auflösung
        let sepMapForLinks = null
        if (hasLinks && this._separatorMap) {
            sepMapForLinks = new Map()
            for (const link of para.links[field]) {
                const { label, isBroken } = this.model.resolveLinkLabel(link.targetId, this._separatorMap)
                sepMapForLinks.set(link.targetId, { label, isBroken })
            }
        }

        // Note/Info-Felder: immer EDITOR_TEXT als Base-Style (unabhängig vom Absatztyp),
        // da Heading-Bold nur für das text-Feld gilt
        const baseStyle = (field === 'note' || field === 'info')
            ? Style.EDITOR_TEXT
            : this._getBaseStyle(paraType, para)
        // urlStyle: Web-URLs im Editor als klickbare Links stylen (alle 3 Felder)
        const parsed = buildStyledChars(text, para.formats[field], para.links[field], baseStyle, sepMapForLinks, Style.EDITOR_LINK)

        // Mapping: nur bei Links nötig
        const mapping = hasLinks
            ? buildRichTextMapping(text, para.links[field], sepMapForLinks)
            : null

        cacheMap.set(paraId, {
            text, paraType, parsed, mapping,
            _gen: para._gen, linkVersion,
            formatsRef: para.formats[field],
            linksRef: para.links[field]
        })
        return parsed
    }

    /**
     * Gibt den Base-Style für einen Paragraph-Typ zurück.
     * Im RT-Modus wird der konkrete Heading-Level genutzt.
     * @param {string} paraType
     * @param {object} [para] - Paragraph-Objekt (für RT-Heading-Level)
     */
    _getBaseStyle(paraType, para) {
        if (para) para = this._effectiveParaForRender(para)
        // Code-Block-Absatz: alle Zeichen mono + Code-Farbe (analog zu Inline-
        // Code-Spans, nur eben paragraph-weit). Greift VOR heading/blockquote,
        // da im Code-Para keine anderen Block-Formate koexistieren können.
        if (para && para.codeBlock) return Style.EDITOR_CODE
        if (paraType === 'heading') {
            if (para && para.headingLevel > 0 && para.headingLevel <= 6) {
                return Style['EDITOR_HEADING_' + para.headingLevel]
            }
            return Style.EDITOR_HEADING_1
        }
        if (paraType === 'blockquote') return Style.EDITOR_BLOCKQUOTE
        return Style.EDITOR_TEXT
    }

    /**
     * Ermittelt die Bullet-Position (Data-Offset des Markers) für visuelle Darstellung.
     * @param {string} text - Paragraph-Text
     * @returns {number} Indent-Spaces vor dem Marker, oder -1 wenn kein Bullet
     */
    _getBulletMarkerOffset(text) {
        const match = text.match(/^(\s*)([-*+]) /)
        return match ? match[1].length : -1
    }

    /**
     * Findet Wortgrenzen im Display-Text (WYSIWYG) und gibt Data-Offsets zurück.
     * Nutzt Display-Mapping, damit unsichtbare Marker die Wort-Erkennung nicht verfälschen.
     *
     * @param {number} paragraphId
     * @param {number} dataOffset - Position im Data-Text
     * @param {string} field - 'text' oder 'note'
     * @returns {{start: number, end: number}|null} Data-Offsets oder null
     */
    getDisplayWordBounds(paragraphId, dataOffset, field = 'text') {
        const para = this.model.getParagraph(paragraphId)
        if (!para) return null
        const text = field === 'info' ? para.info : field === 'note' ? para.note : para.text
        if (!text) return null
        const mapping = this.getDisplayMapping(paragraphId, field)

        // Ohne Mapping (RT-Modus ohne Links): direkt auf dem Text arbeiten
        if (!mapping) {
            let off = dataOffset
            if (off >= text.length || !isWordChar(text[off])) {
                if (off > 0 && isWordChar(text[off - 1])) off--
                else return null
            }
            let start = off
            while (start > 0 && isWordChar(text[start - 1])) start--
            let end = off
            while (end < text.length && isWordChar(text[end])) end++
            return start < end ? { start, end } : null
        }

        // Data-Offset → Display-Offset
        let displayOffset = mapping.dataToDisplay[dataOffset]

        // Wenn auf Marker: nächste sichtbare Position suchen
        if (displayOffset === undefined || displayOffset === -1) {
            // Versuche links zu finden
            for (let i = dataOffset - 1; i >= 0; i--) {
                if (mapping.dataToDisplay[i] !== -1) {
                    displayOffset = mapping.dataToDisplay[i]
                    break
                }
            }
            if (displayOffset === undefined || displayOffset === -1) return null
        }

        // Display-Text aufbauen (bei Links aus Mapping, sonst aus Data-Text)
        const { displayToData, displayLength } = mapping
        let displayText
        if (mapping.displayText) {
            displayText = mapping.displayText
        } else {
            const displayChars = []
            for (let i = 0; i < displayLength; i++) {
                displayChars.push(text[displayToData[i]])
            }
            displayText = displayChars.join('')
        }

        // Wortgrenzen im Display-Text finden
        let dOff = displayOffset
        if (dOff >= displayText.length || !isWordChar(displayText[dOff])) {
            if (dOff > 0 && isWordChar(displayText[dOff - 1])) dOff--
            else return null
        }
        let dStart = dOff
        while (dStart > 0 && isWordChar(displayText[dStart - 1])) dStart--
        let dEnd = dOff
        while (dEnd < displayLength && isWordChar(displayText[dEnd])) dEnd++

        if (dStart === dEnd) return null

        // Zurück zu Data-Offsets
        const dataStart = displayToData[dStart]
        const dataEnd = dEnd < displayLength ? displayToData[dEnd] : text.length
        return { start: dataStart, end: dataEnd }
    }

    /**
     * Wie getDisplayWordBounds, aber trailing Satzzeichen werden einbezogen.
     * Für Word-Select-Mode: "World!" selektiert inkl. "!".
     *
     * @param {number} paragraphId
     * @param {number} dataOffset
     * @param {string} field
     * @returns {{start: number, end: number}|null}
     */
    getDisplayWordBoundsWithPunctuation(paragraphId, dataOffset, field = 'text') {
        const bounds = this.getDisplayWordBounds(paragraphId, dataOffset, field)
        if (!bounds) return null

        const para = this.model.getParagraph(paragraphId)
        const text = field === 'info' ? para.info : field === 'note' ? para.note : para.text
        const mapping = this.getDisplayMapping(paragraphId, field)

        // Trailing Satzzeichen einbeziehen (nur sichtbare Positionen)
        let end = bounds.end
        const isPunct = (ch) => /[.,!?;:'")\]\-]/.test(ch)
        if (mapping) {
            while (end < text.length && mapping.dataToDisplay[end] !== -1 && isPunct(text[end])) {
                end++
            }
        } else {
            // Ohne Mapping (RT-Modus ohne Links): direkt auf Text
            while (end < text.length && isPunct(text[end])) {
                end++
            }
        }

        return { start: bounds.start, end }
    }

    /**
     * Gibt das gecachte Display-Mapping für einen Paragraph/Feld zurück.
     * @param {number} paragraphId
     * @param {string} field - 'text', 'note' oder 'info'
     * @returns {{dataToDisplay: Int16Array, displayToData: number[], displayLength: number, markerPositions: Set<number>}|null}
     */
    getDisplayMapping(paragraphId, field = 'text') {
        const para = this.model.getParagraph(paragraphId)
        if (!para) return null

        // Mapping nur bei Links nötig
        const hasLinks = para.links[field] && para.links[field].length > 0
        if (!hasLinks) return null  // Ohne Links: textOffset === displayOffset

        // Aus Cache holen (via _getParsedRichText, das auch Mapping cached)
        const text = field === 'info' ? para.info
            : field === 'note' ? para.note : para.text
        const cacheMap = field === 'info' ? this._infoParseCache
            : field === 'note' ? this._noteParseCache : this._textParseCache
        const cached = cacheMap.get(paragraphId)
        const linkVersion = this._separatorMapVersion || 0
        if (cached && cached.text === text && cached._gen === para._gen
            && cached.mapping && cached.linkVersion === linkVersion
            && cached.linksRef === para.links[field]) {
            return cached.mapping
        }
        // Cache-Miss: _getParsedRichText aufrufen (füllt auch Mapping)
        const paraType = this._getParaType(para)
        this._getParsedRichText(cacheMap, paragraphId, text, paraType, field)
        const freshCached = cacheMap.get(paragraphId)
        return freshCached ? freshCached.mapping : null
    }

    /**
     * Entfernt Cache-Einträge für gelöschte Paragraphs.
     * @param {Map} cacheMap
     */
    _pruneParseCache(cacheMap) {
        if (cacheMap.size === 0) return
        for (const paraId of cacheMap.keys()) {
            if (!this.model.getParagraph(paraId)) {
                cacheMap.delete(paraId)
            }
        }
    }

    // ===== Rendering =====

    /**
     * Rendert den Editor-Inhalt (nur Text, kein Cursor).
     * Der Cursor wird vom Framework-Renderer gezeichnet.
     *
     * @param {number} offsetX - X-Offset der Pane
     * @param {number} offsetY - Y-Offset der Pane
     * @param {Function} setCharFn - Funktion zum Setzen eines Zeichens (x, y, char, style)
     */
    render(offsetX, offsetY, setCharFn) {
        // Differenziertes Rebuild basierend auf model._dirtyLevel (NICHT view.isDirty!)
        // view.isDirty bedeutet nur "braucht Repaint" (z.B. Viewport verschoben),
        // NICHT "Lines müssen neu berechnet werden"
        const modelDirty = this.model._dirtyLevel
        if (modelDirty === 'STRUCTURAL' && this._linesVersion !== this.model._dirtyVersion) {
            this._rebuildLines()
        } else if (modelDirty === 'PARAGRAPH' && this._linesVersion !== this.model._dirtyVersion) {
            for (const paraId of this.model._dirtyParagraphIds) {
                this.updateParagraph(paraId)
            }
            this._linesVersion = this.model._dirtyVersion
        }

        // LineDelta-Flag zurücksetzen (Full-Render hat alles neu gezeichnet)
        this._hadLineDelta = false

        // Anstehende Spell-Repaints sind mit dem Full-Render abgedeckt —
        // _renderLine liest die Warnings live.
        this._spellRepaintParas.clear()

        // Selection-Cache einmalig pro Render-Zyklus berechnen
        this._selectionCache = this._buildSelectionCache()

        // Cursor-Sichtbarkeit wird von EditorInput gesteuert (nicht bei Maus-Scroll)

        // Sichtbare Zeilen rendern
        if (this._useQuarterSpacing() && this.renderer) {
            // Proportional mit Quarter-Spacing: fraktionale Y-Positionen
            const viewportEndPx = this.height * this.renderer.charHeight
            const quarterH = this.renderer.charHeight / 4
            for (let i = this.viewportStart; i < this.lines.length; i++) {
                const pxY = this._lineScreenPixelY(i)
                if (pxY >= viewportEndPx) break
                // Sichtbarkeitsprüfung mit variabler Zeilenhöhe
                const lineH = (this.lines[i].heightQ || 4) * quarterH
                if (pxY + lineH <= 0) continue
                const fractionalY = pxY / this.renderer.charHeight
                this._renderLine(offsetX, offsetY + fractionalY, i, setCharFn)
            }
        } else {
            // Monospace: ganzzahlige Y-Positionen (wie bisher)
            for (let y = 0; y < this.height; y++) {
                const lineIndex = this.viewportStart + y

                if (lineIndex < this.lines.length) {
                    this._renderLine(offsetX, offsetY + y, lineIndex, setCharFn)
                } else {
                    this._renderEmptyLine(offsetX, offsetY + y, setCharFn)
                }
            }
        }

        // Scroll-Delta zurücksetzen nach dem Render — Full-Render zeichnet aus
        // der fraktionalen Position, daher ist Canvas == Modell, kein Residual nötig
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
        this._shiftResidual = 0

        // Active-Row-Background ist nun fuer den aktuellen Cursor-Absatz gemalt
        this._lastRenderedActiveParagraphId = this.model.cursor ? this.model.cursor.paragraphId : null

        // Scrollbar wird extern gerendert (main.js Render-Loop)
    }

    /**
     * Gibt den Scroll-Delta zurück und prüft ob Scroll-Shift möglich ist.
     * Scroll-Shift ist nur möglich wenn:
     * - Kein Rebuild nötig (nur Scroll, keine Textänderung)
     * - Delta ist kleiner als die Viewport-Höhe
     *
     * @returns {{delta: number, canShift: boolean}}
     */
    getScrollShiftInfo() {
        const delta = this._scrollDelta || 0
        // Scroll-Shift nur möglich wenn Lines unverändert sind (kein Text-Rebuild nötig)
        // STRUCTURAL/PARAGRAPH = Lines haben sich geändert → kein Shift
        // CURSOR_ONLY + Selection = Selection-Grenzen haben sich geändert → Shifted Zeilen
        //   hätten falsches Highlighting → kein Shift (Reines Scrolling bleibt CLEAN)
        const needsLineRebuild = this.model._dirtyLevel === 'STRUCTURAL' || this.model._dirtyLevel === 'PARAGRAPH'
        const selectionChanged = this.model._dirtyLevel === 'CURSOR_ONLY' && this.model.selection
        // Cursor wandert ueber Absatz-Grenzen → Active-Row-Background-Streifen
        // muss umgemalt werden. Im Scroll-Shift wuerde der alte Streifen
        // mitgeshiftet und ein neuer dazukommen → Treppen-Artefakt beim
        // Pfeiltaste-Halten. Nur bei CURSOR_ONLY pruefen — bei CLEAN ist
        // alles vorher gerendert worden, _lastRenderedActiveParagraphId stimmt.
        const currentParaId = this.model.cursor ? this.model.cursor.paragraphId : null
        const paragraphChanged = this.model._dirtyLevel === 'CURSOR_ONLY'
            && currentParaId !== this._lastRenderedActiveParagraphId

        if (this.renderer) {
            // Pixel-basierter Shift mit Sub-Pixel-Residual — für Proportional UND Monospace.
            // shiftPaneContent rundet auf ganze Pixel — wir geben hier schon den
            // gerundeten Wert raus und tragen den Bruchteil über _shiftResidual
            // in den nächsten Frame. Sonst läuft die Canvas-Bild-Position gegen
            // die logische Offset-Position aus und es wackelt sichtbar.
            const exactDelta = (this._scrollPixelDelta || 0) + (this._shiftResidual || 0)
            const pixelDelta = Math.round(exactDelta)
            const viewportPx = this.height * this.renderer.charHeight
            // canShift auch bei pixelDelta === 0 erlauben, solange ein Sub-Pixel-Delta
            // vorliegt: sonst fällt ein "0.4-px-Frame" auf den Full-Render-Pfad — teuer,
            // und in langsamen Frames genau beim Separator-Durchgang (Border-Rerender +
            // Title-Wechsel addieren sich zum Full-Render) kann das Frame-Budget reißen,
            // der Browser droppt das nächste RAF, Wheel-Events stauen sich, und im
            // folgenden Frame gibt's einen Jump ("Distanz auslassen, dann nachholen").
            // Mit sp=0 returnt shiftPaneContent früh, renderPartial hat einen No-Op-Zweig
            // für Sub-Pixel-Scrolls, und _shiftResidual trägt den Rest ins nächste Frame.
            const hasAnyDelta = exactDelta !== 0 || (this._scrollPixelDelta || 0) !== 0
            const canShift = !needsLineRebuild && !selectionChanged && !paragraphChanged
                && hasAnyDelta && Math.abs(pixelDelta) < viewportPx

            // Debug: Grund für !canShift. Wird vom ScrollProfiler im 'full'-Pfad geloggt,
            // um zu unterscheiden zwischen "echtem" Full-Render (Text-Änderung) und
            // Scroll-Überlauf-Fallback (Delta > Viewport) oder unnötigem Full-Render
            // (isDirty=true, aber actualPixelDelta=0 am Rand geklemmt).
            let noShiftReason = ''
            if (!canShift) {
                if (needsLineRebuild) noShiftReason = 'rebuild'
                else if (selectionChanged) noShiftReason = 'selection'
                else if (paragraphChanged) noShiftReason = 'paragraphChanged'
                else if (!hasAnyDelta) noShiftReason = 'noDelta'
                else if (Math.abs(pixelDelta) >= viewportPx) noShiftReason = `delta>=vp(${pixelDelta}vs${viewportPx.toFixed(0)})`
                else noShiftReason = 'unknown'
            }
            return { delta, canShift, shiftPixels: pixelDelta, isQuarterSpacing: this._useQuarterSpacing(), exactDelta, noShiftReason }
        }

        // Fallback ohne Renderer (Tests)
        const canShift = !needsLineRebuild && !selectionChanged && !paragraphChanged && delta !== 0 && Math.abs(delta) < this.height
        return { delta, canShift, shiftPixels: 0, isQuarterSpacing: false, noShiftReason: '' }
    }

    /**
     * Rendert nur die neu sichtbar gewordenen Zeilen nach einem Scroll-Shift.
     *
     * @param {number} offsetX - Pane X-Offset
     * @param {number} offsetY - Pane Y-Offset
     * @param {number} scrollDelta - Scroll-Delta (positiv = nach unten)
     * @param {Function} setCharFn - Render-Callback
     */
    renderPartial(offsetX, offsetY, scrollDelta, setCharFn, shiftPixels = 0) {
        // Rebuild NICHT aufrufen bei partiellem Render (Zeilen sind noch gültig)

        // Sub-Pixel-only-Frame (Proportional): Scroll-Delta < 0.5 px, gerundet 0.
        // Canvas bleibt unverändert, _shiftResidual trägt den Rest ins nächste Frame.
        // So vermeiden wir den teuren Full-Render-Fallback bei jedem Sub-Pixel-Frame
        // (der am Separator-Durchgang das Frame-Budget reißen und RAF-Drops erzeugen kann).
        if (this.renderer && shiftPixels === 0 && this._useQuarterSpacing()) {
            const exact = (this._scrollPixelDelta || 0) + (this._shiftResidual || 0)
            const rounded = Math.round(exact)
            this._shiftResidual = exact - rounded
            this._scrollDelta = 0
            this._scrollPixelDelta = 0
            return
        }

        // Selection-Cache aktualisieren
        this._selectionCache = this._buildSelectionCache()

        if (this.renderer && shiftPixels !== 0) {
            // Pixel-basierter Pfad — für Proportional UND Monospace (Sub-Pixel-Scroll).
            // Zeilen rendern die im exponierten Pixelbereich liegen.
            const useQuarter = this._useQuarterSpacing() && this._lineQuarterY.length > 0
            const viewportPx = this.height * this.renderer.charHeight
            const absShift = Math.abs(shiftPixels)
            const ch = this.renderer.charHeight

            // Exponierter Bereich (in Screen-Pixeln relativ zum Pane)
            const exposedStart = shiftPixels > 0 ? viewportPx - absShift : 0
            const exposedEnd = shiftPixels > 0 ? viewportPx : absShift

            // Pixel-Koordinaten des Pane-Ursprungs (für fillRect)
            const panePixelX = offsetX * this.renderer.charWidth
            const panePixelY = offsetY * ch
            const panePixelW = this.mainPaneWidth * this.renderer.charWidth

            const quarterH = ch / 4
            for (let i = this.viewportStart; i < this.lines.length; i++) {
                const pxY = this._lineScreenPixelY(i)
                if (pxY >= viewportPx) break
                // Clear-/Sichtbarkeits-Höhe = echte Grid-Zellhöhe, NICHT die nominelle
                // heightQ. _paneCellHeightQ ist auf den nächsten Zeilen-Top gedeckelt
                // (min(next, slotBottom) − top): eine Padding-Zeile ist 0, eine Content-
                // Zelle reicht exakt bis zur nächsten Zeile, nie darüber. Mit heightQ hätte
                // eine Padding-Zeile (collabiert auf slotBottom, echte Höhe 0) ihre volle
                // nominelle Höhe gecleart und so die schon korrekte Folgezeile nach einem
                // langen Note-Padding-Block ausgelöscht. Outline/Context nutzen längst
                // _paneCellHeightQ — Main zog hier nach.
                const lineH = useQuarter ? (this._paneCellHeightQ('text', i) * quarterH) : ch
                if (pxY + lineH <= 0) continue

                // Zeile liegt (teilweise) im exponierten Bereich?
                if (pxY + lineH > exposedStart && pxY < exposedEnd) {
                    // Hintergrund der gesamten Zeile clearen: Bei Sub-Pixel-Scroll
                    // kann eine Zeile die Grenze zwischen geshiftetem und geleertem
                    // Bereich überspannen — transparente Glyph-Bereiche würden sonst
                    // den alten geshifteten Inhalt durchscheinen lassen (1px-Artefakte)
                    const clearTop = Math.max(0, pxY)
                    const clearBottom = Math.min(viewportPx, pxY + lineH)
                    this.renderer.ctx.fillStyle = this.renderer.colors.background
                    this.renderer.ctx.fillRect(
                        panePixelX, panePixelY + clearTop,
                        panePixelW, clearBottom - clearTop
                    )

                    const fractionalY = pxY / ch
                    this._renderLine(offsetX, offsetY + fractionalY, i, setCharFn)
                }
            }
        } else if (scrollDelta > 0) {
            // Monospace: Nach unten gescrollt — neue Zeilen am unteren Rand
            const startY = this.height - scrollDelta
            for (let y = startY; y < this.height; y++) {
                const lineIndex = this.viewportStart + y
                if (lineIndex < this.lines.length) {
                    this._renderLine(offsetX, offsetY + y, lineIndex, setCharFn)
                } else {
                    this._renderEmptyLine(offsetX, offsetY + y, setCharFn)
                }
            }
        } else {
            // Monospace: Nach oben gescrollt — neue Zeilen am oberen Rand
            const count = Math.abs(scrollDelta)
            for (let y = 0; y < count; y++) {
                const lineIndex = this.viewportStart + y
                if (lineIndex < this.lines.length) {
                    this._renderLine(offsetX, offsetY + y, lineIndex, setCharFn)
                } else {
                    this._renderEmptyLine(offsetX, offsetY + y, setCharFn)
                }
            }
        }

        // Scroll-Delta zurücksetzen, Sub-Pixel-Restbetrag in den nächsten Frame tragen
        const exact = (this._scrollPixelDelta || 0) + (this._shiftResidual || 0)
        const rounded = Math.round(exact)
        this._shiftResidual = exact - rounded
        this._scrollDelta = 0
        this._scrollPixelDelta = 0
    }

    /**
     * Rendert nur die Zeilen von dirty Paragraphs (Paragraph-Partial-Pfad).
     * Voraussetzung: model._dirtyLevel === 'PARAGRAPH' und kein Scroll.
     *
     * @param {number} offsetX - Pane X-Offset
     * @param {number} offsetY - Pane Y-Offset
     * @param {Function} setCharFn - Render-Callback
     * @param {Function} clearLineFn - Funktion (charX, charY, charWidth) zum Löschen einer Zeile
     * @returns {Array|null} Array von dirty Ranges bei Erfolg, null bei nötigem Fallback
     */
    tryRenderDirtyOnly(offsetX, offsetY, setCharFn, clearLineFn) {
        if (this.model._dirtyLevel !== 'PARAGRAPH') return null

        // Zeilenanzahl hat sich geändert (z.B. durch ensureCursorVisible) → Full-Render
        if (this._hadLineDelta) return null

        // Dirty Paragraphs updaten und sichtbare Ranges sammeln
        const dirtyRanges = []

        for (const paraId of this.model._dirtyParagraphIds) {
            const info = this.updateParagraph(paraId)
            if (!info || info.lineDelta !== 0) return null  // Fallback nötig

            // Sichtbare Zeilen dieses Paragraphs bestimmen
            const startLine = info.startLineIndex
            const endLine = startLine + info.newLineCount

            // Auf Viewport clippen
            const visibleStart = Math.max(startLine, this.viewportStart)
            const visibleEnd = Math.min(endLine, this.viewportStart + this.height)

            if (visibleStart < visibleEnd) {
                dirtyRanges.push({ fromLineIndex: visibleStart, toLineIndex: visibleEnd })
            }
        }

        // Selection-Cache aufbauen
        this._selectionCache = this._buildSelectionCache()

        // Nur dirty Ranges rendern (pro Zeile: clearLine + renderLine)
        for (const range of dirtyRanges) {
            for (let lineIdx = range.fromLineIndex; lineIdx < range.toLineIndex; lineIdx++) {
                const screenY = this._lineToScreenY(lineIdx)
                const heightQ = this.lines[lineIdx]?.heightQ || 4
                clearLineFn(offsetX, offsetY + screenY, this.width, heightQ)
                this._renderLine(offsetX, offsetY + screenY, lineIdx, setCharFn)
            }
        }

        this._scrollDelta = 0
        this._scrollPixelDelta = 0
        return dirtyRanges
    }

    // ===== Spellcheck-Repaint (V3.1, System-Review 2026-06-10) =====

    /**
     * Meldet ein frisches Spellcheck-Worker-Ergebnis fuer einen Absatz an.
     * Kein Content-Change: Zeilen, Position-Caches und Spacing bleiben
     * gueltig — nur die Wavy-Underlines des Absatzes muessen neu gezeichnet
     * werden. Offscreen-Absaetze sind No-ops: der Renderer liest Warnings
     * beim naechsten regulaeren Render ohnehin live ueber
     * `_getEffectiveSpellWarnings`.
     * @param {string} paragraphId
     */
    markSpellWarningsDirty(paragraphId) {
        if (!this._spellVisibleLineRange(paragraphId)) return
        this._spellRepaintParas.add(paragraphId)
    }

    /** Stehen Spell-Repaints an? (renderLoop-Gate) */
    hasPendingSpellRepaint() {
        return this._spellRepaintParas.size > 0
    }

    /** Verwirft anstehende Spell-Repaints (z.B. vor erzwungenem Full-Render). */
    clearSpellRepaints() {
        this._spellRepaintParas.clear()
    }

    /**
     * Sichtbarer Zeilen-Bereich eines Absatzes, ueber alle drei Grids
     * gescannt: bei Co-Location (v4 Spec §3.2) gehoeren noteLines-Zeilen
     * dem Outline-Item, waehrend lines[] dem Host-Absatz gehoert — der
     * Scan per ODER deckt beide Faelle ab.
     * @param {string} paragraphId
     * @returns {{start: number, end: number}|null} end exklusiv; null wenn
     *          der Absatz nicht gelayoutet oder ausserhalb des Viewports ist
     */
    _spellVisibleLineRange(paragraphId) {
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return null
        let end = startIdx
        while (end < this.lines.length) {
            const owns = (this.lines[end] && this.lines[end].paragraphId === paragraphId)
                || (this.noteLines[end] && this.noteLines[end].paragraphId === paragraphId)
                || (this.infoLines[end] && this.infoLines[end].paragraphId === paragraphId)
            if (!owns) break
            end++
        }
        const visibleStart = Math.max(startIdx, this.viewportStart)
        const visibleEnd = Math.min(end, this.viewportStart + this.height)
        if (visibleStart >= visibleEnd) return null
        return { start: visibleStart, end: visibleEnd }
    }

    /**
     * Zeichnet die vorgemerkten Spell-Repaint-Absaetze neu (Main-Grid;
     * Outline/Context laufen im renderLoop ueber render*DirtyRanges mit
     * denselben Zeilen-Indizes). Analog zu tryRenderDirtyOnly, aber OHNE
     * updateParagraph/_computeSpacing/_clampViewport — der Text hat sich
     * nicht geaendert, nur die Warnings.
     *
     * @param {number} offsetX - Pane X-Offset
     * @param {number} offsetY - Pane Y-Offset
     * @param {Function} setCharFn - Render-Callback
     * @param {Function} clearLineFn - Funktion (charX, charY, charWidth, heightQ)
     * @returns {Array|null} dirty Ranges oder null wenn nichts sichtbar war
     */
    trySpellRepaint(offsetX, offsetY, setCharFn, clearLineFn) {
        if (this._spellRepaintParas.size === 0) return null

        // Ranges zum Render-Zeitpunkt frisch berechnen — zwischen Vormerken
        // und Rendern kann gescrollt worden sein.
        const dirtyRanges = []
        for (const paraId of this._spellRepaintParas) {
            const range = this._spellVisibleLineRange(paraId)
            if (range) dirtyRanges.push({ fromLineIndex: range.start, toLineIndex: range.end })
        }
        this._spellRepaintParas.clear()
        if (dirtyRanges.length === 0) return null

        this._selectionCache = this._buildSelectionCache()

        for (const range of dirtyRanges) {
            for (let lineIdx = range.fromLineIndex; lineIdx < range.toLineIndex; lineIdx++) {
                const screenY = this._lineToScreenY(lineIdx)
                const heightQ = this.lines[lineIdx]?.heightQ || 4
                clearLineFn(offsetX, offsetY + screenY, this.width, heightQ)
                this._renderLine(offsetX, offsetY + screenY, lineIdx, setCharFn)
            }
        }
        return dirtyRanges
    }

    /**
     * Gibt die Cursor-Position relativ zur Pane zurück.
     * Wird vom Framework verwendet um den Pane-Cursor zu aktualisieren.
     *
     * @returns {{x: number, y: number}|null}
     */
    getCursorPosition() {
        if (!this.model.cursor) return null

        const screenPos = this.dataToScreen(
            this.model.cursor.paragraphId,
            this.model.cursor.offset
        )

        return screenPos
    }

    // ===== Delta-Review Hint-API =====
    // Der ReviewController setzt/loescht ueber diese API den Review-Zustand
    // im View. Minimal gehalten — keine Domain-Logik hier drin.

    /**
     * Aktiviert den Review-Render-Modus.
     *
     * Pro Overlay-Kind wird einmalig der Render-Cache aufgebaut:
     *   rewrite → Wort-Diff(Original, Vorschlag) → Composite + Ranges
     *   delete  → keine Vorberechnung, nur Marker im Cache
     *   insert  → fliegt in eine separate Liste (virtuelle Zeilen)
     *
     * @param {{dimAllExcept: Set<paragraphId>, overlays: object[]}} hint
     */
    setReviewHint(hint) {
        if (!hint) {
            this._reviewHint = null
            this._reviewComposites = null
            this._reviewInserts = null
            this._rebuildLines()
            this.isDirty = true
            return
        }
        const composites = new Map()
        const inserts = []
        for (const ov of hint.overlays || []) {
            if (ov.kind === 'rewrite' && ov.anchorParagraphId != null) {
                const para = this.model.getParagraph?.(ov.anchorParagraphId) || null
                const originalFormats = para?.formats?.text || []
                const originalLinks = para?.links?.text || []
                const diff = computeReviewDiff(ov.originalText || '', ov.proposedText || '', {
                    originalLinks,
                    proposedLinks: ov.proposedLinks || [],
                    separatorMap: this._separatorMap || null
                })
                // Block-Format-Floor (Heading-Bold, Blockquote-Italic) je Seite —
                // der Composite-Renderer nutzt Diff-Akzent-Styles, die selbst
                // weder bold noch italic sind. injectBlockFloor liefert die
                // virtuellen Spans (siehe reviewDiff.js).
                const origFormatsForDiff = injectBlockFloor(
                    originalFormats, (ov.originalText || '').length,
                    para?.headingLevel || 0, !!para?.blockquote
                )
                const propFormatsForDiff = injectBlockFloor(
                    ov.proposedFormats || [], (ov.proposedText || '').length,
                    ov.proposedHeadingLevel || 0, !!ov.proposedBlockquote
                )
                // FormatRuns je Toggle-View vorberechnen (display-koordinaten),
                // damit der Composite-Renderer pro Zeile nur noch slicen muss.
                const styleFn = (kind, view) => this._styleForReviewKind(kind, view)
                const formatRunsProposed = buildCompositeFormatRuns(
                    diff, origFormatsForDiff, propFormatsForDiff, 'proposed', styleFn, STYLES
                )
                const formatRunsOriginal = buildCompositeFormatRuns(
                    diff, origFormatsForDiff, propFormatsForDiff, 'original', styleFn, STYLES
                )
                // Block-WYSIWYG braucht die Block-Felder nicht im Composite —
                // _effectiveParaForRender liest sie direkt vom Overlay. Das
                // Status-Panel-Delta entsteht parallel im ReviewController
                // (_computeBlockDeltaForPanel) als Single Source of Truth.
                composites.set(ov.anchorParagraphId, {
                    kind: 'rewrite', ...diff, overlay: ov,
                    formatRunsProposed, formatRunsOriginal
                })
            } else if (ov.kind === 'delete' && ov.anchorParagraphId != null) {
                // Delete als Diff-Stub: alles ist "remove". Damit greift der
                // gleiche Render-Pfad wie bei Rewrite — Strikethrough im
                // proposed-View, KEEP-Style (Fliesstext) im original-View
                // (siehe _styleForReviewKind).
                const para = this.model.getParagraph(ov.anchorParagraphId)
                const text = para?.text || ''
                const originalFormats = para?.formats?.text || []
                const originalLinks = para?.links?.text || []
                // Diff-Helper auch fuer Delete benutzen, damit Section-Links
                // aufgeloeste Klartext-Labels bekommen (sonst stehen die
                // ￼-Zeichen unverarbeitet im durchgestrichenen Text).
                const diff = computeReviewDiff(text, '', {
                    originalLinks,
                    proposedLinks: [],
                    separatorMap: this._separatorMap || null
                })
                // Block-Floor fuer den Original-Para — bei einem H1-Delete soll
                // der Strikethrough auf fettem Heading-Text sitzen, nicht auf
                // Plaintext.
                const origFormatsForDiff = injectBlockFloor(
                    originalFormats, text.length,
                    para?.headingLevel || 0, !!para?.blockquote
                )
                const styleFn = (kind, view) => this._styleForReviewKind(kind, view)
                const formatRunsProposed = buildCompositeFormatRuns(
                    diff, origFormatsForDiff, [], 'proposed', styleFn, STYLES
                )
                const formatRunsOriginal = buildCompositeFormatRuns(
                    diff, origFormatsForDiff, [], 'original', styleFn, STYLES
                )
                composites.set(ov.anchorParagraphId, {
                    kind: 'delete', ...diff, overlay: ov,
                    formatRunsProposed, formatRunsOriginal
                })
            } else if (ov.kind === 'insert') {
                // anchorParagraphId null/undefined/-1 ODER das implicit-head
                // Token (section_0) → Insert am Dokumentanfang. Das Overlay vom
                // ReviewController nutzt anchorParagraphId einheitlich fuer alle
                // Kinds; section_0 ist kein echter Para, wird hier wie "Anfang"
                // behandelt (der Apply schluckt ggf. den leeren Erst-Absatz).
                const anchor = ov.anchorParagraphId
                const atStart = anchor == null || anchor === -1 || anchor === IMPLICIT_HEAD_ID
                inserts.push({
                    afterParagraphId: atStart ? null : anchor,
                    overlay: ov
                })
            }
        }
        this._reviewHint = hint
        this._reviewComposites = composites
        this._reviewInserts = inserts
        // Voller Rebuild — Lines fuer Review-Paragraphen muessen neu auf dem
        // Composite umgebrochen werden.
        this._rebuildLines()
        this.isDirty = true
    }

    /** Deaktiviert den Review-Render-Modus. */
    clearReviewHint() {
        this._reviewHint = null
        this._reviewComposites = null
        this._reviewInserts = null
        this._rebuildLines()
        this.isDirty = true
    }

    /**
     * Scrollt den Main-Pane so, dass der gegebene Paragraph sichtbar wird.
     * Wird vom ReviewController beim Flush ins REVIEWING und bei jeder
     * ←/→/Tab-Navigation aufgerufen — sonst sieht der User Vorschlaege
     * ausserhalb des Viewports nicht und kann die Locked-Tastatur auch nicht
     * zur Navigation nutzen.
     *
     * Verhalten:
     *  - paragraphId === null|undefined → scrollt zum Dokumentanfang.
     *  - Para schon im "Sweet Spot" (oberes Drittel + obere Mitte) → no-op.
     *  - Sonst springt die First-Line auf rund ein Viertel von oben — gibt
     *    Platz fuer mehrzeilige Composites unterhalb. Bewusst aggressiv: beim
     *    Vorwaerts-Stepping rutschen Composites am unteren Rand sonst
     *    schnell raus, weil sie mehrere Zeilen brauchen.
     *
     * @param {number|string|null} paragraphId
     */
    scrollToParagraph(paragraphId) {
        if (this.lines.length === 0 || this.height <= 0) return
        if (paragraphId == null) {
            this.scrollTo(0)
            return
        }
        const startIdx = this._paragraphLineIndex.get(paragraphId)
        if (startIdx == null) return
        // Sweet Spot: oberes Achtel bis untere Haelfte des Viewports. Liegt
        // die First-Line dort, lassen wir sie stehen (kein Springen). Sonst
        // springen wir zum Idealoffset h/4.
        const sweetTop = this.viewportStart + Math.floor(this.height / 8)
        const sweetBottom = this.viewportStart + Math.floor(this.height / 2)
        if (startIdx >= sweetTop && startIdx <= sweetBottom) return
        const idealOffset = Math.floor(this.height / 4)
        const target = Math.max(0, startIdx - idealOffset)
        this.scrollTo(target)
    }

    /**
     * Scrollt zum aktuellen Review-Vorschlag und zentriert ihn vertikal im
     * Viewport. Insert-Vorschlaege haben keine paragraphId im Modell — die
     * Vorschau lebt als virtuelle Lines mit `_reviewInsertOverlay.toolCallId`.
     * Wir suchen den ersten/letzten Line-Index passend zur Suggestion und
     * platzieren den Block mittig; passt er nicht in den Viewport, scrollen
     * wir auf seine erste Zeile (Top-Align).
     *
     * Wird statt `scrollToParagraph` vom ReviewController gerufen, weil
     * `payload.afterParagraphId` bei Multi-Insert-Reviews fuer alle Vorschlaege
     * derselbe Anker waere — der Scroll bliebe sonst beim ersten Vorschlag
     * kleben (Bug 02.05.).
     *
     * @param {object} suggestion - {type, toolCallId, payload, ...}
     */
    scrollToReviewSuggestion(suggestion) {
        if (!suggestion || this.lines.length === 0 || this.height <= 0) return
        const range = this._findReviewSuggestionRange(suggestion)
        if (!range) return
        const blockHeight = range.endIdx - range.startIdx + 1
        let target
        if (blockHeight >= this.height) {
            // Block laenger als der Viewport → erste Zeile oben.
            target = range.startIdx
        } else {
            // Block zentrieren.
            const idealOffset = Math.floor((this.height - blockHeight) / 2)
            target = Math.max(0, range.startIdx - idealOffset)
        }
        this.scrollTo(target)
    }

    _findReviewSuggestionRange(s) {
        if (s.type === 'insert') {
            const tcid = s.toolCallId
            const targetId = tcid != null ? `__insert__${tcid}` : null
            let startIdx = -1
            let endIdx = -1
            for (let i = 0; i < this.lines.length; i++) {
                if (this.lines[i].paragraphId === targetId) {
                    if (startIdx === -1) startIdx = i
                    endIdx = i
                } else if (startIdx !== -1) {
                    break
                }
            }
            if (startIdx !== -1) return { startIdx, endIdx }
            // Fallback: virtuelle Vorschau-Line nicht (mehr) sichtbar — Anker.
            // section_0 (implicit-head) zählt wie "Dokumentanfang".
            const anchorId = s.payload?.afterParagraphId ?? null
            if (anchorId == null || anchorId === IMPLICIT_HEAD_ID) return { startIdx: 0, endIdx: 0 }
            const anchorIdx = this._paragraphLineIndex.get(anchorId)
            if (anchorIdx == null) return null
            return { startIdx: anchorIdx, endIdx: anchorIdx }
        }
        // Rewrite / Delete: Range ueber alle Lines des Ziel-Paragraphs.
        const paraId = s.payload?.paragraphId
        if (paraId == null) return null
        const startIdx = this._paragraphLineIndex.get(paraId)
        if (startIdx == null) return null
        let endIdx = startIdx
        while (endIdx + 1 < this.lines.length && this.lines[endIdx + 1].paragraphId === paraId) {
            endIdx++
        }
        return { startIdx, endIdx }
    }

    /** @returns {{kind: string, composite?: string, ranges?: object[], overlay: object}|null} */
    _getReviewComposite(paragraphId) {
        return this._reviewComposites?.get(paragraphId) || null
    }

    /**
     * Fuegt fuer jeden Insert-Overlay virtuelle Lines an der richtigen Position
     * in this.lines/noteLines/infoLines ein. Wird nach der Hauptschleife in
     * _rebuildLines aufgerufen, vor allen nachfolgenden Berechnungen.
     */
    _applyReviewInserts() {
        if (!this._reviewInserts || this._reviewInserts.length === 0) return

        // Pro Anker eine Gruppe — mehrere Inserts mit demselben afterParagraphId
        // landen am gleichen insertAt und MUESSEN als ein zusammenhaengender
        // Block in Quell-Reihenfolge eingefuegt werden. Einzeln nacheinander zu
        // splicen kehrt die Reihenfolge um, weil jeder splice den vorigen Insert
        // ein Stueck nach hinten schiebt.
        const groups = new Map()  // insertAt → { lines:[], notes:[], infos:[] }
        for (const ins of this._reviewInserts) {
            let insertAt
            if (ins.afterParagraphId == null) {
                insertAt = 0
            } else {
                const startIdx = this._paragraphLineIndex.get(ins.afterParagraphId)
                if (startIdx == null) continue   // Anchor nicht sichtbar (Filter/Collapse)
                let endIdx = startIdx
                while (endIdx < this.lines.length && this.lines[endIdx].paragraphId === ins.afterParagraphId) {
                    endIdx++
                }
                insertAt = endIdx
            }
            const insertLines = this._buildReviewInsertLines(ins.overlay)
            const noteLines = insertLines.map(ln => this._makePaddingLine(ln.paragraphId, 'note'))
            const infoLines = insertLines.map(ln => this._makePaddingLine(ln.paragraphId, 'info'))
            let group = groups.get(insertAt)
            if (!group) {
                group = { lines: [], notes: [], infos: [] }
                groups.set(insertAt, group)
            }
            group.lines.push(...insertLines)
            group.notes.push(...noteLines)
            group.infos.push(...infoLines)
        }

        // Gruppen von hinten nach vorne splicen, damit die insertAt-Indizes der
        // weiter vorne liegenden Gruppen stabil bleiben.
        const sortedGroups = [...groups.entries()].sort((a, b) => b[0] - a[0])
        for (const [insertAt, group] of sortedGroups) {
            this.lines.splice(insertAt, 0, ...group.lines)
            this.noteLines.splice(insertAt, 0, ...group.notes)
            this.infoLines.splice(insertAt, 0, ...group.infos)
        }

        // _paragraphLineIndex neu aufbauen — die echten Paragraphen haben sich
        // verschoben, virtuelle Insert-Lines stehen mit Fake-IDs drin und
        // brauchen den Index nicht.
        this._paragraphLineIndex.clear()
        for (let i = 0; i < this.lines.length; i++) {
            const pid = this.lines[i].paragraphId
            if (typeof pid === 'string' && pid.startsWith('__insert__')) continue
            if (!this._paragraphLineIndex.has(pid)) {
                this._paragraphLineIndex.set(pid, i)
            }
        }
    }

    /**
     * Erzeugt virtuelle ScreenLines aus dem newText eines Insert-Overlays.
     * Die Lines tragen einen Sonder-Flag `_reviewInsertOverlay`, damit
     * _renderLine sie als Insert-Vorschau rendert.
     *
     * Format-Daten und Block-WYSIWYG (Block-Felder vom Overlay) werden hier
     * vorberechnet und auf die Lines gehaengt:
     *  - `_insertDiff`: computeReviewDiff('', newText) liefert displayProposed
     *    mit aufgeloesten Section-Link-Labels — auf den wir wrappen, damit
     *    der User Klartext sieht statt `[[@id]]`-Marker.
     *  - `_insertFormatRuns{Proposed,Original}`: vorberechnete FormatRuns je
     *    Toggle-View, mit Inline-Bold/Italic kombiniert mit dem Diff-Style.
     *  - heightQ folgt activeView: bei proposed mit Heading-Level → groessere
     *    Schrift; bei original (Insert ist "weg") immer NORMAL.
     */
    _buildReviewInsertLines(overlay) {
        const newText = overlay.newText || ''
        const fakeId = `__insert__${overlay.toolCallId || Math.random()}`
        const activeView = overlay.activeView || 'proposed'
        const proposedFormats = overlay.proposedFormats || []
        const proposedLinks = overlay.proposedLinks || []

        // Diff-Stub: '' vs newText → Composite ist displayProposed (mit
        // aufgeloesten Section-Labels). Damit haben wir EINE Datenstruktur
        // fuer Wrap + FormatRuns, identisch zum Composite-Pfad.
        const diff = computeReviewDiff('', newText, {
            proposedLinks,
            separatorMap: this._separatorMap || null
        })
        // Block-Floor (siehe Composite-Pfad): bei Heading-Insert Bold ueber
        // die ganze Zeile, bei Blockquote-Insert Italic. Damit der Insert-
        // Vorschlag visuell als Heading bzw. Blockquote erkennbar wird, nicht
        // nur ueber Marker und Schriftgroesse.
        const propIsHeading = (overlay.proposedHeadingLevel || 0) > 0
        const propFormatsForDiff = injectBlockFloor(
            proposedFormats, newText.length,
            overlay.proposedHeadingLevel || 0, !!overlay.proposedBlockquote
        )
        const styleFn = (kind, view) => this._styleForReviewKind(kind, view)
        const formatRunsProposed = buildCompositeFormatRuns(diff, [], propFormatsForDiff, 'proposed', styleFn, STYLES)
        const formatRunsOriginal = buildCompositeFormatRuns(diff, [], propFormatsForDiff, 'original', styleFn, STYLES)

        // Heading-Hoehe nur bei proposed-View. Original-View zeigt Plaintext-
        // Strikethrough — Heading-Schriftgroesse waere unerwartet.
        const heightQ = (activeView === 'proposed' && propIsHeading)
            ? headingHeightQ(overlay.proposedHeadingLevel)
            : LINE_HEIGHT_Q.NORMAL

        // List-/Quote-/Heading-Marker bei proposed-View. _getRichTextPrefix
        // funktioniert auf einem Pseudo-Para, der nur Block-Felder traegt —
        // der Helper liest sonst nichts. Bei activeView=original wird kein
        // Marker gerendert (Insert ist semantisch "nicht drin", Strike-Plain).
        let insertRtPrefix = ''
        let insertHangingIndent = 0
        if (activeView === 'proposed') {
            const pseudoPara = {
                id: fakeId,
                listType: overlay.proposedListType || null,
                listLevel: overlay.proposedListLevel || 0,
                headingLevel: overlay.proposedHeadingLevel || 0,
                blockquote: !!overlay.proposedBlockquote
            }
            const rt = this._getRichTextPrefix(pseudoPara)
            insertRtPrefix = rt.prefix
            insertHangingIndent = rt.hangingIndent
        }

        const wrapText = diff.composite
        // Wrap-Format-Runs aus dem Composite (proposed-View): traegt Mono-Flag
        // fuer Inline-Code, sonst rechnet der Wrap proportional und Code-Zeilen
        // umbrechen zu spaet.
        const wrapRuns = activeView === 'original' ? formatRunsOriginal : formatRunsProposed
        const lines = this.mainPaneWidth > 0
            ? this._breakText(fakeId, wrapText, this.textWidth, 'text', null, heightQ, wrapRuns)
            : [(() => {
                const ln = new ScreenLine(fakeId, 0, wrapText.length, 'text')
                ln.isFirstLine = true
                ln.isLastLine = true
                return ln
              })()]
        for (const ln of lines) {
            ln._reviewInsertOverlay = overlay
            ln._insertDiff = diff
            ln._insertFormatRunsProposed = formatRunsProposed
            ln._insertFormatRunsOriginal = formatRunsOriginal
            ln.heightQ = heightQ
        }
        // Marker auf line[0], hangingIndent auf alle Zeilen — Prop erwartet
        // hangingIndent in Pixel (Char-Anzahl × charWidth approximiert; bei
        // proportionalen Markern leicht ungenau, aber bei kurzen Insert-
        // Vorschlaegen visuell unkritisch). Mono nutzt rtPrefix.length direkt
        // als Char-Cells. Wrap-Width-Korrektur hier nicht — bei mehrzeiligen
        // List-/Quote-Inserts mit Bullet+Wrap nochmal anpacken (Followup).
        if (lines.length > 0 && insertRtPrefix) {
            const charWidth = this.renderer ? this.renderer.charWidth : 0
            const hangingPx = insertHangingIndent * charWidth
            lines[0].rtPrefix = insertRtPrefix
            for (const ln of lines) ln.hangingIndent = hangingPx
        }
        return lines
    }

    /**
     * Liefert den Style fuer einen Diff-Range-Anteil basierend auf activeView.
     *
     * Asymmetrische Darstellung:
     *  - Original-View liest sich wie unveraenderter Fliesstext (KEEP), damit
     *    der User den Ist-Zustand klar erkennt.
     *  - Vorschlag-View hebt das Neue visuell hervor (SHOWN) — solange der
     *    Vorschlag nicht akzeptiert ist, soll der "neue" Text als Vorschlag
     *    erkennbar bleiben.
     *  - Was nicht zur aktiven Variante gehoert, ist durchgestrichen.
     *
     *   common              → KEEP
     *   add  + proposed     → SHOWN       (Vorschlag visuell betont)
     *   add  + original     → STRIKE
     *   remove + proposed   → STRIKE
     *   remove + original   → KEEP        (liest sich wie Fliesstext)
     */
    _styleForReviewKind(kind, activeView) {
        if (kind === 'common') return Style.EDITOR_REVIEW_KEEP
        if (kind === 'add') {
            return activeView === 'original' ? Style.EDITOR_REVIEW_STRIKE : Style.EDITOR_REVIEW_SHOWN
        }
        // kind === 'remove'
        return activeView === 'original' ? Style.EDITOR_REVIEW_KEEP : Style.EDITOR_REVIEW_STRIKE
    }

    /**
     * Zeichnet den dezenten Active-Row-Background fuer den aktiven Absatz im
     * Main-Pane. Pro sichtbare ScreenLine ein fillRect ueber die volle
     * Pane-Breite. Farbe stammt aus mode.activeRowBackground (deklarativ).
     * Wird am Anfang von _renderLineBody gerufen, vor allen Glyphen.
     *
     * @param {number} x          Pane-Origin in Char-Spalten (Pane-relativ 0)
     * @param {number} y          Y-Position der Zeile in Char-Zeilen (kann fraktional sein)
     * @param {ScreenLine} line   Die aktuelle ScreenLine
     */
    /**
     * Active-Row-Background-Farbe für eine Zeile, oder null wenn die Zeile nicht
     * aktiv ist. Aktiv = Cursor im Main-Absatz dieser Zeile ODER in der co-located
     * Note (Outline-Item), die sich die Zeile mit dem Text-Para teilt (Spec §3.2).
     * Geteilt von _fillActiveRowBg und dem Ellipsis-Überpinseln — letzteres würde
     * sonst mit der normalen Background-Farbe ein Loch ins Active-Highlight stanzen.
     * @returns {string|null}
     */
    _activeRowBgColor(line, lineIndex) {
        if (!this.renderer || line.isPadding) return null
        const cursor = this.model && this.model.cursor
        if (!cursor) return null
        const noteLine = lineIndex != null ? this.noteLines[lineIndex] : null
        const active = cursor.paragraphId === line.paragraphId
            || (noteLine && cursor.paragraphId === noteLine.paragraphId)
        if (!active) return null
        const colors = this.renderer.colors
        return (colors && colors.activeRowBackground) || null
    }

    _fillActiveRowBg(x, y, line, lineIndex) {
        const bg = this._activeRowBgColor(line, lineIndex)
        if (!bg) return
        const charWidth = this.renderer.charWidth
        const charHeight = this.renderer.charHeight
        const heightQ = line.heightQ || 4
        const lineHeightPx = (this.renderer.isProportional() && this._useQuarterSpacing())
            ? heightQ * (charHeight / 4)
            : charHeight
        const ctx = this.renderer.ctx
        ctx.fillStyle = bg
        ctx.fillRect(x * charWidth, y * charHeight, this.width * charWidth, lineHeightPx)
    }

    /**
     * Zeichnet den dezenten Active-Hintergrund unter einer Composite- oder
     * Insert-Zeile. Genutzt im Proportional-Pfad (Mono geht ueber den
     * `highlightType='match'`-Parameter von setCharFn). Farbe = SEARCH_MATCH-
     * Background — derselbe Look wie nicht-aktive Cmd-F-Treffer.
     *
     * Y-Position und Hoehe folgen der gleichen Formel wie die normale
     * Text-Selection (siehe _renderLineProportional: selPixelY +
     * textHeightForHeightQ), damit der Highlight nicht "hochrutscht".
     */
    _fillReviewActiveBg(x, y, charWidth, charHeight, heightQ) {
        if (!this.renderer) return
        const bgColor = this.renderer._resolveSearchHighlightColor('match')
        if (!bgColor) return
        const renderer = this.renderer
        const startPx = x * charWidth
        const widthPx = (this.markerWidth + this.textWidth + 1) * charWidth
        const hq = heightQ || 4
        const selPixelY = y * charHeight + renderer._verticalPad * hq / 4
        const localTextHeight = renderer.textHeightForHeightQ(hq)
        const ctx = renderer.ctx
        ctx.save()
        ctx.fillStyle = bgColor
        ctx.fillRect(startPx, selPixelY, widthPx, localTextHeight)
        ctx.restore()
    }

    /**
     * Rendert eine Zeile, deren Paragraph einen aktiven Rewrite-Vorschlag hat.
     * Statt para.text wird der Composite (Original + Vorschlag inline-gemerged)
     * gerendert; pro Zeichen/Range wird der Style basierend auf der Diff-Range
     * + activeView gewaehlt — Toggle aendert nur Kontrast/Strikethrough, nicht
     * das Layout (Word-Wrap ist auf dem Composite gelaufen, also stabil).
     *
     * Verzweigt zwischen Monospace (setCharFn pro Zeichen) und Proportional
     * (eine renderProportionalLine-Anweisung mit Diff-FormatRuns).
     */
    _renderReviewCompositeLine(x, y, line, setCharFn) {
        const data = line._reviewComposite
        const overlay = data.overlay
        const activeView = overlay.activeView || 'proposed'
        const isActive = !!overlay.isActive

        if (this.renderer && this.renderer.isProportional()) {
            this._renderReviewCompositeLineProportional(x, y, line, data, activeView, isActive, setCharFn)
        } else {
            this._renderReviewCompositeLineMonospace(x, y, line, data, activeView, isActive, setCharFn)
        }
    }

    /** Monospace-Variante (Char-Grid via setCharFn). */
    _renderReviewCompositeLineMonospace(x, y, line, data, activeView, isActive, setCharFn) {
        const composite = data.composite

        const start = line.startOffset
        const end = Math.min(line.endOffset, composite.length)

        // Aktiver Vorschlag: dezenter Hintergrund-Highlight wie ein
        // Cmd-F-Suchtreffer (siehe Lesson 76 / Style.SEARCH_MATCH).
        const hl = isActive ? 'match' : null

        if (line.isFirstLine) {
            setCharFn(x, y, '>', Style.EDITOR_MARKER, false, hl)
        } else {
            setCharFn(x, y, ' ', Style.EDITOR_TEXT, false, hl)
        }

        // Vorberechnete FormatRuns (Diff-Style + Inline-Format kombiniert) je
        // activeView. Im Mono-Pfad zaehlt nur der `style`-String (Bold/Italic-
        // Glyphen gibt es im Char-Grid nicht). Den Run pro Char per linearem
        // Index — Runs sind sortiert.
        const fullRuns = activeView === 'original'
            ? (data.formatRunsOriginal || [])
            : (data.formatRunsProposed || [])
        let runIdx = 0
        while (runIdx < fullRuns.length && fullRuns[runIdx].end <= start) runIdx++
        let col = x + this.markerWidth

        // List-/Blockquote-/Numbered-Marker (rtPrefix) auf der ersten Zeile
        // rendern; Continuation-Zeilen ueberspringen die gleiche Anzahl Cells
        // (hangingIndent in Char-Breite) — sonst wuerden die Folge-Zeilen
        // unter dem Marker stehen, nicht unter dem Text.
        const prefixCells = line.rtPrefix ? line.rtPrefix.length : 0
        if (line.rtPrefix && line.isFirstLine) {
            const prefixStyle = activeView === 'original'
                ? Style.EDITOR_REVIEW_KEEP
                : Style.EDITOR_REVIEW_SHOWN
            for (let p = 0; p < line.rtPrefix.length; p++) {
                setCharFn(col + p, y, line.rtPrefix[p], prefixStyle, false, hl)
            }
        } else if (prefixCells > 0) {
            // Continuation: mit Highlight-Hintergrund auffuellen, damit der
            // Active-Bg auch ueber den eingerueckten Bereich durchgezogen wird.
            for (let p = 0; p < prefixCells; p++) {
                setCharFn(col + p, y, ' ', Style.EDITOR_TEXT, false, hl)
            }
        }
        col += prefixCells

        for (let i = start; i < end; i++) {
            while (runIdx < fullRuns.length && fullRuns[runIdx].end <= i) runIdx++
            const style = fullRuns[runIdx]?.style || Style.EDITOR_REVIEW_KEEP
            setCharFn(col, y, composite[i], style, false, hl)
            col++
        }
        // Resliche Cells in der Zeile mit Highlight fuellen, damit der Bg
        // bis zum End-Marker durchgezogen wird (sonst hat der Highlight ein
        // zerrupftes rechtes Ende auf nicht ganz vollen Continuation-Zeilen).
        if (isActive) {
            const lastTextCol = x + this.markerWidth + this.textWidth
            for (let c = col; c < lastTextCol; c++) {
                setCharFn(c, y, ' ', Style.EDITOR_TEXT, false, hl)
            }
        }

        if (line.isLastLine) {
            const endX = x + this.markerWidth + this.textWidth
            setCharFn(endX, y, '<', Style.EDITOR_MARKER, false, hl)
        }
    }

    /**
     * Rendert eine virtuelle Insert-Vorschau-Zeile. Style ist activeView-
     * abhaengig — analog zur Inline-Diff-Logik:
     *   activeView = 'proposed' → SHOWN (der neue Absatz ist "drin")
     *   activeView = 'original' → STRIKE (der neue Absatz ist "nicht drin")
     * Damit ist der T-Toggle ueber Rewrite/Insert/Delete konsistent.
     */
    _renderReviewInsertLine(x, y, line, setCharFn) {
        const overlay = line._reviewInsertOverlay
        const isActive = !!overlay.isActive
        const activeView = overlay.activeView || 'proposed'
        // Composite-Text mit aufgeloesten Section-Labels (siehe _buildReviewInsertLines).
        // Fallback auf overlay.newText, falls die Insert-Line ohne _insertDiff
        // gebaut wurde (sollte nach Schritt 6 nicht mehr passieren).
        const composite = line._insertDiff?.composite ?? (overlay.newText || '')
        const fullRuns = activeView === 'original'
            ? (line._insertFormatRunsOriginal || [])
            : (line._insertFormatRunsProposed || [])

        if (this.renderer && this.renderer.isProportional()) {
            this._renderReviewInsertLineProportional(x, y, line, composite, fullRuns, isActive)
        } else {
            this._renderReviewInsertLineMonospace(x, y, line, composite, fullRuns, isActive, setCharFn)
        }
    }

    _renderReviewInsertLineMonospace(x, y, line, composite, fullRuns, isActive, setCharFn) {
        const hl = isActive ? 'match' : null
        if (line.isFirstLine) {
            setCharFn(x, y, '+', Style.EDITOR_MARKER, false, hl)
        } else {
            setCharFn(x, y, ' ', Style.EDITOR_TEXT, false, hl)
        }
        const start = line.startOffset
        const end = Math.min(line.endOffset, composite.length)
        let runIdx = 0
        while (runIdx < fullRuns.length && fullRuns[runIdx].end <= start) runIdx++
        let col = x + this.markerWidth

        // List-/Quote-Marker (rtPrefix): erste Zeile zeigt den Marker, Folge-
        // zeilen reservieren denselben Platz mit Spaces — sonst rutscht der
        // Continuation-Text unter den Marker statt unter den Text.
        const prefixCells = line.rtPrefix ? line.rtPrefix.length : 0
        if (line.rtPrefix && line.isFirstLine) {
            const prefixStyle = Style.EDITOR_REVIEW_SHOWN
            for (let p = 0; p < line.rtPrefix.length; p++) {
                setCharFn(col + p, y, line.rtPrefix[p], prefixStyle, false, hl)
            }
        } else if (prefixCells > 0) {
            for (let p = 0; p < prefixCells; p++) {
                setCharFn(col + p, y, ' ', Style.EDITOR_TEXT, false, hl)
            }
        }
        col += prefixCells

        for (let i = start; i < end; i++) {
            while (runIdx < fullRuns.length && fullRuns[runIdx].end <= i) runIdx++
            const style = fullRuns[runIdx]?.style || Style.EDITOR_REVIEW_SHOWN
            setCharFn(col, y, composite[i], style, false, hl)
            col++
        }
        if (isActive) {
            const lastTextCol = x + this.markerWidth + this.textWidth
            for (let c = col; c < lastTextCol; c++) {
                setCharFn(c, y, ' ', Style.EDITOR_TEXT, false, hl)
            }
        }
        if (line.isLastLine) {
            const endX = x + this.markerWidth + this.textWidth
            setCharFn(endX, y, '<', Style.EDITOR_MARKER, false, hl)
        }
    }

    _renderReviewInsertLineProportional(x, y, line, composite, fullRuns, isActive) {
        const renderer = this.renderer
        const heightQ = line.heightQ || LINE_HEIGHT_Q.NORMAL
        const MARKER_HQ = LINE_HEIGHT_Q.NORMAL
        const charWidth = renderer.charWidth
        const charHeight = renderer.charHeight
        const markerPx = x * charWidth + renderer.markerWidthPx()
        const pixelY = y * charHeight
        const markerBaselineShift = heightQ > MARKER_HQ
            ? (renderer._verticalPad * (heightQ - MARKER_HQ) / 4
                + (renderer.fontSizeForHeightQ(heightQ) - renderer.fontSizeForHeightQ(MARKER_HQ)) * 0.85)
            : 0

        // Aktiver Vorschlag: voller Zeilen-Background als Highlight (siehe
        // Lesson 76 / Boris-Feedback Phase C). Vor allen Glyphen zeichnen,
        // damit die Texte daraufgelegt werden.
        if (isActive) {
            this._fillReviewActiveBg(x, y, charWidth, charHeight, heightQ)
        }

        if (line.isFirstLine) {
            renderer.renderProportionalLine(
                renderer.ctx, '+',
                x * charWidth, pixelY + markerBaselineShift,
                [{ start: 0, end: 1, bold: false, italic: false,
                   underline: false, strikethrough: false, style: Style.EDITOR_MARKER }],
                null, null, null, MARKER_HQ
            )
        }

        const start = line.startOffset
        const end = Math.min(line.endOffset, composite.length)
        const lineText = composite.slice(start, end)
        // FormatRuns auf line-lokale Koordinaten slicen (siehe Composite-Pfad).
        const formatRuns = sliceFormatRunsForLine(fullRuns, start, end)

        // List-/Quote-Marker (rtPrefix) auf der ersten Zeile rendern; Text
        // startet entsprechend versetzt. Continuation-Zeilen erben nur den
        // Pixel-Indent (line.hangingIndent), kein Marker.
        const hangingPx = line.hangingIndent || 0
        if (line.rtPrefix && line.isFirstLine) {
            const indentLevel = line.rtPrefix.length - line.rtPrefix.trimStart().length
            const markerPart = line.rtPrefix.trimStart()
            if (markerPart.length > 0) {
                const markerStartPx = markerPx + indentLevel * charWidth
                renderer.renderProportionalLine(
                    renderer.ctx, markerPart, markerStartPx, pixelY,
                    [{ start: 0, end: markerPart.length, bold: false, italic: false,
                       underline: false, strikethrough: false, style: Style.EDITOR_REVIEW_SHOWN }],
                    null, null, null, heightQ
                )
            }
        }
        const textStartPx = markerPx + hangingPx
        const textWidthPx = this.textWidth * charWidth - hangingPx

        renderer.renderProportionalLine(
            renderer.ctx,
            lineText,
            textStartPx,
            pixelY,
            formatRuns,
            null,
            line.isLastLine ? { char: '<', selected: false } : null,
            null,
            heightQ
        )
    }

    /**
     * Proportional-Variante: nutzt renderer.renderProportionalLine mit
     * FormatRuns aus den Diff-Ranges. Damit haben wir 6QL-Glyphen, korrekte
     * Kerning-Layout und Strikethrough wie im normalen Pfad.
     */
    _renderReviewCompositeLineProportional(x, y, line, data, activeView, isActive, setCharFn) {
        const renderer = this.renderer
        const composite = data.composite
        const heightQ = line.heightQ || LINE_HEIGHT_Q.NORMAL
        const MARKER_HQ = LINE_HEIGHT_Q.NORMAL

        const start = line.startOffset
        const end = Math.min(line.endOffset, composite.length)
        const lineText = composite.slice(start, end)

        const charWidth = renderer.charWidth
        const charHeight = renderer.charHeight
        const markerPx = x * charWidth + renderer.markerWidthPx()
        const pixelY = y * charHeight
        const markerBaselineShift = heightQ > MARKER_HQ
            ? (renderer._verticalPad * (heightQ - MARKER_HQ) / 4
                + (renderer.fontSizeForHeightQ(heightQ) - renderer.fontSizeForHeightQ(MARKER_HQ)) * 0.85)
            : 0

        // Aktiver Vorschlag: voller Zeilen-Background als Highlight (siehe
        // Lesson 76 / Boris-Feedback Phase C). Vor allen Glyphen zeichnen.
        if (isActive) {
            this._fillReviewActiveBg(x, y, charWidth, charHeight, heightQ)
        }

        // Start-Marker
        if (line.isFirstLine) {
            renderer.renderProportionalLine(
                renderer.ctx, '>',
                x * charWidth, pixelY + markerBaselineShift,
                [{ start: 0, end: 1, bold: false, italic: false,
                   underline: false, strikethrough: false, style: Style.EDITOR_MARKER }],
                null, null, null, MARKER_HQ
            )
        }

        // Vorberechnete FormatRuns (Diff-Style + Inline-Format kombiniert) je
        // activeView aus dem Composite-Cache holen und auf die Zeile slicen.
        // sliceFormatRunsForLine rechnet auf line-lokale Koordinaten um.
        const fullRuns = activeView === 'original'
            ? (data.formatRunsOriginal || [])
            : (data.formatRunsProposed || [])
        const formatRuns = sliceFormatRunsForLine(fullRuns, start, end)

        // List-/Blockquote-/Numbered-Marker (rtPrefix) auf der ersten Zeile
        // proportional rendern. Der Wrap hat hangingIndent bereits in der
        // Zeilenbreite beruecksichtigt (siehe _breakTextProportional via
        // _getRichTextPrefix(eff)) — hier nur der visuelle Marker.
        const hangingPx = line.hangingIndent || 0
        if (line.rtPrefix && line.isFirstLine) {
            const indentLevel = line.rtPrefix.length - line.rtPrefix.trimStart().length
            const markerPart = line.rtPrefix.trimStart()
            if (markerPart.length > 0) {
                const markerStartPx = markerPx + indentLevel * charWidth
                // Diff-Akzent als Marker-Color: bei activeView=proposed der
                // SHOWN-Akzent (neuer Marker), bei original der KEEP-Akzent.
                const prefixStyle = activeView === 'original'
                    ? Style.EDITOR_REVIEW_KEEP
                    : Style.EDITOR_REVIEW_SHOWN
                renderer.renderProportionalLine(
                    renderer.ctx, markerPart, markerStartPx, pixelY,
                    [{ start: 0, end: markerPart.length, bold: false, italic: false,
                       underline: false, strikethrough: false, style: prefixStyle }],
                    null, null, null, heightQ
                )
            }
        }

        // Text rendern: bei rtPrefix bzw. Continuation-Zeilen (mit hangingIndent)
        // startet der Text um hangingPx weiter rechts; verfuegbare Breite sinkt
        // entsprechend.
        const textStartPx = markerPx + hangingPx
        const textWidthPx = this.textWidth * charWidth - hangingPx
        renderer.renderProportionalLine(
            renderer.ctx,
            lineText,
            textStartPx,
            pixelY,
            formatRuns,
            null,           // keine Selection im Review (Main locked)
            line.isLastLine ? { char: '<', selected: false } : null,
            null,           // kein cached layout — Composite ist transient
            heightQ
        )
    }

    /**
     * Interner Wrapper: wenn der Paragraph gedimmt werden soll, ctx.save()+
     * globalAlpha setzen; sonst no-op. Gibt immer eine restore-Funktion
     * zurueck (auch no-op). Fuer try/finally-Einsatz in _renderLine.
     * @returns {function} restore-Callback
     */
    _applyReviewDim(paragraphId) {
        if (!this._reviewHint || !this.renderer) return noopRestore
        if (this._reviewHint.dimAllExcept?.has(paragraphId)) return noopRestore
        // Separatoren bleiben waehrend Delta-Review immer in voller
        // Lesbarkeit — sie sind strukturelle Anker, kein "Beiwerk".
        const para = this.model.getParagraph?.(paragraphId)
        if (para && para.type === 'separator') return noopRestore
        const ctx = this.renderer.ctx
        ctx.save()
        ctx.globalAlpha = (ctx.globalAlpha ?? 1) * REVIEW.DIM_ALPHA
        return () => ctx.restore()
    }

    /**
     * Rendert eine einzelne Zeile.
     * Der Cursor wird NICHT hier gerendert - das macht der Framework-Renderer.
     *
     * @param {number} x - Screen X
     * @param {number} y - Screen Y
     * @param {number} lineIndex - Index in this.lines
     * @param {Function} setCharFn
     */
    /**
     * Holt Spellcheck-Warnings fuer einen Paragraph und unterdrueckt das Wort,
     * in dem der Cursor steht (Spec §8.2 / §6.4.7). Anti-Frust: Solange der
     * User aktiv tippt, soll keine Welle aufpoppen.
     *
     * Wichtig: Suppression nur, wenn der Cursor *exakt* an der Position
     * steht, an der die letzte Mutation passierte (`_lastEditedCursor`).
     * Reine Klicks auf ein fehlerhaftes Wort lassen die Welle bestehen,
     * weil dann ja noch keine Korrektur im Gange ist — die Welle hilft dem
     * User, das Wort zu sehen, statt sie wegzunehmen, sobald er sie
     * anvisiert.
     *
     * @param {*} paraId
     * @param {string} field - 'text' | 'note' | 'info'
     * @returns {Array|null}
     */
    /**
     * Spellcheck-Warnings, projiziert auf die line-lokalen Koordinaten einer
     * gerenderten Zeile (Main, Note oder Info). Liefert `null`, wenn der
     * Manager nicht geladen ist, das Feld nicht gepruefft wird, der
     * Paragraph keine Warnings hat oder keine Warning in den Line-Range
     * faellt. Wird von OutlineView/ContextView genauso genutzt wie vom
     * Main-Render — die Wavy-Underline-Logik ist damit field-unabhaengig.
     *
     * @param {ScreenLine} line - eine der `lines/noteLines/infoLines`
     * @param {'text'|'note'|'info'} field
     * @returns {Array<{start:number,length:number}>|null}
     */
    buildLocalSpellWarnings(line, field) {
        if (!this.spellCheckManager || !_isSpellCheckedField(field)) return null
        const warnings = this._getEffectiveSpellWarnings(line.paragraphId, field)
        if (!warnings || warnings.length === 0) return null
        const localWarnings = []
        for (const w of warnings) {
            const wEnd = w.start + w.length
            const lo = Math.max(w.start, line.startOffset)
            const hi = Math.min(wEnd, line.endOffset)
            if (hi > lo) {
                localWarnings.push({ start: lo - line.startOffset, length: hi - lo })
            }
        }
        return localWarnings.length > 0 ? localWarnings : null
    }

    /**
     * Liefert true, wenn der Cursor im selben Paragraph steht wie die Zeile —
     * Aktive-Para-Marker fuer Wavy-Dimming.
     */
    isLineActiveParagraph(line) {
        const cursor = this.model && this.model.cursor
        return !!cursor && cursor.paragraphId === line.paragraphId
    }

    _getEffectiveSpellWarnings(paraId, field) {
        if (!this.spellCheckManager) return null
        const warnings = this.spellCheckManager.getWarnings(paraId, field)
        if (!warnings || warnings.length === 0) return warnings
        const cursor = this.model.cursor

        // One-shot stale-check fuer _lastEditedCursor: sobald die Cursor-
        // Position vom Post-Mutation-Snapshot abweicht (Pfeil, Klick, was
        // auch immer), gilt die Suppression als VERBRAUCHT und wird
        // permanent geleert. Sonst kommt sie zurueck, wenn der Cursor
        // zufaellig wieder denselben Offset hat (Beispiel: "m" loeschen,
        // "n" tippen an Offset 4 → Welle weg; Pfeil-Links nach 3 →
        // Welle wieder da; Pfeil-Rechts nach 4 → Welle wieder weg, weil
        // Offset zufaellig matcht. Hier wollen wir: weg bleiben).
        const lec = this._lastEditedCursor
        if (lec) {
            const stale = !cursor
                || lec.paragraphId !== cursor.paragraphId
                || lec.offset !== cursor.offset
                || (lec.field || 'text') !== (cursor.field || 'text')
            if (stale) {
                this._lastEditedCursor = null
            }
        }

        if (!cursor || cursor.paragraphId !== paraId) return warnings
        if ((cursor.field || 'text') !== field) return warnings

        // Suppression greift nur, wenn der Stale-Check oben den Snapshot
        // NICHT geleert hat — d.h. der Cursor steht seit der Mutation an
        // exakt der gleichen Stelle (Feld inklusive, sonst kollidieren
        // note/info-Edits mit text-Klicks am gleichen numerischen Offset,
        // ultrareview bug_001).
        if (!this._lastEditedCursor) return warnings

        const para = this.model.getParagraph(paraId)
        if (!para) return warnings
        const text = field === 'text' ? para.text : (para[field] || '')
        const wr = wordRangeAt(text, cursor.offset)
        if (!wr) return warnings
        const wStart = wr.start, wEnd = wr.start + wr.length
        // Warning, das mit dem Cursor-Wort ueberlappt, herausfiltern.
        return warnings.filter(w => {
            const e = w.start + w.length
            return e <= wStart || w.start >= wEnd
        })
    }

    _renderLine(x, y, lineIndex, setCharFn) {
        const line = this.lines[lineIndex]
        // Virtuelle Insert-Lines haben Fake-paragraphId — `para` ist dann
        // null. Der Insert-Render-Pfad braucht para nicht.
        const para = this.model.getParagraph(line.paragraphId)
        if (!para && !line._reviewInsertOverlay) return

        // Delta-Review-Dim: nicht-Review-Paragraphen runter ziehen. Der Wrapper
        // ist idempotent (no-op wenn kein Review aktiv oder paraId in dimAllExcept).
        // try/finally stellt sicher, dass alle early returns unten sauber
        // restauriert werden.
        // Insert-Lines werden NIE gedimmt — sie sind Teil des Reviews.
        const restoreDim = line._reviewInsertOverlay ? noopRestore : this._applyReviewDim(line.paragraphId)
        try {
            this._renderLineBody(x, y, lineIndex, line, para, setCharFn)
        } finally {
            restoreDim()
        }
    }

    /** Eigentlicher Render-Body (aus _renderLine ausgelagert fuer Dim-Wrapper). */
    _renderLineBody(x, y, lineIndex, line, para, setCharFn) {
        // Padding-Zeilen: komplett leer (keine Marker)
        if (line.isPadding) {
            this._renderEmptyLine(x, y, setCharFn, line.isBackground ? '+BG' : '')
            return
        }

        // Active-Row-Background fuer den aktiven Absatz (Cursor-Anker, der
        // sichtbar bleibt, wenn der Cursor in Console/Diktat o.a. wandert).
        // Vor allen Glyphen — Glyphen liegen visuell darauf.
        this._fillActiveRowBg(x, y, line, lineIndex)

        // Review-Insert-Vorschau (virtuelle Zeile zwischen echten Paragraphen)
        if (line._reviewInsertOverlay) {
            this._renderReviewInsertLine(x, y, line, setCharFn)
            return
        }

        // Review-Composite-Zeile: Inline-Diff aus Original + Vorschlag rendern
        // (vor allen anderen Pfaden, weil der Composite-Text nicht zum
        // para.text-Markdown-Parser passt — er ist Plain-Text mit Diff-Spans).
        if (line._reviewComposite) {
            this._renderReviewCompositeLine(x, y, line, setCharFn)
            return
        }

        // HR-Zeile: kurze horizontale Linie (Drittel der Textbreite)
        if (para.type === 'hr' && line.isFirstLine) {
            this._renderHrLine(x, y, line, setCharFn)
            return
        }

        // Separator-Zeile: durchgehende ─ Linie mit optionalem Titel
        if (line.isSeparator) {
            this._renderSeparatorLine(x, y, line, setCharFn)
            return
        }

        // Proportional: eigener Render-Pfad
        if (this.renderer && this.renderer.isProportional()) {
            this._renderLineProportional(x, y, lineIndex, line, para, setCharFn)
            return
        }

        const paraType = this._getParaType(para)
        // Background-Suffix für Styles im Background-Bereich
        const bgSuffix = line.isBackground ? '+BG' : ''

        // Start-Marker rendern (nur bei erster Zeile eines Absatzes)
        if (line.isFirstLine) {
            const startMarkerSelected = this._isStartMarkerSelected(line.paragraphId)
            // Gesperrte Abschnitte: Marker unsichtbar (Leerzeichen), aber selektierbar
            const markerChar = this.model.isSectionLocked(line.paragraphId) ? ' ' : '>'
            setCharFn(x, y, markerChar, this._markerStyle(line.paragraphId), startMarkerSelected)
        } else {
            setCharFn(x, y, ' ', Style.EDITOR_TEXT + bgSuffix)
        }

        // Per-Paragraph Markdown-Parsing (gecacht)
        const fullParsed = this._getParsedRichText(this._textParseCache, para.id, para.text, paraType)
        let parsed = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Listen-Prefix als visuelle Einrückung (erste Zeile)
        const prefixLen = line.listPrefixLen
        const hangingOffset = prefixLen > 0
            ? prefixLen
            : (line.hangingIndent || 0)

        // posIndex früh berechnen (auch für Prefix-Selection)
        const posIndex = this._selectionCache ? this.model.getParagraphIndex(line.paragraphId) : -1
        // Prefix-Selection: Nur in der ersten Zeile die Lücke zur Absatzmarke schließen
        const prefixSelected = line.isFirstLine && posIndex >= 0 && this._isPositionSelectedCached(line.paragraphId, 0, 'text', posIndex)

        if (line.rtPrefix) {
            // RT-Modus: Prefix direkt rendern (nicht aus parsed, da nicht im Text)
            // Farbe vom ersten Textzeichen übernehmen (z.B. Durchstreichung → gedämpfter Bullet)
            const firstCharStyle = parsed.length > 0 ? parsed[0].style : null
            // paraType ist bereits effective (kommt aus _getParaType, das
            // _effectiveParaForRender anwendet). Direkter Lookup auf
            // para.headingLevel waere im Review falsch — paraType nutzen.
            const prefixStyle = (firstCharStyle
                || (paraType === 'heading' ? this._getBaseStyle(paraType, para) : Style.EDITOR_TEXT)
            ) + bgSuffix
            for (let hi = 0; hi < line.rtPrefix.length; hi++) {
                setCharFn(x + this.markerWidth + hi, y, line.rtPrefix[hi], prefixStyle, prefixSelected)
            }
            // parsed bleibt unverändert (kein Prefix im Text)
        } else if (prefixLen > 0) {
            // MD-Modus: Originalzeichen aus dem parsed Array rendern, Bullet-Marker als •
            const bulletPos = this._getBulletMarkerOffset(para.text)
            for (let hi = 0; hi < prefixLen && hi < parsed.length; hi++) {
                let char = parsed[hi].char
                const style = parsed[hi].style + bgSuffix
                if (bulletPos >= 0 && parsed[hi].dataOffset === bulletPos) {
                    char = '•'
                }
                const charSel = posIndex >= 0
                    && this._isPositionSelectedCached(line.paragraphId, parsed[hi].dataOffset, 'text', posIndex)
                setCharFn(x + this.markerWidth + hi, y, char, style, charSel)
            }
            // Content ab prefixLen
            parsed = parsed.slice(prefixLen)
        } else {
            // Hanging Indent: Continuation-Zeilen einrücken
            for (let hi = 0; hi < hangingOffset; hi++) {
                setCharFn(x + this.markerWidth + hi, y, ' ', Style.EDITOR_TEXT + bgSuffix)
            }
        }

        // Spellcheck-Warnings vorab in para-data-Koordinaten holen. Wir
        // vergleichen waehrend des Loops gegen `parsed[i].dataOffset` und
        // aggregieren benachbarte Treffer zu Grid-X-Ranges, die wir nach dem
        // Loop als Wavy-Underline zeichnen. Phase-1-Pendant zum Proportional-
        // Pfad in `_renderLineProportional`. data≈display ohne Section-Links;
        // Edge-Case mit Links wird in Phase 3 sauber adressiert.
        const spellRanges = []
        let curWavy = null
        const lineWarnings = (this.spellCheckManager && _isSpellCheckedField(line.field))
            ? this._getEffectiveSpellWarnings(line.paragraphId, line.field)
            : null
        const hasWarnings = lineWarnings && lineWarnings.length > 0

        const maxChars = hangingOffset > 0 ? this.textWidth - hangingOffset : this.textWidth
        let col = 0
        for (let i = 0; i < parsed.length && col < maxChars; i++) {
            const charX = x + this.markerWidth + hangingOffset + col
            const { char, style, dataOffset } = parsed[i]

            const isSelected = this._isPositionSelectedCached(line.paragraphId, dataOffset, 'text', posIndex)
                || this._isLinkCharSelected(line.paragraphId, 'text', parsed[i])
            const highlightType = this.searchHighlightFn
                ? this.searchHighlightFn(line.paragraphId, dataOffset)
                : null

            setCharFn(charX, y, char, style + bgSuffix, isSelected, highlightType)
            const dispW = charDisplayWidth(char)

            if (hasWarnings) {
                let inWarn = false
                for (let wi = 0; wi < lineWarnings.length; wi++) {
                    const w = lineWarnings[wi]
                    if (dataOffset >= w.start && dataOffset < w.start + w.length) {
                        inWarn = true
                        break
                    }
                }
                if (inWarn) {
                    if (curWavy && curWavy.gridX + curWavy.gridW === charX) {
                        curWavy.gridW += dispW
                    } else {
                        curWavy = { gridX: charX, gridW: dispW }
                        spellRanges.push(curWavy)
                    }
                } else {
                    curWavy = null
                }
            }

            col += dispW
        }

        // End-Marker direkt nach dem Text (nur bei letzter Zeile eines Absatzes).
        // Phantom-Outline-Zeilen (Spec §3.4) bekommen KEINEN <-Marker — nur das
        // einzelne >-Symbol am Anfang signalisiert "hier könnte Text stehen".
        if (line.isLastLine && !line.isPhantomOutline) {
            const endX = x + this.markerWidth + hangingOffset + col
            const endMarkerSelected = this._isEndMarkerSelected(line.paragraphId)
            // Gesperrte Abschnitte: Marker unsichtbar (Leerzeichen), aber selektierbar
            const endChar = this.model.isSectionLocked(line.paragraphId) ? ' ' : '<'
            setCharFn(endX, y, endChar, this._markerStyle(line.paragraphId), endMarkerSelected)
        }

        // Wavy-Underlines NACH allen Glyphen zeichnen — additiv, ueberdeckt nichts.
        // Dim, wenn der Cursor nicht in diesem Absatz steht — analog zum
        // Active-Field-Highlight-Pattern.
        if (spellRanges.length > 0 && this.renderer && this.renderer.drawSpellWavyMonospace) {
            const cursor = this.model && this.model.cursor
            const dim = !cursor || cursor.paragraphId !== line.paragraphId
            for (const r of spellRanges) {
                this.renderer.drawSpellWavyMonospace(r.gridX, y, r.gridW, dim)
            }
        }
    }

    /**
     * Rendert eine Zeile im Proportional-Modus.
     * Marker bleiben grid-basiert, Text wird pixelgenau gerendert.
     */
    _renderLineProportional(x, y, lineIndex, line, para, setCharFn) {
        // Separator-Zeile: wird im gemeinsamen Pfad gerendert (vor Proportional-Check)
        // (bereits in _renderLine behandelt)

        const renderer = this.renderer
        const bgSuffix = line.isBackground ? '+BG' : ''
        const heightQ = line.heightQ || 4
        const localTextHeight = renderer.textHeightForHeightQ(heightQ)
        // Selection/Highlight-Rects vertikal zentriert (wie die Glyphen)
        // _verticalPad skaliert mit heightQ (größere Zeilen → mehr Versatz)
        const selPixelY = y * renderer.charHeight + renderer._verticalPad * heightQ / 4

        // Start-Marker — immer in NORMAL-Größe (6QL), unabhängig vom Absatz-heightQ
        const MARKER_HQ = 6  // LINE_HEIGHT_Q.NORMAL
        const markerTextHeight = renderer.textHeightForHeightQ(MARKER_HQ)
        // Baseline-Alignment: Marker vertikal verschieben wenn Zeile größer ODER kleiner
        // als MARKER_HQ. Bei größeren Zeilen (Heading) sitzt der Marker auf der gleichen
        // Baseline wie der Text. Bei kleineren Zeilen (Code-Block, 4QL) würde der
        // 6QL-Marker visuell nach unten überstehen — wir heben ihn um 1QL an.
        const markerBaselineShift = heightQ > MARKER_HQ
            ? (renderer._verticalPad * (heightQ - MARKER_HQ) / 4
                + (renderer.fontSizeForHeightQ(heightQ) - renderer.fontSizeForHeightQ(MARKER_HQ)) * 0.85)
            : heightQ < MARKER_HQ
                ? -renderer.charHeight / 4
                : 0
        const startMarkerSelected = line.isFirstLine && this._isStartMarkerSelected(line.paragraphId)
        const isLocked = this.model.isSectionLocked(line.paragraphId)
        // Collapsed-Active-Host: wie locked keine >/< Marker, zusätzlich dimmed gerendert.
        const isCollapsedActive = line.isCollapsedActiveHost === true
        const hideMarkers = isLocked || isCollapsedActive
        // Dim-Wrapper für globalAlpha beim Zeichnen des Text-Inhalts
        const dimBegin = () => {
            if (isCollapsedActive) {
                renderer.ctx.save()
                renderer.ctx.globalAlpha = 0.45
            }
        }
        const dimEnd = () => {
            if (isCollapsedActive) {
                renderer.ctx.restore()
            }
        }
        if (line.isFirstLine) {
            const markerPixelX = x * renderer.charWidth
            const markerPixelY = y * renderer.charHeight + markerBaselineShift
            if (startMarkerSelected) {
                // Selection in voller Zeilenhöhe (nicht nur Marker-Höhe), vertikal zentriert
                this._fillSelectionRect(renderer.ctx, markerPixelX, selPixelY,
                    renderer.markerWidthPx(), localTextHeight)
            }
            // Gesperrte / collapsed Absätze: Marker nicht rendern
            if (!hideMarkers) {
                // Start-Marker als proportionalen Glyph in NORMAL-Größe rendern
                renderer.renderProportionalLine(
                    renderer.ctx, '>', markerPixelX, markerPixelY,
                    [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(line.paragraphId) }],
                    null, null, null, MARKER_HQ
                )
            }
        } else {
            setCharFn(x, y, ' ', Style.EDITOR_TEXT + bgSuffix)
        }

        // Parsed Markdown für diese Zeile
        const paraType = this._getParaType(para)
        const fullParsed = this._getParsedRichText(this._textParseCache, para.id, para.text, paraType)
        const fullLineSlice = this._sliceParsedForLine(fullParsed, line.startOffset, line.endOffset)

        // Listen-Prefix als visuelle Einrückung: Prefix-Zeichen aus Content entfernen
        const prefixLen = line.listPrefixLen
        let parsed = prefixLen > 0 && fullLineSlice.length >= prefixLen
            ? fullLineSlice.slice(prefixLen)
            : fullLineSlice

        // Pixel-Positionen (frühzeitig berechnen, auch für Prefix-Rendering)
        const charWidth = renderer.charWidth
        const charHeight = renderer.charHeight
        // Proportional: > Marker in 6QL ist breiter als charWidth → markerWidthPx()
        const markerPx = renderer.isProportional()
            ? x * charWidth + renderer.markerWidthPx()
            : (x + this.markerWidth) * charWidth
        const hangingPx = line.hangingIndent || 0
        const textStartX = markerPx + hangingPx
        const pixelY = y * charHeight

        // Prefix-Selection: Nur in der ersten Zeile die Lücke zur Absatzmarke schließen
        const propPosIndex = this._selectionCache ? this.model.getParagraphIndex(line.paragraphId) : -1
        if (line.isFirstLine && hangingPx > 0 && propPosIndex >= 0
            && this._isPositionSelectedCached(line.paragraphId, 0, 'text', propPosIndex)) {
            this._fillSelectionRect(renderer.ctx, markerPx, selPixelY, hangingPx, localTextHeight)
        }

        // Bullet/Nummer-Präfix wie den Text abtönen (dimBegin/dimEnd ist no-op
        // außer bei collapsed-active) — sonst bliebe der Bullet/die Nummer voll hell.
        dimBegin()
        if (line.rtPrefix) {
            // RT-Modus: Indent-Spaces in Grid-Breite überspringen, Marker-Teil proportional rendern
            // paraType ist effective (Review-WYSIWYG); para.headingLevel direkt waere im Review falsch.
            const isHeading = paraType === 'heading'
            const firstCharStyle = parsed.length > 0 ? parsed[0].style : null
            const prefixStyle = (firstCharStyle
                || (isHeading ? this._getBaseStyle(paraType, para) : Style.EDITOR_TEXT)
            ) + bgSuffix
            const indentLevel = line.rtPrefix.length - line.rtPrefix.trimStart().length
            const markerPart = line.rtPrefix.trimStart()
            const markerStartPx = markerPx + indentLevel * charWidth
            if (markerPart.length > 0) {
                renderer.renderProportionalLine(
                    renderer.ctx, markerPart, markerStartPx, pixelY,
                    [{ start: 0, end: markerPart.length, bold: isHeading, italic: false, underline: false, strikethrough: false, style: prefixStyle }],
                    null, null, null, heightQ
                )
            }
        } else if (prefixLen > 0) {
            // MD-Modus: Prefix-Zeichen aus parsed Array rendern
            const bulletPos = this._getBulletMarkerOffset(para.text)
            for (let hi = 0; hi < prefixLen && hi < fullLineSlice.length; hi++) {
                let char = fullLineSlice[hi].char
                const style = fullLineSlice[hi].style + bgSuffix
                if (bulletPos >= 0 && fullLineSlice[hi].dataOffset === bulletPos) {
                    char = '•'
                }
                const charPx = markerPx + hi * charWidth
                renderer.renderProportionalLine(
                    renderer.ctx, char, charPx, pixelY,
                    [{ start: 0, end: char.length, bold: false, italic: false, underline: false, strikethrough: false, style }],
                    null, null, null, heightQ
                )
            }
        }
        dimEnd()

        if (parsed.length === 0) {
            // End-Marker bei leerer letzter Zeile (oder Prefix-only Zeile)
            // Phantom-Outline-Zeilen (Spec §3.4) bekommen KEIN <-Marker — der
            // einzelne >-Marker am Anfang signalisiert das offene Ende.
            if (line.isLastLine && !line.isPhantomOutline) {
                const endMarkerSelected = this._isEndMarkerSelected(line.paragraphId)
                // textStartX = markerPx + hangingPx (Einrückung durch Bullet/Nummerierung)
                const endMarkerPx = textStartX
                const endMarkerPy = pixelY + markerBaselineShift
                if (endMarkerSelected) {
                    this._fillSelectionRect(renderer.ctx, endMarkerPx, selPixelY + markerBaselineShift,
                        renderer.markerWidthPx(), markerTextHeight)
                }
                // Gesperrte / collapsed Abschnitte: End-Marker nicht rendern
                if (!hideMarkers) {
                    renderer.renderProportionalLine(
                        renderer.ctx, '<', endMarkerPx, endMarkerPy,
                        [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(line.paragraphId) }],
                        null, null, null, MARKER_HQ
                    )
                }
            }
            return
        }

        // Zeilen-Text und Format-Runs (angereichert für Headings etc.)
        const lineText = parsedCharsToString(parsed)
        let formatRuns = _enrichFormatRuns(parsedToFormatRuns(parsed))

        // Background-Suffix an formatRun-Styles anhängen
        if (bgSuffix) {
            formatRuns = formatRuns.map(run => ({ ...run, style: run.style + bgSuffix }))
        }

        // Spellcheck-Warnings auf line-lokale Koordinaten projizieren und das
        // additive spellWarning-Flag an die formatRuns anhaengen. Phase 3:
        // alle drei Felder (text, note, info) werden gepruefft. data≈display
        // gilt fuer note/info, weil dort keine Section-Links erlaubt sind;
        // im text-Feld ist es ein Approximation, der Edge-Case mit Links
        // wird beim Lookup-Pfad sauber abgefangen.
        const localSpellWarnings = this.buildLocalSpellWarnings(line, line.field)
        if (localSpellWarnings) {
            formatRuns = enrichRunsWithSpellWarnings(
                formatRuns, localSpellWarnings, null, !this.isLineActiveParagraph(line)
            )
        }

        // PositionMap (autoritäre Quelle für Pixel-Positionen, UTF-16-basiert)
        const posMap = this._getPositionMap(lineIndex)
        const rawLayout = posMap ? posMap._rawLayout : null

        // Selection/Link-Highlight-Pixel-Range über posMap berechnen (UTF-16-konsistent)
        // Gleiche Methode wie Cursor-Klick: posMap.xPositions[] mit UTF-16-Indizes
        let selRange = null
        {
            const hasSelection = !!this._selectionCache
            const hasSelectedLink = !!this.model._selectedLink
            const posIndex = hasSelection ? this.model.getParagraphIndex(line.paragraphId) : -1
            if ((posIndex >= 0 || hasSelectedLink) && posMap) {
                let selStartU16 = -1, selEndU16 = -1
                let selStartCp = -1, selEndCp = -1
                let utf16Idx = 0
                for (let i = 0; i < parsed.length; i++) {
                    const isSel = (posIndex >= 0 && this._isPositionSelectedCached(line.paragraphId, parsed[i].dataOffset, 'text', posIndex))
                        || this._isLinkCharSelected(line.paragraphId, 'text', parsed[i])
                    const charLen = parsed[i].char.length
                    if (isSel && selStartU16 < 0) { selStartU16 = utf16Idx; selStartCp = i }
                    if (isSel) { selEndU16 = utf16Idx + charLen; selEndCp = i + 1 }
                    utf16Idx += charLen
                }
                if (selStartU16 >= 0) {
                    // Prüfen ob End-Marker auch selektiert ist
                    const endMarkerSel = line.isLastLine && this._isEndMarkerSelected(line.paragraphId)
                    if (endMarkerSel && selEndU16 >= lineText.length) {
                        // End-Marker selektiert: Renderer zeichnet Text-Selection + End-Marker
                        // nahtlos zusammen (kein Lücken-Risiko). Codepoint-Indizes für Renderer.
                        selRange = { start: selStartCp, end: selEndCp }
                    } else {
                        // Kein End-Marker: Selection hier zeichnen mit _textHeight
                        const x1 = textStartX + posMap.xPositions[selStartU16]
                        let x2
                        if (selEndU16 < posMap.xPositions.length) {
                            x2 = textStartX + posMap.xPositions[selEndU16]
                        } else {
                            x2 = textStartX + posMap.totalWidth
                        }
                        this._fillSelectionRect(renderer.ctx, x1, selPixelY, x2 - x1, localTextHeight)
                        selRange = { start: -1, end: -1, _rendered: true }
                    }
                }
            }
        }

        // Such-Highlighting VOR dem Text rendern (vertikal zentriert)
        if (this.searchHighlightFn && posMap) {
            this._renderSearchHighlightsProportional(
                renderer.ctx, parsed, posMap, line.paragraphId,
                textStartX, selPixelY, localTextHeight
            )
        }

        // End-Marker im proportionalen Fluss (bei gesperrten/collapsten Abschnitten unsichtbar)
        let endMarker = null
        if (line.isLastLine && !hideMarkers) {
            endMarker = {
                char: '<',
                style: this._markerStyle(line.paragraphId),
                selected: this._isEndMarkerSelected(line.paragraphId)
            }
        }

        // Proportional rendern (Text + End-Marker, Selection bereits gezeichnet)
        dimBegin()
        renderer.renderProportionalLine(
            renderer.ctx,
            lineText,
            textStartX,
            pixelY,
            formatRuns,
            selRange && selRange._rendered ? null : selRange,
            endMarker,
            rawLayout,
            heightQ
        )
        dimEnd()

        // Collapse-Ellipse: `…` im dim-Style. Bei umgebrochener (abgeschnittener)
        // erster Zeile ersetzt sie die letzten 3 Codepoints; ohne Umbruch wird sie
        // hinter den vollständigen Text angehängt.
        if (isCollapsedActive && line.collapseHasEllipsis && posMap && parsed.length > 0) {
            let cutPixelX
            // Beim Anhängen ein Space vor die Punkte ("Kapitel 1 …"); beim Ersetzen
            // (Umbruch) direkt `…` an der Schnittstelle.
            const ellipsisText = line.collapseEllipsisAppend ? ' …' : '…'
            if (line.collapseEllipsisAppend) {
                // Vollständiger Text (kein Umbruch) → ` …` direkt dahinter, kein
                // Überpinseln (es gibt keinen abzuschneidenden Schwanz).
                cutPixelX = textStartX + posMap.totalWidth
            } else {
                // Abgeschnittene erste Zeile (Umbruch) → letzte 3 Codepoints durch
                // `…` ersetzen: Schwanz mit Background überpinseln, `…` an dessen
                // Startposition zeichnen.
                const cutCp = Math.max(0, parsed.length - 3)
                let cutU16 = 0
                for (let i = 0; i < cutCp; i++) cutU16 += parsed[i].char.length
                const xAfterCut = cutU16 < posMap.xPositions.length
                    ? posMap.xPositions[cutU16]
                    : posMap.totalWidth
                cutPixelX = textStartX + xAfterCut
                const tailPixelX = textStartX + posMap.totalWidth
                const fullLineH = heightQ * (charHeight / 4)
                const padPx = renderer._verticalPad * heightQ / 4
                const rectW = Math.max(tailPixelX - cutPixelX + charWidth, charWidth * 2)
                // 1. Voller Bereich inkl. Rand oben/unten mit normalem Background →
                //    Ascender-/Descender-Reste des abgeschnittenen Schwanzes löschen.
                renderer.ctx.fillStyle = renderer.colors.background
                renderer.ctx.fillRect(cutPixelX, pixelY - padPx, rectW, fullLineH + padPx * 2)
                // 2. Nur die Zeilen-Box mit dem Active-Row-Background füllen — exakt
                //    derselbe Y-Bereich wie _fillActiveRowBg (pixelY = y·charHeight,
                //    Höhe fullLineH), damit das Highlight oben/unten nicht übersteht.
                const activeBg = this._activeRowBgColor(line, lineIndex)
                if (activeBg) {
                    renderer.ctx.fillStyle = activeBg
                    renderer.ctx.fillRect(cutPixelX, pixelY, rectW, fullLineH)
                }
            }
            // Ellipsis im dim-Style zeichnen
            dimBegin()
            renderer.renderProportionalLine(
                renderer.ctx, ellipsisText, cutPixelX, pixelY,
                [{ start: 0, end: ellipsisText.length, bold: false, italic: false, underline: false, strikethrough: false, style: Style.EDITOR_TEXT + bgSuffix }],
                null, null, null, heightQ
            )
            dimEnd()
        }
    }

    /**
     * Zeichnet ein Selection-Hintergrund-Rechteck.
     * Zentrale Methode — gleiche Farb-Logik wie im Renderer.
     */
    _fillSelectionRect(ctx, x, y, width, height) {
        if (width <= 0) return
        const renderer = this.renderer
        const selStyle = STYLES[Style.EDITOR_SELECTION]
        let bgColor
        if (renderer.isNerdMode()) {
            bgColor = selStyle.nerdBackground
        } else if (renderer.colors.isColored) {
            bgColor = renderer.colors.isDarkMode ? selStyle.darkBackground : selStyle.lightBackground
        } else {
            bgColor = renderer.isLightBackground()
                ? selStyle.lightBackground : selStyle.darkBackground
        }
        ctx.fillStyle = bgColor
        ctx.fillRect(x, y, width, height)
    }

    /**
     * Rendert Such-Highlighting für eine proportionale Zeile.
     */
    _renderSearchHighlightsProportional(ctx, parsed, posMap, paragraphId, textStartX, pixelY, charHeight) {
        const renderer = this.renderer
        let hlStart = -1
        let hlType = null

        for (let i = 0; i <= parsed.length; i++) {
            const currentHl = i < parsed.length
                ? this.searchHighlightFn(paragraphId, parsed[i].dataOffset)
                : null

            if (currentHl !== hlType) {
                // Vorherigen Highlight-Block abschließen
                if (hlType && hlStart >= 0) {
                    const startPx = textStartX + (hlStart < posMap.xPositions.length ? posMap.xPositions[hlStart] : posMap.totalWidth)
                    const endPx = textStartX + (i < posMap.xPositions.length ? posMap.xPositions[i] : posMap.totalWidth)

                    const bgColor = renderer._resolveSearchHighlightColor(hlType)
                    if (bgColor) {
                        ctx.fillStyle = bgColor
                        ctx.fillRect(startPx, pixelY, endPx - startPx, charHeight)
                    }
                }
                hlStart = i
                hlType = currentHl
            }
        }
    }

    /**
     * Extrahiert den Teil des parsed-Arrays der zu einer ScreenLine gehört.
     * fullParsed hat Display-Indizes (Marker übersprungen), jedes Element hat dataOffset.
     * Die ScreenLine hat Data-Offsets (start/end beziehen sich auf den Originaltext).
     *
     * @param {Array} fullParsed - Komplettes parsed Array (Display-Indizes)
     * @param {number} dataStart - Data-Offset Start
     * @param {number} dataEnd - Data-Offset End
     * @returns {Array}
     */
    _sliceParsedForLine(fullParsed, dataStart, dataEnd) {
        return sliceParsedForLine(fullParsed, dataStart, dataEnd)
    }

    /**
     * Rendert eine leere Zeile.
     * @param {number} x
     * @param {number} y
     * @param {Function} setCharFn
     */
    _renderEmptyLine(x, y, setCharFn, bgSuffix = '') {
        for (let i = 0; i < this.width; i++) {
            setCharFn(x + i, y, ' ', Style.EDITOR_TEXT + bgSuffix)
        }
    }

    /**
     * Rendert eine HR-Zeile: kurze horizontale Linie (Drittel der Textbreite).
     * Monospaced: Box-Drawing ─, Proportional: Strikethrough-Strich.
     */
    _renderHrLine(x, y, line, setCharFn) {
        const bgSuffix = line.isBackground ? '+BG' : ''
        const hrStyle = Style.EDITOR_HR + bgSuffix

        // Kein Start-Marker bei HR — Marker-Bereich bleibt leer
        setCharFn(x, y, ' ', Style.EDITOR_TEXT + bgSuffix)

        // Linie: Drittel der Textbreite
        const hrLen = Math.max(3, Math.floor(this.textWidth / 3))
        const startCol = x + this.markerWidth

        if (this.renderer && this.renderer.isProportional()) {
            // Proportional: horizontale Linie, zentriert in der Zeilenhöhe
            const charWidth = this.renderer.charWidth
            const charHeight = this.renderer.charHeight
            const pixelY = y * charHeight
            // Marker-Bereich in Pixel (breiter als charWidth im Proportional-Modus)
            const x1 = x * charWidth + this.renderer.markerWidthPx()
            const x2 = x1 + hrLen * charWidth
            const ctx = this.renderer.ctx
            const style = STYLES[Style.EDITOR_HR]
            let color
            if (this.renderer.isNerdMode()) {
                color = this.renderer.colors.foreground[style.intensity]
            } else if (this.renderer.colors.isColored) {
                color = this.renderer.colors.isDarkMode ? style.darkColor : style.lightColor
            } else {
                color = this.renderer.colors.foreground[style.intensity]
            }
            ctx.strokeStyle = color
            ctx.lineWidth = Math.max(1, Math.round(this.renderer.fontSize * 0.06))
            // Vertikal in der Zeilenmitte zentrieren (HR hat 6QL Zeilenhöhe)
            const hrHeight = this.renderer.charHeightForHeightQ(line.heightQ || 6)
            const lineY = pixelY + hrHeight / 2
            ctx.beginPath()
            ctx.moveTo(x1, lineY)
            ctx.lineTo(x2, lineY)
            ctx.stroke()
        } else {
            // Monospaced: Box-Drawing ─
            for (let i = 0; i < hrLen; i++) {
                setCharFn(startCol + i, y, '─', hrStyle)
            }
        }

        // Rest der Zeile mit Leerzeichen füllen
        for (let i = startCol + hrLen; i < x + this.width; i++) {
            setCharFn(i, y, ' ', Style.EDITOR_TEXT + bgSuffix)
        }
    }

    /**
     * Liefert die Fuell-Farbe fuer ueberlagerte Sub-Bereiche (Label,
     * Lock-Control) auf Separator-Zeilen. Standard ist mode.background;
     * steht der Cursor auf diesem Separator, kommt mode.activeRowBackground —
     * sonst stanzten Label und Lock-Control sichtbare Loecher in den Active-
     * Row-Highlight, weil _fillActiveRowBg() schon die volle Zeile gefuellt
     * hat und die fillRect-Overlays diese Farbe ueberschreiben.
     */
    _separatorOverlayBg(line) {
        const colors = this.renderer && this.renderer.colors
        if (!colors) return null
        const cursor = this.model && this.model.cursor
        if (cursor && line && cursor.paragraphId === line.paragraphId) {
            return colors.activeRowBackground || colors.background
        }
        return colors.background
    }

    /**
     * Rendert eine Separator-Zeile.
     * Erst durchgehende ─ Linie, dann Label darüber mit Blanks als Abstand.
     * Titel in Main wenn sichtbar, Nummer als Fallback wenn Outline nicht sichtbar.
     *
     * Editierbare Separatoren (alles außer Background) bekommen zusätzlich
     * Absatz-Marker `>` `<` links und rechts vom Label-Block — analog zu
     * normalen Absätzen. Bei leerem Label wird eine Mindest-Innenbreite
     * gehalten, damit der Klickbereich für ein neues Label sichtbar bleibt.
     */
    _renderSeparatorLine(x, y, line, setCharFn) {
        const isBackgroundSep = line.separatorDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH
        const bgSuffix = line.isBackground ? '+BG' : ''
        const lineChar = isBackgroundSep ? '═' : '─'
        // Linie + Icon-Borders teilen den Stil der Outline-Connector-Linie:
        // passiv die Connector-Farbe, aktiv (Cursor auf diesem Separator) die
        // Active-Border-Color — analog zur senkrechten Outline-Linie. Gilt auch
        // für den Background-Separator: dessen Doppellinie (═══) trägt dieselbe
        // Farbe wie normale Linien, nur sein Label bleibt sepia (+BG am Text).
        const isActiveSep = this.model.cursor && this.model.cursor.paragraphId === line.paragraphId
        const hrStyle = isActiveSep ? Style.OUTLINE_CURRENT : Style.OUTLINE_CONNECTOR

        // 1. Durchgehende Linie (═══ für Background, ─── für normal)
        for (let i = 0; i < this.width; i++) {
            setCharFn(x + i, y, lineChar, hrStyle)
        }

        // 2. Label bestimmen
        let title = line.separatorTitle
        const showNumber = !title && this.outlinePaneWidth <= 0

        // Background-Separator: Label = "Background · Dokumenttitel" (oder nur "Background")
        if (isBackgroundSep) {
            const docTitle = this.model.metadata && this.model.metadata.title
            title = docTitle ? `Background · ${docTitle}` : 'Background'
        }

        const label = title || (showNumber ? line.separatorNumber : '')

        // Lock-Control (v)/( ) immer rendern — auch ohne Label
        if (!isBackgroundSep && this.width >= 8) {
            const locked = this.model.isSectionLocked(line.paragraphId)
            const controlX = this.width - 4
            if (controlX > 0) {
                // Symbol bei Hover in der Hover-Farbe (analog Outline-Icons)
                const lockSymbolStyle = this._hoveredMainSepLock === line.paragraphId
                    ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL
                if (this.renderer) {
                    const cw = this.renderer.charWidth
                    const ch = this.renderer.charHeight
                    this.renderer.ctx.fillStyle = this._separatorOverlayBg(line)
                    this.renderer.ctx.fillRect((x + controlX) * cw, y * ch, 3 * cw, ch)
                }
                if (this.renderer && this.renderer.isProportional()) {
                    this.renderer._renderControlIcon(x + controlX + 1, y, locked ? 'v' : ' ', lockSymbolStyle, hrStyle)
                } else {
                    setCharFn(x + controlX, y, '(', hrStyle)
                    setCharFn(x + controlX + 1, y, locked ? 'v' : ' ', lockSymbolStyle)
                    setCharFn(x + controlX + 2, y, ')', hrStyle)
                }
            }
        }

        // Background-Separator: kein Edit, kein Marker — wenn kein Label, fertig.
        if (isBackgroundSep && !label) return
        // Editierbarer Separator ohne Label: Marker trotzdem rendern (Klickzone),
        // also nicht abbrechen.
        // Mindestbreite fuer leeren Marker (>< direkt) + Lock + Spacer = 8 chars.
        // Mit Label wird der Display-Text bei schmaler Pane abgeschnitten.
        if (!isBackgroundSep && this.width < 8) return

        // Background-Separator hat kein editierbares Titel-Feld (Label ist automatisch).
        // Editierbarkeit haengt vom Typ ab, nicht vom Inhalt — sonst waeren Marker
        // bei leerem Label dim, obwohl der Cursor hier steht.
        const isEditableTitle = !isBackgroundSep
        const rawSepState = isEditableTitle && this._separatorInputAccessor ? this._separatorInputAccessor() : null
        // Separator-Input-State nur verwenden wenn AKTIV in DIESEM Pane UND
        // fuer DIESE Separator-Zeile editiert wird. Sonst wuerde der scrollOffset
        // des gerade editierten (langen) Labels alle anderen Labels kuerzen.
        const isActiveSeparatorInput = rawSepState
            && rawSepState.pane === 'main'
            && this.model.cursor
            && this.model.cursor.paragraphId === line.paragraphId
        const sepState = isActiveSeparatorInput ? rawSepState : null
        const hasSel = sepState && sepState.selectionStart !== sepState.selectionEnd
            && sepState.text === line.separatorTitle
        // Style des Labels haengt davon ab, ob das Label der echte Titel ist
        // (bold, SEPARATOR_TITLE) oder nur Nummer-Fallback (nicht-bold, EDITOR_HR).
        const isLabelFromTitle = !!title
        // Titel bold in normaler Textfarbe (SEPARATOR_TITLE), Nummer-Fallback in
        // normaler Textfarbe nicht-bold (EDITOR_TEXT statt früher EDITOR_HR).
        const labelStyle = (isLabelFromTitle || isBackgroundSep ? Style.SEPARATOR_TITLE : Style.EDITOR_TEXT) + bgSuffix
        // Marker-Style auf editierbarem Separator: voll wenn Cursor hier UND
        // Edit-Pane === main, sonst dim. Für Background gar keine Marker.
        const markerStyle = isBackgroundSep
            ? null
            : pickSeparatorMarkerStyle(MarkerPane.MAIN, line.paragraphId, this.model.cursor, sepState)

        const renderer = this.renderer
        const isProp = renderer && renderer.isProportional()
        // Label-Start: 2 Zeichen Abstand vom linken Rand
        const labelCol = 2

        if (isProp) {
            // === Proportionaler Modus ===
            const charWidth = renderer.charWidth
            const charHeight = renderer.charHeight
            const pixelY = y * charHeight
            const selPixelY = pixelY + renderer._verticalPad

            const scrollOffset = sepState ? sepState.scrollOffset : 0
            const displayLabel = label.slice(scrollOffset)
            const isEmpty = displayLabel.length === 0 && !isBackgroundSep

            let selRange = null
            if (hasSel) {
                const sMin = Math.min(sepState.selectionStart, sepState.selectionEnd)
                const sMax = Math.max(sepState.selectionStart, sepState.selectionEnd)
                selRange = {
                    start: Math.max(0, sMin - scrollOffset),
                    end: Math.max(0, sMax - scrollOffset)
                }
            }

            const formatRuns = [{
                start: 0, end: displayLabel.length,
                bold: isLabelFromTitle || isBackgroundSep, italic: false, underline: false, strikethrough: false,
                style: labelStyle
            }]

            // Label-Breite berechnen — _activeMetrics liefert System-Font-Cache (z.B. NotoSans bei Garamond)
            const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
            const layout = computeStringLayout(displayLabel, activeCache, activeCache.baseStep, formatRuns)
            const labelWidthPx = layout.totalWidth * activeSF
            const labelStartPx = (x + labelCol) * charWidth  // direkt am labelCol, kein Spacer

            // Hintergrund-Block ueberdeckt > Label < (oder >< bei leerem Label)
            // damit die ─ Linie nicht durch die Marker schimmert. Auf aktivem
            // Separator: activeRowBackground via _separatorOverlayBg.
            // Marker passen sich der Linien-Hoehe an (kleiner auf Separator-
            // Zeile, lineHQ=4 statt 6) — die Spitze bleibt automatisch auf
            // der Linie, da Glyph-Mitte = pixelY + charHeight/2 unabhaengig
            // von markerHQ gilt. Auch die Glyph-Box muss proportional kleiner
            // werden, sonst entsteht eine Luecke zwischen Glyph-Spitze und
            // Label (Glyph wird linksbuendig in Box gemalt).
            // Ohne Marker (Background-Sep): charWidth Spacer statt Marker, sonst
            // klebt die ═══ Linie direkt am Label.
            const lineHQ = line.heightQ || 4
            const markerHQ = lineHQ
            const markerWidthPx = renderer.markerWidthPx() * lineHQ / LINE_HEIGHT_Q.NORMAL
            if (markerStyle || !isEmpty) {
                const sideWidth = markerStyle ? markerWidthPx : charWidth
                const startPx = labelStartPx - sideWidth
                const totalPx = sideWidth + labelWidthPx + sideWidth
                renderer.ctx.fillStyle = this._separatorOverlayBg(line)
                renderer.ctx.fillRect(startPx, pixelY, totalPx, charHeight)
            }

            // Marker > links vom Label, < rechts (nur fuer editierbare Separatoren)
            if (markerStyle) {
                // markerHQ === lineHQ → Glyph-Mitte sitzt auf pixelY + charHeight/2
                // (gleiche Position wie ─ Linie). Kein Vertikal-Shift noetig.
                const markerPixelY = pixelY
                const startMarkerPx = labelStartPx - markerWidthPx
                const endMarkerPx = labelStartPx + labelWidthPx
                const markerRuns = [{
                    start: 0, end: 1, bold: false, italic: false,
                    underline: false, strikethrough: false, style: markerStyle
                }]
                renderer.renderProportionalLine(
                    renderer.ctx, '>', startMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
                renderer.renderProportionalLine(
                    renderer.ctx, '<', endMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
            }

            // Proportionalen Text darüber (nur wenn nicht leer)
            const paneEndPx = (x + this.width) * charWidth
            if (displayLabel.length > 0) {
                renderer.renderProportionalLine(
                    renderer.ctx, displayLabel, labelStartPx, pixelY,
                    formatRuns, selRange, null, null
                )
            }

            // Such-Highlighting für proportionalen Separator-Titel
            if (this.searchHighlightFn && displayLabel.length > 0) {
                const sf = activeSF
                let hlStart = -1, hlType = null
                const codepoints = Array.from(displayLabel)
                for (let i = 0; i <= codepoints.length; i++) {
                    const dataOffset = scrollOffset + i
                    const currentHl = i < codepoints.length
                        ? this.searchHighlightFn(line.paragraphId, dataOffset) : null
                    if (currentHl !== hlType) {
                        if (hlType && hlStart >= 0) {
                            const sPx = labelStartPx + (hlStart < layout.positions.length
                                ? layout.positions[hlStart] * sf : layout.totalWidth * sf)
                            const ePx = labelStartPx + (i < layout.positions.length
                                ? layout.positions[i] * sf : layout.totalWidth * sf)
                            const bgColor = renderer._resolveSearchHighlightColor(hlType)
                            if (bgColor) {
                                renderer.ctx.fillStyle = bgColor
                                renderer.ctx.fillRect(sPx, selPixelY, ePx - sPx, charHeight)
                            }
                        }
                        hlStart = i
                        hlType = currentHl
                    }
                }
                // Nochmal Text darüber (Highlight ist Hintergrund)
                if (hlType !== null || hlStart > 0) {
                    renderer.renderProportionalLine(
                        renderer.ctx, displayLabel, labelStartPx, pixelY,
                        formatRuns, selRange, null, null
                    )
                }
            }
        } else {
            // === Monospace-Modus ===
            // Marker reservieren je 1 Char-Spalte links/rechts vom Label (kein Spacer)
            const markerReserve = markerStyle ? 1 : 0
            const maxLabelLen = this.width - labelCol - 1 - 2 * markerReserve
            const scrollOffset = sepState ? sepState.scrollOffset : 0
            const displayLabel = maxLabelLen > 0
                ? label.slice(scrollOffset, scrollOffset + maxLabelLen)
                : ''
            const isEmpty = displayLabel.length === 0 && !isBackgroundSep

            let selStart = -1, selEnd = -1
            if (hasSel) {
                const sMin = Math.min(sepState.selectionStart, sepState.selectionEnd)
                const sMax = Math.max(sepState.selectionStart, sepState.selectionEnd)
                selStart = Math.max(0, Math.min(sMin - scrollOffset, displayLabel.length))
                selEnd = Math.max(0, Math.min(sMax - scrollOffset, displayLabel.length))
            }

            // BG-Block ueber > Label < (oder >< bei leerem Label) damit die ─
            // Linie nicht durch die Marker schimmert. Background-Sep ohne Marker:
            // nur unter dem Label.
            if (renderer && (markerStyle || !isEmpty)) {
                const charWidth = renderer.charWidth
                const charHeight = renderer.charHeight
                const startCol = markerStyle ? labelCol - 1 : labelCol
                const totalCols = (markerStyle ? 1 : 0)
                    + (isEmpty ? 0 : displayLabel.length)
                    + (markerStyle ? 1 : 0)
                renderer.ctx.fillStyle = this._separatorOverlayBg(line)
                renderer.ctx.fillRect((x + startCol) * charWidth, y * charHeight,
                    totalCols * charWidth, charHeight)
            }

            if (isEmpty) {
                // ><  direkt aneinander an Spalte labelCol-1 / labelCol
                if (markerStyle) {
                    setCharFn(x + labelCol - 1, y, '>', markerStyle)
                    setCharFn(x + labelCol, y, '<', markerStyle)
                }
            } else {
                // Marker > links vom Label (Spalte labelCol - 1)
                if (markerStyle) {
                    setCharFn(x + labelCol - 1, y, '>', markerStyle)
                }

                // Label direkt ab labelCol (kein Spacer)
                let col = labelCol
                for (let i = 0; i < displayLabel.length && col < this.width - 1; i++) {
                    const selected = hasSel && i >= selStart && i < selEnd
                    const dataOffset = scrollOffset + i
                    const highlightType = this.searchHighlightFn
                        ? this.searchHighlightFn(line.paragraphId, dataOffset) : null
                    setCharFn(x + col++, y, displayLabel[i], labelStyle, selected, highlightType)
                }

                // Marker < direkt rechts vom Label
                if (markerStyle && col < this.width) {
                    setCharFn(x + col, y, '<', markerStyle)
                }
            }
        }

    }

    /**
     * Rendert die Scrollbar.
     * @param {number} x - Absolute X-Position
     * @param {number} y - Absolute Y-Position
     * @param {number} height - Höhe der Scrollbar (kann vom Editor-Height abweichen)
     * @param {Function} setCharFn
     */
    renderScrollbar(x, y, height, setCharFn) {
        // Lines sind bereits aktuell (durch render() oder tryRenderDirtyOnly())
        const state = this.getScrollbarState(height)
        const renderer = this.renderer

        // Proportional-Mode: pixelgenaue Minimap (Sections + Separatoren + Viewport)
        if (renderer && this._useQuarterSpacing() && this._totalQuarters > 0) {
            this._renderMinimap(x, y, height, state)
            return
        }

        // Monospace: Thumb als fillRect (pixelgenau, kein Glyph-topPad-Overhang),
        // Track als `░`-Glyph via setCharFn.
        const thumbColor = (renderer && state.visible)
            ? renderer._resolveStyle(Style.SCROLLBAR_THUMB).color : null

        for (let i = 0; i < height; i++) {
            const isThumb = state.visible && i >= state.thumbTop && i < state.thumbTop + state.thumbHeight
            if (isThumb && thumbColor) {
                const cw = renderer.charWidth
                const ch = renderer.charHeight
                renderer.ctx.fillStyle = thumbColor
                renderer.ctx.fillRect(x * cw, (y + i) * ch, cw, ch)
            } else if (isThumb) {
                // Fallback (Tests ohne Renderer)
                setCharFn(x, y + i, '█', Style.SCROLLBAR_THUMB)
            } else {
                setCharFn(x, y + i, '░', Style.SCROLLBAR_TRACK)
            }
        }
    }

    /**
     * Rendert die Scrollbar im Proportional-Mode als pixelgenaue Minimap:
     * Viewport-Underlayer + Section-Füllungen (editable/locked/background) +
     * Separator-Linien (1 px, Doppellinie bei Background-Separator).
     *
     * Mapping: `_lineQuarterY + BOTTOM_PADDING` → volle Scrollbar-Pixel-Höhe.
     * Farben werden aus dem aktiven Style-System aufgelöst, Alpha aus MINIMAP.
     *
     * @param {number} x - Char-Spalte der Scrollbar
     * @param {number} y - Char-Y-Start
     * @param {number} height - Scrollbar-Höhe in Chars
     * @param {{visible: boolean}} state - Scrollbar-State (für Viewport-Sichtbarkeit)
     */
    _renderMinimap(x, y, height, state) {
        const renderer = this.renderer
        const ctx = renderer.ctx
        const cw = renderer.charWidth
        const ch = renderer.charHeight

        // Pixel-Box der Scrollbar-Spalte
        const px = x * cw
        const py = y * ch
        const pw = cw
        const ph = height * ch

        // Opaker Hintergrund — verhindert dass Shift-Reste oder alter Content
        // durch die Alpha-Layer durchscheinen.
        ctx.fillStyle = renderer.colors.background
        ctx.fillRect(px, py, pw, ph)

        // Mapping Quarter → Pixel (totalQ inkl. Bottom-Padding, wie getScrollbarState)
        const quarterH = ch / 4
        const totalQ = this._totalQuarters + PARAGRAPH_SPACING.BOTTOM_PADDING
        if (totalQ <= 0) return
        const qToPixel = (q) => py + Math.max(0, Math.min(1, q / totalQ)) * ph

        // --- 1. Sections + Separatoren aus ScreenLines extrahieren ---
        const sections = []
        const separators = []
        let current = {
            startQ: 0,
            locked: !!this.model._firstSectionLocked,
            isBackground: false
        }

        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (!line.isSeparator) continue

            const lineQ = this._lineQuarterY[i] || 0
            const lineHQ = line.heightQ || 0
            const paraIdx = this.model.getParagraphIndex(line.paragraphId)
            const para = paraIdx >= 0 ? this.model.getParagraphAt(paraIdx) : null
            const isBgSep = !!(para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH)

            // Aktuellen Abschnitt bis zum Separator schließen
            current.endQ = lineQ
            sections.push(current)

            separators.push({ quarterY: lineQ, heightQ: lineHQ, isBackground: isBgSep })

            // Neuer Abschnitt nach dem Separator
            current = {
                startQ: lineQ + lineHQ,
                locked: para ? !!para.locked : false,
                isBackground: isBgSep || current.isBackground
            }
        }
        current.endQ = totalQ
        sections.push(current)

        // --- 2. Farben aus dem aktiven Style-System auflösen ---
        const fgColor = renderer._resolveStyle(Style.EDITOR_TEXT).color
        const bgColor = renderer._resolveStyle(Style.EDITOR_TEXT + '+BG').color

        // Minimap-Inhalt etwas schmaler als die Spalte — der Viewport (volle Breite)
        // ragt beidseitig um `inset` Pixel heraus und wird dadurch deutlich sichtbar.
        const inset = Math.min(MINIMAP.INSET, Math.floor((pw - 1) / 2))
        const mpx = px + inset
        const mpw = Math.max(1, pw - 2 * inset)

        // --- 3. Viewport-Underlayer (sanfter Aufheller, zuerst → wirkt "unter" den Sections) ---
        // vpBounds merken — die Rand-Streifen bekommen am Ende einen zweiten Pass mit
        // höherem Alpha, damit der Viewport-Überstand ohne Section-Overlay sichtbar ist.
        let vpBounds = null
        if (state.visible) {
            const vpStartQ = (this._lineQuarterY[this.viewportStart] || 0) + (this._viewportPixelOffset || 0) / quarterH
            const vpEndQ = vpStartQ + height * 4
            let vpPxStart = qToPixel(vpStartQ)
            let vpPxEnd = qToPixel(Math.min(vpEndQ, totalQ))

            // Mindesthöhe garantieren, damit der Drag-Handle mit der Maus greifbar bleibt
            const minH = MINIMAP.MIN_VIEWPORT_HEIGHT
            if (vpPxEnd - vpPxStart < minH) {
                const mid = (vpPxStart + vpPxEnd) / 2
                vpPxStart = mid - minH / 2
                vpPxEnd = mid + minH / 2
                // Innerhalb der Scrollbar-Box clampen
                if (vpPxStart < py) { vpPxEnd += py - vpPxStart; vpPxStart = py }
                if (vpPxEnd > py + ph) { vpPxStart -= vpPxEnd - (py + ph); vpPxEnd = py + ph }
            }

            const vpY = Math.round(vpPxStart)
            const vpH = Math.round(vpPxEnd - vpPxStart)

            ctx.save()
            ctx.globalAlpha = MINIMAP.VIEWPORT_ALPHA
            ctx.fillStyle = fgColor
            ctx.fillRect(px, vpY, pw, vpH)
            ctx.restore()

            vpBounds = { y: vpY, h: vpH }
        }

        // --- 4. Section-Füllungen (Alpha-Layer über den Viewport-Aufheller) ---
        for (const sec of sections) {
            const pyStart = Math.round(qToPixel(sec.startQ))
            const pyEnd = Math.round(qToPixel(sec.endQ))
            if (pyEnd <= pyStart) continue

            let alpha, color
            if (sec.isBackground) {
                alpha = MINIMAP.SECTION_BG_ALPHA
                color = bgColor
            } else if (sec.locked) {
                alpha = MINIMAP.SECTION_LOCKED_ALPHA
                color = fgColor
            } else {
                alpha = MINIMAP.SECTION_EDITABLE_ALPHA
                color = fgColor
            }

            ctx.save()
            ctx.globalAlpha = alpha
            ctx.fillStyle = color
            ctx.fillRect(mpx, pyStart, mpw, pyEnd - pyStart)
            ctx.restore()
        }

        // --- 5. Separator-Linien (exakt 1 Hardware-Pixel dick, damit sie auf Retina
        //    nicht doppelt so breit wirken wie die Cursor-Linie; Background-Separator doppellinig) ---
        const sepDpr = window.devicePixelRatio || 1
        const sepThickness = 1 / sepDpr
        ctx.save()
        ctx.globalAlpha = MINIMAP.SEPARATOR_ALPHA
        for (const sep of separators) {
            const midQ = sep.quarterY + sep.heightQ / 2
            const linePxY = Math.round(qToPixel(midQ) * sepDpr) / sepDpr
            ctx.fillStyle = sep.isBackground ? bgColor : fgColor
            if (sep.isBackground) {
                // Doppel-Linie: 1 HW-Pixel + Gap + 1 HW-Pixel
                const gap = MINIMAP.BG_SEPARATOR_GAP
                ctx.fillRect(mpx, linePxY - sepThickness - gap, mpw, sepThickness)
                ctx.fillRect(mpx, linePxY + sepThickness, mpw, sepThickness)
            } else {
                ctx.fillRect(mpx, linePxY, mpw, sepThickness)
            }
        }
        ctx.restore()

        // --- 6. Viewport-Rand-Streifen (Überstand) mit stärkerem Alpha nachzeichnen,
        // damit der Viewport auch ohne Section-Overlay deutlich sichtbar ist.
        if (vpBounds && inset > 0) {
            ctx.save()
            ctx.globalAlpha = MINIMAP.VIEWPORT_EDGE_ALPHA
            ctx.fillStyle = fgColor
            ctx.fillRect(px, vpBounds.y, inset, vpBounds.h)
            ctx.fillRect(px + pw - inset, vpBounds.y, inset, vpBounds.h)
            ctx.restore()
        }

        // --- 7. Cursor-Position als feine Linie in max. Kontrast ---
        // Exakt 1 Hardware-Pixel dick, unabhängig von devicePixelRatio, damit
        // die Linie auf allen Displays scharf bleibt.
        const cursor = this.model.cursor
        if (cursor) {
            let cIdx = -1
            for (let i = 0; i < this.lines.length; i++) {
                const ln = this.lines[i]
                if (ln.paragraphId === cursor.paragraphId
                    && cursor.offset >= ln.startOffset
                    && cursor.offset <= ln.endOffset) {
                    cIdx = i
                    break
                }
            }
            if (cIdx >= 0) {
                const cQ = (this._lineQuarterY[cIdx] || 0) + (this.lines[cIdx].heightQ || 4) / 2
                const cPxY = qToPixel(cQ)

                // Hintergrund-Helligkeit für Kontrast-Farbe
                const bg = renderer.colors.background || '#000'
                const hex = bg.replace('#', '')
                const r = parseInt(hex.slice(0, 2), 16) || 0
                const g = parseInt(hex.slice(2, 4), 16) || 0
                const b = parseInt(hex.slice(4, 6), 16) || 0
                const cursorColor = (r + g + b) / 3 > 127 ? '#000' : '#fff'

                // Device-Pixel-aware: exakt 1 Hardware-Pixel hoch, auf HW-Grid gesnapped
                const dpr = window.devicePixelRatio || 1
                const snappedY = Math.round(cPxY * dpr) / dpr
                const thickness = 1 / dpr

                ctx.save()
                ctx.globalAlpha = 1
                ctx.fillStyle = cursorColor
                ctx.fillRect(px, snappedY, pw, thickness)
                ctx.restore()
            }
        }
    }

    /**
     * Baut den Selection-Cache einmalig pro Render-Zyklus auf.
     * Speichert normalisierte Selection-Indizes für O(1)-Zugriff pro Zeichen.
     * @returns {Object|null}
     */
    _buildSelectionCache() {
        // Outline-Selection hat Vorrang (gegenseitiger Ausschluss mit Text-Selection)
        if (this.model._outlineSelection) {
            return {
                isOutlineSelection: true,
                startIndex: this.model._outlineSelection.startIndex,
                endIndex: this.model._outlineSelection.endIndex
            }
        }

        if (!this.model.hasSelection()) return null

        const { start, end } = this.model.selection.getNormalized(this.model)
        return {
            start,
            end,
            field: start.field || 'text',
            startIndex: this.model.getParagraphIndex(start.paragraphId),
            endIndex: this.model.getParagraphIndex(end.paragraphId)
        }
    }

    /**
     * Prüft ob eine Position selektiert ist (cached Version, O(1) pro Zeichen).
     * @param {number} paragraphId
     * @param {number} offset
     * @param {string} field - 'text' oder 'note'
     * @returns {boolean}
     */
    _isPositionSelectedCached(paragraphId, offset, field = 'text', posIndex = -1) {
        const sel = this._selectionCache
        if (!sel) return false

        // Outline-Selection: alle Felder aller Absätze im Range selektiert
        if (sel.isOutlineSelection) {
            const idx = posIndex >= 0 ? posIndex : this.model.getParagraphIndex(paragraphId)
            return idx >= sel.startIndex && idx <= sel.endIndex
        }

        // Selection gilt nur für das Feld in dem sie stattfindet
        if (sel.field !== field) return false

        const idx = posIndex >= 0 ? posIndex : this.model.getParagraphIndex(paragraphId)
        if (idx < sel.startIndex || idx > sel.endIndex) return false

        if (idx === sel.startIndex && idx === sel.endIndex) {
            return offset >= sel.start.offset && offset < sel.end.offset
        } else if (idx === sel.startIndex) {
            return offset >= sel.start.offset
        } else if (idx === sel.endIndex) {
            return offset < sel.end.offset
        }
        return true
    }

    /**
     * Liefert den passenden Marker-Style fuer Main-Pane:
     * EDITOR_MARKER fuer den aktiven Absatz im text-Feld, sonst EDITOR_MARKER_DIM.
     * @param {*} paragraphId
     * @returns {string}
     */
    _markerStyle(paragraphId) {
        return pickMarkerStyle(MarkerPane.MAIN, paragraphId, this.model.cursor)
    }

    /**
     * Prüft ob der Start-Marker (">") selektiert angezeigt werden soll.
     * Das ist der Fall wenn der gesamte Absatz selektiert ist.
     * @param {number} paragraphId
     * @returns {boolean}
     */
    _isStartMarkerSelected(paragraphId) {
        const sel = this._selectionCache
        if (!sel) return false

        // Outline-Selection: alle Absätze im Range sind komplett selektiert
        if (sel.isOutlineSelection) {
            const idx = this.model.getParagraphIndex(paragraphId)
            return idx >= sel.startIndex && idx <= sel.endIndex
        }

        // Nur für Text-Feld-Selections (nicht Note)
        if (sel.field !== 'text') return false

        // Anfangsmarke nur selektiert wenn Endmarke auch selektiert ist
        // (Selection muss mindestens bis Pos0 des Folgeabsatzes / Abschnittsende reichen)
        if (!this._isEndMarkerSelected(paragraphId)) return false

        const para = this.model.getParagraph(paragraphId)
        if (!para) return false

        const posIndex = this.model.getParagraphIndex(paragraphId)

        // Gesamter Text muss ab Anfang selektiert sein
        let startsAtVisualBeginning = sel.start.offset === 0
        if (!startsAtVisualBeginning && posIndex === sel.startIndex) {
            const mapping = this.getDisplayMapping(paragraphId, 'text')
            if (mapping) {
                startsAtVisualBeginning = true
                for (let i = 0; i < sel.start.offset; i++) {
                    if (mapping.dataToDisplay[i] !== -1) {
                        startsAtVisualBeginning = false
                        break
                    }
                }
            }
        }
        return posIndex > sel.startIndex ||
            (posIndex === sel.startIndex && startsAtVisualBeginning)
    }

    /**
     * Prüft ob ein Absatz Teil der Outline-Selection ist.
     * Wird von OutlineView genutzt für crossPaneSelected.
     * @param {number} paragraphId
     * @returns {boolean}
     */
    _isOutlineSelected(paragraphId) {
        if (!this.model._outlineSelection) return false
        const idx = this.model.getParagraphIndex(paragraphId)
        return idx >= this.model._outlineSelection.startIndex
            && idx <= this.model._outlineSelection.endIndex
    }

    /**
     * Prüft ob ein geparstes Zeichen Teil eines selektierten Links ist.
     * Verwendet _selectedLink aus dem Input-Layer (via model._selectedLink).
     * @param {number} paragraphId
     * @param {string} field - 'text', 'note' oder 'info'
     * @param {object} parsedChar - Geparstes Zeichen mit isLink, dataOffset
     * @returns {boolean}
     */
    _isLinkCharSelected(paragraphId, field, parsedChar) {
        const sl = this.model._selectedLink
        if (!sl) return false
        return sl.paragraphId === paragraphId &&
            sl.field === field &&
            parsedChar.isLink &&
            parsedChar.dataOffset === sl.dataStart
    }

    /**
     * Prüft ob der End-Marker ("<") selektiert angezeigt werden soll.
     * Das ist der Fall wenn die Selektion über die Absatzgrenze hinausgeht.
     * @param {number} paragraphId
     * @returns {boolean}
     */
    _isEndMarkerSelected(paragraphId) {
        const sel = this._selectionCache
        if (!sel) return false

        // Outline-Selection: alle Absätze im Range sind komplett selektiert
        if (sel.isOutlineSelection) {
            const idx = this.model.getParagraphIndex(paragraphId)
            return idx >= sel.startIndex && idx <= sel.endIndex
        }

        const posIndex = this.model.getParagraphIndex(paragraphId)
        if (posIndex < sel.startIndex || posIndex > sel.endIndex) return false

        // Absatz vor dem letzten selektierten → Zeilenumbruch ist in der Selection
        if (posIndex < sel.endIndex) return true

        // Letzter selektierter Absatz: End-Marker nur wenn an Abschnittsgrenze
        // (der Umbruch geht konzeptionell zum Separator/Dokumentende)
        const para = this.model.getParagraph(paragraphId)
        if (!para) return false
        if (sel.end.offset < para.text.length) return false

        // Prüfe ob Folgeabsatz ein Separator oder Dokumentende ist
        const nextPara = this.model.paragraphs[posIndex + 1]
        return !nextPara || nextPara.type === 'separator'
    }

}
